<template>
  <div id="app" class="min-h-screen flex flex-col md:flex-row antialiased selection:bg-sky-100 selection:text-sky-900 w-full overflow-x-hidden">
    <aside id="sidebarNav" class="w-full md:w-72 bg-white text-slate-700 flex-shrink-0 flex flex-col border-r border-slate-200 sticky top-0 z-40 md:h-screen shadow-xs">
        
        <!-- Sidebar Header (Profile Settings Trigger) -->
        <div class="p-4 border-b border-slate-100 flex items-center justify-between bg-sky-50/40">
            <div @click="openModalGuarded('siteSettingsModal')" class="flex items-center space-x-3 cursor-pointer group hover:opacity-90 transition" title="点击修改个人与网站设置">
                <div id="sidebarAvatarContainer" class="w-10 h-10 rounded-2xl bg-sky-500 flex items-center justify-center text-white shadow-md shadow-sky-500/20 overflow-hidden relative group-hover:scale-105 transition">
                    <i id="sidebarAvatarIcon" ref="sidebarAvatarIcon" class="fa-solid fa-baby text-xl"></i>
                    <img id="sidebarAvatarImg" ref="sidebarAvatarImg" src="" class="w-full h-full object-cover hidden" alt="Avatar">
                </div>
                <div class="min-w-0">
                    <div class="flex items-center space-x-1">
                        <h1 id="sidebarSiteTitle" ref="sidebarSiteTitle" class="text-sm font-extrabold text-slate-800 tracking-wide truncate">王秉宣的成长纪事</h1>
                        <i class="fa-solid fa-gear text-[10px] text-slate-400 group-hover:text-sky-600 transition"></i>
                    </div>
                    <span id="sidebarDadStatus" ref="sidebarDadStatus" class="inline-block bg-sky-100 text-sky-800 text-[10px] px-2 py-0.5 rounded-full border border-sky-200 font-semibold truncate bkleft">奶爸备养中</span>
                </div>
            </div>
            <button @click="toggleMobileNav()" class="md:hidden text-slate-500 hover:text-slate-800 p-2 focus:outline-none">
                <i class="fa-solid fa-bars text-lg"></i>
            </button>
        </div>

        <div class="p-3.5 border-b border-slate-200 bg-gradient-to-br from-amber-50/90 via-orange-50/60 to-rose-50/80">
            <div class="bg-white/80 backdrop-blur-xs border border-amber-200/80 rounded-2xl p-3.5 space-y-2.5 shadow-2xs">
                <div class="flex items-center justify-between">
                    <span class="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                        <i class="fa-solid fa-hourglass-half text-amber-500"></i>
                        <span>{{this.userInfo.chlidName}}诞生倒计时</span>
                    </span>
                    <span id="dueDateTag" ref="dueDateTag" class="bg-rose-100 text-rose-700 text-[10px] px-2 py-0.5 rounded-full font-extrabold border border-rose-200">2027-06-15</span>
                </div>

                <div class="flex items-baseline justify-between pt-1">
                    <div class="text-slate-600 text-xs">
                        剩余 <span id="countdownDaysLarge" ref="countdownDaysLarge" class="text-2xl font-black text-rose-600 font-mono tracking-tight">0</span> 天
                    </div>
                    <div class="text-right">
                        <span id="gestationalWeek" ref="gestationalWeek" class="text-xs font-bold text-slate-700">孕中期 · 18周+4天</span>
                    </div>
                </div>

                <!-- Gestational Progress Bar -->
                <div class="space-y-1">
                    <div class="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                        <div id="gestationalProgressBar" ref="gestationalProgressBar" class="bg-gradient-to-r from-amber-400 to-rose-500 h-2 rounded-full transition-all duration-500" style="width: 45%;"></div>
                    </div>
                    <div class="flex justify-between text-[10px] text-slate-400 font-mono">
                        <span>孕早期</span>
                        <span>孕中期</span>
                        <span>孕晚期</span>
                    </div>
                </div>

                <!-- EMBRYO TRANSFER & DUE DATE CALCULATOR LAUNCHER -->
                <button @click="toggleTransferCalc()" class="w-full mt-1 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-300 text-amber-900 rounded-lg text-[11px] font-bold transition flex items-center justify-center space-x-1">
                    <i class="fa-solid fa-calculator text-amber-600"></i>
                    <span>⚙️ 计算/校准预产期 (胚胎移植/末次月经)</span>
                </button>

                <!-- Embedded Transfer Calculator Drawer -->
                <div id="transferCalcDrawer" ref="transferCalcDrawer" class="hidden bg-white p-3 rounded-xl border border-amber-200 space-y-2 text-xs text-slate-700 mt-2">
                    <div class="font-bold text-amber-900 flex justify-between items-center text-[11px]">
                        <span>🧪 预产期推算算法</span>
                        <button @click="toggleTransferCalc()" class="text-slate-400 hover:text-slate-600"><i class="fa-solid fa-xmark"></i></button>
                    </div>
                    <div>
                        <label class="block text-[10px] text-slate-500 mb-0.5 txtleft">选择推算依据</label>
                        <select id="calcTypeSelect" ref="calcTypeSelect" class="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-slate-50">
                            <option v-for="item in pregnancyList" :key="item.id" :value="item.id">{{ item.dictName }}</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-[10px] text-slate-500 mb-0.5 txtleft">选择移植 / 月经日期</label>
                        <input type="date" id="calcDateInput" ref="calcDateInput" @click="onDatePickerClick($event)" class="w-full px-2 py-1 border border-slate-300 rounded text-xs bg-slate-50 cursor-pointer">
                    </div>
                    <button @click="calculateAndSaveDueDate()" class="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-1.5 rounded-lg transition text-xs">
                        更新并保存最新预产期
                    </button>
                </div>
            </div>
        </div>

        <nav id="navMenuItems" ref="navMenuItems" class="hidden md:flex flex-1 flex-col p-3 space-y-1 overflow-y-auto custom-scrollbar text-xs font-medium">

            <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-2 pb-1 txtleft">日常护理与数据记录</div>

            <button @click="switchTab('tracker')" id="nav-btn-tracker" ref="nav-btn-tracker" class="nav-item active w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-bolt text-amber-500 w-5 text-center"></i>
                <span>日常护理与生长速记</span>
            </button>

            <button @click="switchTab('fetal')" id="nav-btn-fetal" ref="nav-btn-fetal" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-person-pregnant text-rose-500 w-5 text-center"></i>
                <span>孕期胎儿数据与产检</span>
            </button>

            <button @click="switchTab('charts')" id="nav-btn-charts" ref="nav-btn-charts" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-chart-line text-purple-500 w-5 text-center"></i>
                <span>WHO生长曲线与数据表</span>
            </button>

            <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-3 pb-1 txtleft">育儿知识与深度科学</div>

            <button @click="switchTab('diary')" id="nav-btn-diary" ref="nav-btn-diary" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-book-bookmark text-indigo-500 w-5 text-center"></i>
                <span>时光成长日记 (年月归档)</span>
            </button>

            <button v-if="isLoggedIn" @click="switchTab('album')" id="nav-btn-album" ref="nav-btn-album" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-images text-teal-500 w-5 text-center"></i>
                <span>{{this.userInfo.chlidName}}时光成长相册</span>
            </button>

            <button @click="switchTab('guide')" id="nav-btn-guide" ref="nav-btn-guide" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-utensils text-emerald-500 w-5 text-center"></i>
                <span>0-3岁科学喂养&4个月辅食计划</span>
            </button>

            <button @click="switchTab('attachment')" id="nav-btn-attachment" ref="nav-btn-attachment" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-heart text-rose-500 w-5 text-center"></i>
                <span>回避型奶爸的安全型依恋重塑与打卡</span>
            </button>

            <button @click="switchTab('motor')" id="nav-btn-motor" ref="nav-btn-motor" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-person-running text-cyan-600 w-5 text-center"></i>
                <span>0-3岁运动与神经肌肉发育</span>
            </button>

            <button @click="switchTab('brain')" id="nav-btn-brain" ref="nav-btn-brain" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-brain text-pink-500 w-5 text-center"></i>
                <span>0-3岁大脑脑科学 (海马体/前额叶)</span>
            </button>

            <button @click="switchTab('emotion')" id="nav-btn-emotion" ref="nav-btn-emotion" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-heart-pulse text-rose-500 w-5 text-center"></i>
                <span>0-10岁儿童情绪理解深度演进</span>
            </button>

            <div class="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 pt-3 pb-1 txtleft">随感</div>

            <button @click="switchTab('mood')" id="nav-btn-mood" ref="nav-btn-mood" class="nav-item w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition text-slate-600 hover:bg-sky-50 hover:text-sky-700">
                <i class="fa-solid fa-comment-dots text-amber-500 w-5 text-center"></i>
                <span>奶爸心情微语</span>
            </button>
        </nav>

        <!-- Sidebar Footer -->
        <div class="p-3 border-t border-slate-100 bg-slate-50 space-y-2">
            <button @click="openModalGuarded('quickAddModal')" class="w-full bg-sky-600 hover:bg-sky-700 text-white text-xs py-2.5 rounded-xl font-semibold shadow-md transition flex items-center justify-center space-x-2">
                <i class="fa-solid fa-plus"></i>
                <span>写新日记 / 上传多媒体</span>
            </button>
        </div>
    </aside>

    <main class="flex-1 flex flex-col min-w-0 h-screen overflow-y-auto custom-scrollbar bg-slate-50 w-full max-w-full">

        <!-- Top Sticky Header -->
        <header class="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 px-3 sm:px-6 py-3 flex justify-between items-center shadow-2xs w-full">
            <div class="min-w-0 flex-1 pr-2">
                <h2 id="currentTabTitle" ref="currentTabTitle" class="text-sm sm:text-base md:text-lg font-extrabold text-slate-800 flex items-center gap-2 truncate">
                    <span>⚡ 日常护理与生长速记中心</span>
                </h2>
                <p id="currentTabSubtitle" ref="currentTabSubtitle" class="text-xs text-slate-500 hidden md:block truncate bkleft">一键快速登记宝宝喂养、睡眠、换尿布、生长指标、皮肤护理、用药执行、疫苗与萌牙情况。</p>
            </div>
            <button @click="openModalGuarded('quickAddModal')" class="bg-sky-600 hover:bg-sky-700 text-white text-xs px-3 py-1.5 rounded-lg font-semibold shadow-xs transition flex items-center space-x-1 shrink-0">
                <i class="fa-solid fa-plus"></i>
                <span class="hidden sm:inline">写日记</span>
            </button>
        </header>

        <!-- Dynamic Content Body -->
        <div class="p-3 sm:p-6 space-y-6 flex-1 w-full max-w-7xl mx-auto">

            <!-- ================= TAB 1: 日常护理与生长速记 ================= -->
            <section id="tab-tracker" ref="tab-tracker" class="tab-content space-y-6">
                <!-- Quick Tips Card -->
                <div class="bg-gradient-to-r from-sky-500/10 via-sky-400/5 to-transparent border border-sky-200 rounded-2xl p-4 flex items-center space-x-3">
                    <div class="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center text-lg flex-shrink-0">
                        <i class="fa-solid fa-lightbulb"></i>
                    </div>
                    <div class="text-xs text-slate-600">
                        <strong class="text-sky-900 font-bold block txtleft">奶爸一键打卡秘诀</strong>
                        点击下方对应功能板块即可秒级记录。所有历史数据会自动聚合至下方的时间轴数据库，方便就医咨询或日常复盘。
                    </div>
                </div>

                <!-- Grid of Quick Action Cards (All buttons aligned at bottom row) -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                    <!-- Card 1: 喂奶速记 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">🍼 喂奶速记</span>
                                <span class="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded font-medium">喂养</span>
                            </div>
                            <div class="space-y-2">
                                <div class="grid grid-cols-3 gap-1.5 text-xs">
                                    <button type="button" @click="$refs.customFeedMl.value = 60" class="py-1.5 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg font-medium transition">60 ml</button>
                                    <button type="button" @click="$refs.customFeedMl.value = 90" class="py-1.5 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg font-medium transition">90 ml</button>
                                    <button type="button" @click="$refs.customFeedMl.value = 120" class="py-1.5 bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-300 rounded-lg font-medium transition">120 ml</button>
                                </div>
                                <input type="number" id="customFeedMl" ref="customFeedMl" placeholder="奶量 (ml)，如 90" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogFeed()" class="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存喂奶记录</button>
                    </div>

                    <!-- Card 2: 睡眠记录 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">😴 睡眠记录</span>
                                <span class="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-medium">作息</span>
                            </div>
                            <div class="space-y-2">
                                <select id="sleepTypeSelect" ref="sleepTypeSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500">
                                    <template v-if="dailyList.length > 1">
                                        <option v-for="item in dailyList[1].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="🌙 开始入睡">🌙 开始入睡</option>
                                        <option value="☀️ 小睡醒来">☀️ 小睡醒来</option>
                                        <option value="🌌 夜间沉睡">🌌 夜间沉睡</option>
                                        <option value="🥺 夜间醒啼哭">🥺 夜间醒啼哭</option>
                                    </template>
                                </select>
                                <input type="text" id="sleepNoteInput" ref="sleepNoteInput" placeholder="睡眠备注 (如：睡得安稳/易惊醒)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogSleep()" class="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存睡眠记录</button>
                    </div>

                    <!-- Card 3: 换尿布 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">💩 换尿布</span>
                                <span class="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">排泄</span>
                            </div>
                            <div class="space-y-2">
                                <select id="diaperTypeSelect" ref="diaperTypeSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-amber-500">
                                    <template v-if="dailyList.length > 2">
                                        <option v-for="item in dailyList[2].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="💧 仅尿湿">💧 仅尿湿</option>
                                        <option value="💩 有便便">💩 有便便</option>
                                        <option value="🔗 尿便混合">🔗 尿便混合</option>
                                        <option value="✨ 检查干爽">✨ 检查干爽</option>
                                        <option value="💩 有便便">💩 有便便</option>
                                        <option value="🔗 尿便混合">🔗 尿便混合</option>
                                        <option value="✨ 干爽无换">✨ 检查干爽</option>
                                    </template>
                                </select>
                                <input type="text" id="diaperNoteInput" ref="diaperNoteInput" placeholder="便便性状 (如：黄金糊状/略稀)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-amber-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogDiaper()" class="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存排泄记录</button>
                    </div>

                    <!-- Card 4: 生长数据测定 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">📏 生长数据测定</span>
                                <span class="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">指标</span>
                            </div>
                            <div class="grid grid-cols-3 gap-2">
                                <input type="number" id="weightInput" ref="weightInput" placeholder="体重kg" class="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                                <input type="number" id="heightInput" ref="heightInput" placeholder="身高cm" class="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                                <input type="number" id="headInput" ref="headInput" placeholder="头围cm" class="px-2 py-1.5 border border-slate-300 rounded-lg text-xs">
                            </div>
                        </div>
                        <button @click="logGrowth()" class="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存测量数值</button>
                    </div>

                    <!-- Card 5: 皮肤护理与洗澡 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">🧴 皮肤护理与洗澡</span>
                                <span class="text-[10px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded font-medium">护肤</span>
                            </div>
                            <div class="space-y-2">
                                <select id="skinTypeSelect" ref="skinTypeSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-rose-500">
                                    <template v-if="dailyList.length > 4">
                                        <option v-for="item in dailyList[4].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="🛋️ 洗澡与抚触">🛋️ 洗澡与抚触</option>
                                        <option value="🧴 涂面霜/护臀膏">rops 涂面霜/护臀膏</option>
                                        <option value="🩺 湿疹敷药护理">🩺 湿疹敷药护理</option>
                                        <option value=" 💆 婴儿抚触按摩">💆 婴儿抚触按摩</option>
                                    </template>
                                </select>
                                <input type="text" id="skinNoteInput" ref="skinNoteInput" placeholder="皮肤状况 (如：脸颊微红/湿疹护理)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-rose-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogSkin()" class="w-full bg-rose-600 hover:bg-rose-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">保存护肤记录</button>
                    </div>

                    <!-- Card 6: 用药打卡 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">💊 用药打卡</span>
                                <span class="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">健康</span>
                            </div>
                            <div class="space-y-2">
                                <select id="medSelect" ref="medSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-purple-500">
                                    <template v-if="dailyList.length > 5">
                                        <option v-for="item in dailyList[5].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="☀️维D3 (400IU)">☀️ 维D3 (400IU)</option>
                                        <option value="🦠 益生菌">🦠 益生菌</option>
                                        <option value="💊 维AD胶囊">💊 维AD胶囊</option>
                                        <option value="✏️ 其他药品/补充剂">✏️ 其他药品/补充剂</option>
                                    </template>
                                </select>
                                <input type="text" id="customMedInput" ref="customMedInput" placeholder="其他药品/剂量 (如美林 2.5ml)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-purple-500 focus:outline-none">
                            </div>
                        </div>
                        <button @click="quickLogMed()" class="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">完成用药打卡</button>
                    </div>

                    <!-- Card 7: 💉 深圳市卫健委指定疫苗接种计划记录 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">💉 疫苗接种记录</span>
                                <span class="text-[10px] bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-medium">深圳卫健委标准</span>
                            </div>
                            <div>
                                <select id="vaccineSelect" ref="vaccineSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500">
                                    <template v-if="dailyList.length > 6">
                                        <option v-for="item in dailyList[6].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="注射：乙肝疫苗第一针 (出生时刻)">注射：乙肝疫苗第一针 (出生时刻)</option>
                                        <option value="接种：卡介苗 (出生24小时内)">接种：卡介苗 (出生24小时内)</option>
                                        <option value="注射：乙肝疫苗第二针 (1月龄)">注射：乙肝疫苗第二针 (1月龄)</option>
                                        <option value="注射：脊灰灭活疫苗第一针 IPV (2月龄)">注射：脊灰灭活疫苗第一针 IPV (2月龄)</option>
                                        <option value="注射：脊灰灭活疫苗第二针 IPV (3月龄)">注射：脊灰灭活疫苗第二针 IPV (3月龄)</option>
                                        <option value="注射：百白破疫苗第一针 (3月龄)">注射：百白破疫苗第一针 (3月龄)</option>
                                        <option value="注射：百白破疫苗第二针 (4月龄)">注射：百白破疫苗第二针 (4月龄)</option>
                                        <option value="注射：13价肺炎球菌疫苗 (自费推荐)">注射：13价肺炎球菌疫苗 (自费推荐)</option>
                                        <option value="注射：五联疫苗 (自费替代)">注射：五联疫苗 (自费替代)</option>
                                        <option value="注射：乙肝疫苗第三针 (6月龄)">注射：乙肝疫苗第三针 (6月龄)</option>
                                        <option value="注射：流脑A群疫苗第一针 (6月龄)">注射：流脑A群疫苗第一针 (6月龄)</option>
                                        <option value="注射：麻腮风疫苗第一针 MMR (8月龄)">注射：麻腮风疫苗第一针 MMR (8月龄)</option>
                                        <option value="注射：乙脑减毒活疫苗第一针 (8月龄)">注射：乙脑减毒活疫苗第一针 (8月龄)</option>
                                    </template>
                                </select>
                            </div>
                        </div>
                        <button @click="quickLogVaccine()" class="w-full bg-sky-600 hover:bg-sky-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">完成疫苗登记</button>
                    </div>

                    <!-- Card 8: 🦷 出牙记录 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">🦷 出牙记录</span>
                                <span class="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-medium">萌牙</span>
                            </div>
                            <div>
                                <select id="teethPositionSelect" ref="teethPositionSelect" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs">
                                    <template v-if="dailyList.length > 7">
                                        <option v-for="item in dailyList[7].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                    </template>
                                    <template v-else>
                                        <option value="下门牙左/右 (通常最早6月萌出)">下门牙左/右 (通常最早6月萌出)</option>
                                        <option value="上门牙左/右 (通常8-10月萌出)">上门牙左/右 (通常8-10月萌出)</option>
                                        <option value="侧门牙 (通常9-13月萌出)">侧门牙 (通常9-13月萌出)</option>
                                        <option value="第一磨牙 (通常13-19月萌出)">第一磨牙 (通常13-19月萌出)</option>
                                        <option value="犬齿/尖牙 (通常16-23月萌出)">犬齿/尖牙 (通常16-23月萌出)</option>
                                    </template>
                                </select>
                            </div>
                        </div>
                        <button @click="quickLogTeeth()" class="w-full bg-amber-600 hover:bg-amber-700 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">登记萌牙时刻</button>
                    </div>

                    <!-- Card 9: 📝 自定义速记事项 -->
                    <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-1.5">📝 自定义速记事项</span>
                                <span class="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">其他</span>
                            </div>
                            <div class="space-y-2">
                                <input type="text" id="customEventName" ref="customEventName" placeholder="事项名称 (如: 趴卧抬头练习/听早教音乐)" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500">
                                <input type="text" id="customEventNote" ref="customEventNote" placeholder="详细说明或感受" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-sky-500">
                            </div>
                        </div>
                        <button @click="quickLogCustomEvent()" class="w-full bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-lg text-xs font-semibold transition mt-2">添加自定义记录</button>
                    </div>

                    <!-- Card 10: ❤️ 安全依恋亲子互动打卡 -->
                    <div class="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-4 shadow-2xs space-y-3 flex flex-col justify-between">
                        <div class="space-y-3">
                            <div class="flex justify-between items-center">
                                <span class="font-bold text-xs sm:text-sm text-rose-900 flex items-center gap-1.5">❤️ 安全依恋亲子互动打卡</span>
                                <span class="text-[10px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded font-bold">依恋重塑</span>
                            </div>
                            <select id="attachmentInteractionSelect" ref="attachmentInteractionSelect" class="w-full px-2.5 py-1.5 border border-rose-200 rounded-lg text-xs bg-white text-slate-800">
                                <template v-if="dailyList.length > 9">
                                    <option v-for="item in dailyList[9].children" :key="item.dictCode || item.dictName" :value="item.dictName">{{ item.dictName }}</option>
                                </template>
                                <template v-else>
                                    <option value="完成15分钟专注皮肤接触/婴儿抚触">完成15分钟专注皮肤接触/婴儿抚触</option>
                                    <option value="哭闹时3秒内温和回应与拥抱">哭闹时3秒内温和回应与拥抱</option>
                                    <option value="完成离别告别仪式 (眼神凝视+承诺归来)">完成离别告别仪式 (眼神凝视+承诺归来)</option>
                                    <option value="进行情绪接纳与命名 (帮孩子说出心理感受)">进行情绪接纳与命名 (帮孩子说出心理感受)</option>
                                    <option value="睡前10分钟无手机专注共读与互动">睡前10分钟无手机专注共读与互动</option>
                                    <option value="口头表达爱意 (告诉秉宣: 爸爸很爱你/为你骄傲)">口头表达爱意 (告诉秉宣: 爸爸很爱你/为你骄傲)</option>
                                </template>
                            </select>
                        </div>
                        <button @click="quickLogAttachment()" class="w-full bg-rose-600 hover:bg-rose-700 text-white py-2 rounded-lg text-xs font-semibold transition shadow-xs mt-2">完成依恋互动打卡</button>
                    </div>

                </div>

                <!-- Tracker History Log Database Box -->
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-4">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <h3 class="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-2">
                            <span>📜 {{this.userInfo.chlidName}}的日常护理与成长打卡数据库</span>
                        </h3>
                        <div class="flex space-x-2 text-xs">
                            <button @click="filterTrackerLogs('ALL')" class="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md font-medium">全部显示</button>
                            <!-- <button @click="clearTrackerLogs()" class="px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-md font-medium">清空当前演示</button> -->
                        </div>
                    </div>

                    <div id="trackerHistoryList" ref="trackerHistoryList" @click="delegateClick" class="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
                        <!-- Dynamic rendered tracker logs -->
                    </div>
                </div>

            </section>

            <!-- ================= TAB 2: 孕期胎儿数据与产检记录 ================= -->
            <section id="tab-fetal" ref="tab-fetal" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-5">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-person-pregnant text-rose-500"></i>
                                <span>孕期胎儿数据与产检档案</span>
                            </h3>
                            <p class="text-xs text-slate-500">详细记录{{this.userInfo.chlidName}}在妈妈肚子里每周的B超数据、胎心率、骨骼发育与医生指导医嘱。</p>
                        </div>
                        <span class="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-semibold">产检档案</span>
                    </div>

                    <!-- Form to add fetal data -->
                    <form id="fetalDataForm" ref="fetalDataForm" @submit.prevent="saveFetalCheckup($event)" class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3 text-xs">
                        <div class="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                            <i class="fa-solid fa-plus-circle text-rose-500"></i>
                            <span>新增一次产检与B超记录：</span>
                        </div>
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">检查日期 *</label>
                                <input type="date" id="fetalDate" ref="fetalDate" required @click="onDatePickerClick($event)" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg cursor-pointer">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">孕周 (如: 18周+4天) *</label>
                                <input type="text" id="fetalWeek" ref="fetalWeek" placeholder="如: 18周+4天" required class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">胎心率 (bpm)</label>
                                <input type="number" id="fetalHeartRate" ref="fetalHeartRate" placeholder="如: 148" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">胎儿估重 (g)</label>
                                <input type="number" id="fetalWeight" ref="fetalWeight" placeholder="如: 230" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                        </div>

                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">双顶径 BPD (mm)</label>
                                <input type="number" step="0.1" id="fetalBpd" ref="fetalBpd" placeholder="如: 42.5" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">股骨长 FL (mm)</label>
                                <input type="number" step="0.1" id="fetalFl" ref="fetalFl" placeholder="如: 27.2" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">腹围 AC (mm)</label>
                                <input type="number" step="0.1" id="fetalAc" ref="fetalAc" placeholder="如: 132.0" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 mb-1 font-medium txtleft">羊水最大深度 (mm)</label>
                                <input type="number" step="0.1" id="fetalAmniotic" ref="fetalAmniotic" placeholder="如: 45" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                        </div>

                        <div>
                            <label class="block text-slate-600 mb-1 font-medium txtleft">医生总结与护理提示 *</label>
                            <textarea id="fetalDoctorSummary" ref="fetalDoctorSummary" rows="2" placeholder="填写大排畸/NT筛查结论、医生嘱咐或奶爸感悟..." required class="w-full px-3 py-1.5 border border-slate-300 rounded-lg"></textarea>
                        </div>

                        <div class="flex justify-end">
                            <button type="submit" class="bg-rose-600 hover:bg-rose-700 text-white px-5 py-2 rounded-lg font-bold transition shadow-xs">保存本次产检记录</button>
                        </div>
                    </form>

                    <!-- History Fetal Records Container -->
                    <div class="space-y-3">
                        <h4 class="font-bold text-xs text-slate-800 txtleft">📋 历史产检与B超测量档案库：</h4>
                        <div id="fetalHistoryContainer" ref="fetalHistoryContainer" @click="delegateClick" class="space-y-3">
                            <!-- Dynamic rendered fetal checkup items -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 3: 时光成长日记 (年月归档) ================= -->
            <section id="tab-diary" ref="tab-diary" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-5">

                    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-3 gap-2">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-book-bookmark text-indigo-500"></i>
                                <span>时光成长日记 (按年月分组归档)</span>
                            </h3>
                            <p class="text-xs text-slate-500">自动按年月聚合的微型日记，一排展示 3 篇，卡片更大、摘要更完整。点击可弹窗查看完整图文、视频与语音。</p>
                        </div>

                        <button @click="openModalGuarded('quickAddModal')" class="bg-sky-600 hover:bg-sky-700 text-white text-xs px-3.5 py-2 rounded-lg font-semibold shadow-xs transition flex items-center space-x-1.5">
                            <i class="fa-solid fa-pen-to-square"></i>
                            <span>写新日记</span>
                        </button>
                    </div>

                    <!-- Category Filter Buttons -->
                    <div class="flex flex-wrap gap-2 text-xs">
                        <button @click="filterDiary('ALL')" :class="['diary-filter-btn px-3 py-1 rounded-lg font-medium transition', currentDiaryCategory === 'ALL' ? 'active bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-sky-50']">全部日记 (<span id="diaryCountAll" ref="diaryCountAll">0</span>)</button>
                        <button v-for="item in diaryList" :key="item.id || item.dictName" @click="filterDiary(item.id)" :class="['diary-filter-btn px-3 py-1 rounded-lg font-medium transition', currentDiaryCategory === item.id ? 'active bg-sky-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-sky-50']">{{ item.dictName }} ({{ diaryCountByType(item.id) }})</button>
                    </div>

                    <!-- Grouped Diaries Container (By Year-Month) -->
                    <div id="groupedDiariesContainer" ref="groupedDiariesContainer" @click="delegateClick" class="space-y-6">
                        <!-- Dynamic rendering for grouped compact card diaries -->
                    </div>
                </div>
            </section>

            <!-- ================= TAB 3.5: 秉宣时光成长相册 (多媒体素材库) ================= -->
            <section id="tab-album" ref="tab-album" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-5">

                    <!-- Header & Stats -->
                    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-100 pb-4 gap-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-images text-teal-600"></i>
                                <span>{{this.userInfo.chlidName}}时光成长相册影像馆</span>
                            </h3>
                            <p class="text-xs text-slate-500 mt-0.5">记录{{this.userInfo.chlidName}}每一个动人瞬间，支持照片与视频分类筛选、时间轴归档及全屏沉浸播放。</p>
                        </div>

                        <!-- Stats & Upload Launcher Button -->
                        <div class="flex items-center space-x-2">
                            <div class="bg-teal-50 border border-teal-200/80 px-3 py-1.5 rounded-lg text-xs font-mono text-teal-800 flex items-center space-x-3">
                                <span>🗂️ 素材: <strong id="albumTotalCount" ref="albumTotalCount">0</strong></span>
                                <span>📷 照片: <strong id="albumPhotoCount" ref="albumPhotoCount">0</strong></span>
                                <span>🎥 视频: <strong id="albumVideoCount" ref="albumVideoCount">0</strong></span>
                            </div>
                            <button @click="openModalGuarded('addAlbumMediaModal')" class="bg-teal-600 hover:bg-teal-700 text-white text-xs px-3.5 py-2 rounded-lg font-semibold shadow-xs transition flex items-center space-x-1.5">
                                <i class="fa-solid fa-cloud-arrow-up"></i>
                                <span>上传素材到相册</span>
                            </button>
                        </div>
                    </div>

                    <!-- Filter Controls (Tags & Types) -->
                    <div class="space-y-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200/80 text-xs">

                        <!-- Filter Row 1: Media Types -->
                        <div class="flex items-center space-x-2">
                            <span class="font-bold text-slate-700 flex items-center gap-1 min-w-[70px]">
                                <i class="fa-solid fa-filter text-teal-500"></i> 素材类型:
                            </span>
                            <div class="flex space-x-1.5">
                                <button @click="filterAlbumType('ALL')" id="album-type-ALL" :class="['album-type-btn px-3 py-1 rounded-lg font-medium transition', currentAlbumType === 'ALL' ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100']">全部素材</button>
                                <button @click="filterAlbumType('image')" id="album-type-image" :class="['album-type-btn px-3 py-1 rounded-lg font-medium transition', currentAlbumType === 'image' ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100']">📷 照片画廊</button>
                                <button @click="filterAlbumType('video')" id="album-type-video" :class="['album-type-btn px-3 py-1 rounded-lg font-medium transition', currentAlbumType === 'video' ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100']">🎥 视频回忆</button>
                            </div>
                        </div>

                        <!-- Filter Row 2: Theme Tags -->
                        <div class="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-200/60">
                            <span class="font-bold text-slate-700 flex items-center gap-1 min-w-[70px]">
                                <i class="fa-solid fa-tags text-teal-500"></i> 主题分类:
                            </span>
                            <button @click="filterAlbumTag('ALL')" :class="['album-tag-btn px-2.5 py-0.5 rounded-md text-[11px] font-medium transition', currentAlbumTag === 'ALL' ? 'active bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-teal-50']">全部标签</button>
                            <button v-for="item in albumList" :key="item.id" @click="filterAlbumTag(item.id)" :class="['album-tag-btn px-2.5 py-0.5 rounded-md text-[11px] font-medium transition', currentAlbumTag == item.id ? 'active bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-teal-50']">{{ item.dictName }}</button>
                        </div>
                    </div>

                    <!-- Album Gallery Grid (Grouped by Year-Month) -->
                    <div id="albumGalleryContainer" ref="albumGalleryContainer" @click="delegateClick" class="space-y-6">
                        <!-- Dynamic rendered media cards -->
                    </div>
                </div>
            </section>

            <!-- ================= TAB 4: 0-3岁科学喂养&4个月辅食计划 ================= -->
            <section id="tab-guide" ref="tab-guide" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800">0-3岁科学喂养&4个月辅食计划</h3>
                            <p class="text-xs text-slate-500">涵盖 0-1 岁液体喂养、6-10个月辅食菜谱、1-2 岁幼儿膳食过渡及 2-3 岁家庭共餐全景方案。</p>
                        </div>
                        <span class="bg-emerald-100 text-emerald-800 text-xs px-2.5 py-1 rounded-full font-semibold">权威护理与辅食</span>
                    </div>

                    <!-- Feeding Roadmap Table for 0-3 Years -->
                    <div class="space-y-3">
                        <h4 class="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                            <span>🍼</span>
                            <span>0-3 岁全阶段喂养结构、奶量与膳食安排全景表</span>
                        </h4>
                        <div class="overflow-x-auto border border-slate-200 rounded-lg">
                            <table class="w-full text-xs text-left">
                                <thead class="bg-slate-50 border-b border-slate-200 text-slate-700 font-semibold">
                                    <tr>
                                        <th class="p-2.5">年龄阶段</th>
                                        <th class="p-2.5">核心食物结构</th>
                                        <th class="p-2.5">每日奶量安排</th>
                                        <th class="p-2.5">餐次与食物形态</th>
                                        <th class="p-2.5">中美欧指南核心禁忌与注意</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-100 text-slate-600">
                                    <tr>
                                        <td class="p-2.5 font-bold text-slate-800">0 - 6 月</td>
                                        <td class="p-2.5">纯母乳 / 配方奶</td>
                                        <td class="p-2.5">600 - 1000 ml (按需)</td>
                                        <td class="p-2.5">每日 6-12 次纯液体喂养</td>
                                        <td class="p-2.5">出生补充维D3 (400IU/天)，严禁喂水、蜂蜜或果汁。</td>
                                    </tr>
                                    <tr>
                                        <td class="p-2.5 font-bold text-slate-800">6 - 12 月</td>
                                        <td class="p-2.5">母乳/配方奶 + 高铁辅食</td>
                                        <td class="p-2.5">600 - 800 ml</td>
                                        <td class="p-2.5">3-4次奶 + 2-3次泥糊至小颗粒辅食</td>
                                        <td class="p-2.5">辅食优先补铁，绝对无盐无糖，1岁内禁止鲜牛奶。</td>
                                    </tr>
                                    <tr class="bg-emerald-50/40">
                                        <td class="p-2.5 font-bold text-emerald-900">1 - 2 岁</td>
                                        <td class="p-2.5">家庭固体食物为主，奶为辅</td>
                                        <td class="p-2.5">400 - 500 ml (可引入全脂鲜牛奶)</td>
                                        <td class="p-2.5">一日三餐家庭共餐 + 2次健康点心</td>
                                        <td class="p-2.5">食物剪小块防止窒息，微盐（低盐），不喝含糖饮料。</td>
                                    </tr>
                                    <tr class="bg-emerald-50/40">
                                        <td class="p-2.5 font-bold text-emerald-900">2 - 3 岁</td>
                                        <td class="p-2.5">完全融入家庭多样化均衡膳食</td>
                                        <td class="p-2.5">300 - 400 ml</td>
                                        <td class="p-2.5">一日三餐 + 1-2次点心 (自主进食)</td>
                                        <td class="p-2.5">培养独立用匙用叉习惯，避免整颗花生、坚果等窒息风险。</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- 4 Months Comprehensive Solid Food Plan & Recipes -->
                    <div class="bg-amber-50/50 border border-amber-200 rounded-xl p-3.5 sm:p-5 space-y-4 w-full">
                        <div class="flex justify-between items-center">
                            <div>
                                <h4 class="text-xs sm:text-sm font-bold text-amber-900 flex items-center space-x-2">
                                    <i class="fa-solid fa-utensils text-amber-600"></i>
                                    <span>6个月+ 跨度 4 个月的科学辅食菜谱打卡表 (按每3天一餐计划进行过敏排除)</span>
                                </h4>
                                <p class="text-[11px] sm:text-xs text-amber-800 mt-1">原则：优先强化铁米粉，每3天为一个食谱观察期，连续观察3天无过敏（无皮疹、腹泻、呕吐）再解锁下一种新食材。</p>
                            </div>
                        </div>

                        <!-- 4 Month Interactive Tabs -->
                        <div class="flex flex-wrap gap-1.5 sm:gap-2 border-b border-amber-200 pb-2 text-xs w-full">
                            <button @click="switchFoodMonth(1)" id="food-tab-1" ref="food-tab-1" class="food-month-btn bg-amber-600 text-white px-2.5 sm:px-3 py-1.5 rounded-lg font-bold transition text-[11px] sm:text-xs">第1个月：单一泥糊 (1-30天)</button>
                            <button @click="switchFoodMonth(2)" id="food-tab-2" ref="food-tab-2" class="food-month-btn bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 px-2.5 sm:px-3 py-1.5 rounded-lg font-medium transition text-[11px] sm:text-xs">第2个月：混合肉菜泥 (31-60天)</button>
                            <button @click="switchFoodMonth(3)" id="food-tab-3" ref="food-tab-3" class="food-month-btn bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 px-2.5 sm:px-3 py-1.5 rounded-lg font-medium transition text-[11px] sm:text-xs">第3个月：颗粒与手指食物 (61-90天)</button>
                            <button @click="switchFoodMonth(4)" id="food-tab-4" ref="food-tab-4" class="food-month-btn bg-white text-amber-900 border border-amber-200 hover:bg-amber-100 px-2.5 sm:px-3 py-1.5 rounded-lg font-medium transition text-[11px] sm:text-xs">第4个月：多样碎小块食 (91-120天)</button>
                        </div>

                        <!-- Month Food Content Area -->
                        <div id="foodMonthContent" ref="foodMonthContent" @click="delegateClick" class="space-y-3 text-xs w-full">
                            <!-- Dynamic food recipes rendering per 3-day block -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 5: 回避型奶爸的安全型依恋重塑与打卡 ================= -->
            <section id="tab-attachment" ref="tab-attachment" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-heart text-rose-500"></i>
                                <span>回避型奶爸的“安全型依恋 (Secure Attachment)” 重塑指南与亲子互动</span>
                            </h3>
                            <p class="text-xs text-slate-500">帮奶爸突破回避型人格的理智防御，为{{this.userInfo.chlidName}}筑造一生受益的底层安全感基地。</p>
                        </div>
                        <span class="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-semibold">依恋心理学</span>
                    </div>

                    <!-- Father's Self Awareness Box -->
                    <div class="bg-gradient-to-r from-rose-50 via-pink-50 to-amber-50 border border-rose-200 p-6 rounded-xl space-y-4 text-xs">
                        <div class="font-bold text-rose-900 text-sm flex items-center gap-2 border-b border-rose-200/60 pt-1 pb-4 px-1">
                            <i class="fa-solid fa-user-shield text-rose-600"></i>
                            <span>💡 给回避型奶爸的心灵信笺：觉察即是破局的开始</span>
                        </div>
                        <div class="px-1.5">
                            <p class="text-slate-700 leading-relaxed">
                                回避型依恋（Avoidant Attachment）往往源于自身童年未被敏感回应的体验，容易习惯用“理智、独立、退缩、压抑情绪”作为心理防御。当面对婴儿剧烈的哭闹或高度情感需求时，回避型奶爸容易产生**“情绪淹没感”**，并下意识想要逃离或冷处理。
                            </p>
                            <p class="text-slate-700 leading-relaxed">
                                <strong class="text-rose-900">但请记住：</strong> 依恋类型并非基因决定，而是由后天**“可预测的敏感回应”**重塑的！只要奶爸在{{this.userInfo.chlidName}} 0-3 岁建立起 **“敏锐回应 + 安全基地 + 关系修补”** 机制，孩子就能成长为阳光、自信、勇敢爱人的**安全型依恋（Secure Attachment）**人格！
                            </p>
                        </div>
                    </div>

                    <!-- 4 Attachment Stages Guidance & Action Checklists -->
                    <div class="space-y-5">
                        <!-- Stage 1 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-rose-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center text-xs">1</span>
                                    <span>0 - 6 个月：身体触觉、声音温度与“3秒敏锐回应”</span>
                                </h4>
                                <span class="bg-rose-100 text-rose-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">生理安全感基石</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">对长时间亲密肢体接触或无原因哭闹感到焦虑，下意识想把哭闹归结为“孩子习惯不好”而推开孩子。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">建立“哭闹=信号，而非攻击”的认知。婴儿6个月前无法被“惯坏”，每一次及时抱抚都在给大脑注入安全神经递质（催产素与多巴胺）。</p>
                                </div>
                                <div class="bg-white border border-rose-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-rose-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-rose-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每日至少完成 15 分钟专注身体接触/抚触（袋鼠式抱抚）。</li>
                                        <li>哭闹发生时，承诺在 3-5 秒内给予眼神注视与温和声音回应。</li>
                                        <li>喂奶时保持眼睛对视（Eye Contact）并给予微笑镜像。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 2 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-amber-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs">2</span>
                                    <span>6 - 18 个月：安全基地（Safe Haven）与不溜走告别仪式</span>
                                </h4>
                                <span class="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">分离焦虑应对</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">宝宝出现分离焦虑粘人时，容易感到被捆绑，甚至想“趁宝宝不注意偷偷溜走”。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">绝对不要偷溜！偷偷溜走会让宝宝杏仁核高度恐慌并诱发焦虑型/回避型人格。做“安全基地”：支持他探索，在他回头看你时给予确定鼓励。</p>
                                </div>
                                <div class="bg-white border border-amber-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-amber-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-amber-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每次离开前进行“温暖固定的告别仪式”（抱一下、对视说明归来时间）。</li>
                                        <li>做“躲猫猫（Peek-a-boo）”游戏，强化物体恒存与“爸爸会回来”的信念。</li>
                                        <li>孩子向外探索受吓归来时，立刻敞开双臂拥抱充能。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 3 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-indigo-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs">3</span>
                                    <span>18 个月 - 3 岁：情绪共情、拒绝冷暴力与禁闭</span>
                                </h4>
                                <span class="bg-indigo-100 text-indigo-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">情绪镜像与修补</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">孩子发脾气撒泼时，回避型奶爸容易启动“冷漠隔离/讲冷冰冰的大道理”防御。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">严禁冷暴力与无回应隔离！先处理情绪，再讲道理。如果奶爸一时情绪失控吼了孩子，一定要在冷静后主动进行“关系破裂修补（Rupture & Repair）”。</p>
                                </div>
                                <div class="bg-white border border-indigo-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-indigo-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-indigo-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每天至少 1 次帮{{this.userInfo.chlidName}}命名情绪（“你现在很沮丧对不对，爸爸陪着你”）。</li>
                                        <li>若自己产生情绪退缩，先深呼吸5秒，告诉孩子：“爸爸需要冷静一下，但我依然爱你”。</li>
                                        <li>固定睡前 10-15 分钟“无手机高质量陪伴”。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 4 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-purple-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">4</span>
                                    <span>3 岁 - 6 岁：主动口头表达爱意与自信独立支持</span>
                                </h4>
                                <span class="bg-purple-100 text-purple-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">完全安全型形成</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 回避型奶爸面临的挑战：</strong>
                                    <p class="text-slate-600 leading-relaxed">说出“爸爸爱你/我想你”等感性话语时感到肉麻或别扭，习惯只讨论“学习与任务”。</p>
                                    <strong class="text-slate-800">🛡️ 依恋重塑核心策略：</strong>
                                    <p class="text-slate-600 leading-relaxed">勇敢打破言语羞耻感！安全型依恋的孩子需要明确听到爱与认可，明白“即使我做错了事，爸爸对我的爱依然不会减少”。</p>
                                </div>
                                <div class="bg-white border border-purple-200 p-3 rounded-lg space-y-2 text-slate-700">
                                    <strong class="text-purple-900 flex items-center gap-1">
                                        <i class="fa-solid fa-list-check text-purple-600"></i>
                                        <span>每日必做依恋打卡工作：</span>
                                    </strong>
                                    <ul class="list-disc list-inside space-y-1 text-slate-600 text-[11px]">
                                        <li>每日至少口头表达 1 次：“{{this.userInfo.chlidName}}，爸爸非常爱你/今天为你感到骄傲”。</li>
                                        <li>平视蹲下，认真听孩子分享的一件小事，不中途打断。</li>
                                        <li>鼓励自主探索并提供坚定后盾支撑。</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <!-- ================= TAB 6: 0-3岁大运动与神经肌肉发育 ================= -->
            <section id="tab-motor" ref="tab-motor" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-person-running text-cyan-600"></i>
                                <span>0-3岁大运动发育、肌肉骨骼与神经生理演进机制</span>
                            </h3>
                            <p class="text-xs text-slate-500">解密婴儿“头尾法则（Cephalocaudal）”与“神经髓鞘化”原理：为什么在这个阶段能做出这些动作？</p>
                        </div>
                        <span class="bg-cyan-100 text-cyan-800 text-xs px-2.5 py-1 rounded-full font-semibold">运动解剖学</span>
                    </div>

                    <!-- Core Scientific Concept Box -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                        <div class="bg-cyan-50/70 border border-cyan-200 p-3.5 rounded-xl space-y-1.5">
                            <h4 class="font-bold text-cyan-900 flex items-center gap-1">
                                <i class="fa-solid fa-arrows-up-down"></i>
                                <span>1. 头尾发展规律 (Cephalocaudal)</span>
                            </h4>
                            <p class="text-slate-600 leading-relaxed">神经与肌肉控制遵循由上至下（头部 ➜ 颈部 ➜ 躯干 ➜ 下肢）发展。宝宝先学会抬头控制眼睛与头部，再学会控制双臂坐立，最后控制双腿行走。</p>
                        </div>
                        <div class="bg-blue-50/70 border border-blue-200 p-3.5 rounded-xl space-y-1.5">
                            <h4 class="font-bold text-blue-900 flex items-center gap-1">
                                <i class="fa-solid fa-bolt"></i>
                                <span>2. 神经髓鞘化 (Myelination)</span>
                            </h4>
                            <p class="text-slate-600 leading-relaxed">神经轴突被脂肪性髓鞘包裹，使电信号传输速度提升数十倍。皮质脊髓束（Corticospinal tract）的髓鞘化进度直接决定了精细运动与协调性。</p>
                        </div>
                        <div class="bg-indigo-50/70 border border-indigo-200 p-3.5 rounded-xl space-y-1.5">
                            <h4 class="font-bold text-indigo-900 flex items-center gap-1">
                                <i class="fa-solid fa-shield-halved"></i>
                                <span>3. 原始反射消退 (Reflex Integration)</span>
                            </h4>
                            <p class="text-slate-600 leading-relaxed">摩罗反射（Moro）、非对称强直性颈反射（ATNR）等由脑干控制的原始反射必须逐步整合消退，大脑皮层才能取得对肌肉的主动自主控制。</p>
                        </div>
                    </div>

                    <!-- Timeline Stage Analysis -->
                    <div class="space-y-4 pt-2">
                        <!-- Stage 1 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-cyan-800">📌 0 - 3 个月：抬头与颈部稳定</span>
                                <span class="bg-cyan-200 text-cyan-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：趴卧抬头90度</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">胸锁乳突肌与头夹肌锻炼发力。颈椎第一弯曲（颈曲）在此阶段趴卧练习中开始建立，为支撑头部重量打下基础。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">前庭系统接收重力感受，前庭脊髓束开始建立连接。摩罗惊跳反射在3个月末逐渐减弱。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 2 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-blue-800">📌 4 - 7 个月：自主翻身与独坐</span>
                                <span class="bg-blue-200 text-blue-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：仰卧翻身、三点支撑坐</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">腹直肌与腹斜肌侧向扭转力增强。坐立时腰椎弯曲（腰曲）建立，竖脊肌与核心肌群支撑脊柱，髋关节外展肌锁定盘腿坐姿。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">ATNR（击剑反射）消退，双手越过身体中线的控制能力建立。小脑参与维持身体重心平衡与空间定向。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 3 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-indigo-800">📌 8 - 12 个月：四肢爬行与扶物站立</span>
                                <span class="bg-indigo-200 text-indigo-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：手膝爬行、扶家具拉站</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">股四头肌、臀大肌与小腿腓肠肌开始接受重力负荷。手掌骨与腕骨承受体重，为精细抓握提供本体感觉基石。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">大脑两侧半球通过胼胝体进行高频对侧对等信息交互（交叉手脚爬行），大大促进双侧大脑发育。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Stage 4 -->
                        <div class="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <span class="font-extrabold text-sm text-purple-800">📌 12 - 36 个月：独立行走、跑步与跳跃</span>
                                <span class="bg-purple-200 text-purple-900 text-[10px] px-2 py-0.5 rounded font-mono">关键动作：稳步行走、双脚离地跳</span>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                    <strong class="text-slate-800">💪 解剖与肌肉参与：</strong>
                                    <p class="text-slate-600 mt-1">足弓逐步发育形成缓冲震荡。下肢骨骼（股骨与胫骨）钙化坚硬，踝关节韧带稳定性成熟，支撑跑跳时的爆发性冲击。</p>
                                </div>
                                <div>
                                    <strong class="text-slate-800">🧠 神经系统突破：</strong>
                                    <p class="text-slate-600 mt-1">运动皮层完成完全意志性控制，步态动态调整自动化（不需要大脑全程集中注意力），感觉统合高度成熟。</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 7: 0-3岁大脑脑科学 ================= -->
            <section id="tab-brain" ref="tab-brain" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-brain text-pink-500"></i>
                                <span>0-3岁大脑脑科学：神经结构、压力与快乐激素调控图谱</span>
                            </h3>
                            <p class="text-xs text-slate-500">解密皮质醇（压力）、催产素（依恋）、内啡肽/多巴胺（快乐与探索）的分泌机制，指导奶爸科学下调压力激素，激发快乐与安全依恋。</p>
                        </div>
                        <span class="bg-pink-100 text-pink-800 text-xs px-2.5 py-1 rounded-full font-semibold">脑科学与神经递质</span>
                    </div>

                    <!-- 3 Major Brain Structures Interactive Cards -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
                        
                        <!-- Structure 1: 海马体 -->
                        <div class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                                    <span class="text-xl">🌊</span>
                                    <span>海马体 (Hippocampus)</span>
                                </h4>
                                <span class="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded font-bold">记忆转换中心</span>
                            </div>
                            <div class="text-xs text-slate-600 space-y-2 leading-relaxed">
                                <p><strong>核心功能：</strong> 负责将短时记忆转化为长时记忆，处理空间定向与事件情境。</p>
                                <p><strong>0-3岁发育机制：</strong> 0-3岁时海马体齿状回正经历爆发性的神经元新生。因为新神经元不断重构神经网络，旧的突触连接被替换，导致了心理学上的**“婴儿期失忆症（Infantile Amnesia）”**（人很难记住3岁以前的具体记忆）。</p>
                                <div class="bg-indigo-50 border border-indigo-100 p-2.5 rounded-lg text-indigo-900 font-medium">
                                    💡 <strong>奶爸指导：</strong> 虽然孩子长大后记不起3岁前的具体事件，但海马体与边缘系统形成的**“内隐情绪记忆（Implicit Memory）”**会陪伴终生！爸爸给的安全感与温暖拥抱，会永久写入底层的安全感基因。
                                </div>
                            </div>
                        </div>

                        <!-- Structure 2: 杏仁核 -->
                        <div class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                                    <span class="text-xl">🚨</span>
                                    <span>杏仁核 (Amygdala)</span>
                                </h4>
                                <span class="bg-rose-100 text-rose-700 text-[10px] px-2 py-0.5 rounded font-bold">情绪与威胁警报器</span>
                            </div>
                            <div class="text-xs text-slate-600 space-y-2 leading-relaxed">
                                <p><strong>核心功能：</strong> 识别恐惧、危险、威胁，触发“战或逃”生理应激反应。</p>
                                <p><strong>0-3岁发育机制：</strong> 杏仁核在出生时就已经相对成熟，能快速感知爸爸的语气、面部表情与紧张气氛。8-18个月出现的**分离焦虑（Separation Anxiety）**就是因为杏仁核发现熟悉照顾者离开时触发了威胁警报。</p>
                                <div class="bg-rose-50 border border-rose-100 p-2.5 rounded-lg text-rose-900 font-medium">
                                    💡 <strong>奶爸指导：</strong> 宝宝哭闹时，杏仁核正处于超负荷警报状态。此时严厉训斥只会让杏仁核更加恐慌。奶爸需要用轻柔低沉的语调与温暖拥抱，帮助其下调杏仁核兴奋度（Co-regulation 共同调节）。
                                </div>
                            </div>
                        </div>

                        <!-- Structure 3: 前额叶皮层 -->
                        <div class="bg-slate-50 border border-slate-200 p-4 rounded-xl space-y-3">
                            <div class="flex items-center justify-between border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                                    <span class="text-xl">🧭</span>
                                    <span>前额叶皮层 (PFC)</span>
                                </h4>
                                <span class="bg-emerald-100 text-emerald-700 text-[10px] px-2 py-0.5 rounded font-bold">理性控制与指挥官</span>
                            </div>
                            <div class="text-xs text-slate-600 space-y-2 leading-relaxed">
                                <p><strong>核心功能：</strong> 理性逻辑、冲动抑制、延迟满足、工作记忆与自我情绪调节。</p>
                                <p><strong>0-3岁发育机制：</strong> 前额叶皮层是大脑中**发育最晚、耗时最长**的区域（要到25岁左右才完全发育成熟！）。2岁左右孩子大发脾气、撒泼打滚，根本原因是杏仁核爆发时，极其幼稚的前额叶皮层根本无法提供足够的“抑制性神经递质（GABA）”来压制冲动！</p>
                                <div class="bg-emerald-50 border border-emerald-100 p-2.5 rounded-lg text-emerald-900 font-medium">
                                    💡 <strong>奶爸指导：</strong> 不要指望2岁孩子能“像大人一样懂理智”。每次冷静地帮孩子命名情绪（“我知道你因为玩具坏了很生气”），就是在帮他的前额叶皮层建立神经连接通路！
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Neurohormones Deep Dive Section -->
                    <div class="border-t border-slate-200 pt-5 space-y-4">
                        <div class="flex items-center space-x-2">
                            <span class="p-2 bg-pink-100 text-pink-700 rounded-xl text-base"><i class="fa-solid fa-flask"></i></span>
                            <div>
                                <h4 class="font-extrabold text-slate-800 text-sm">0-3岁四大核心育儿神经激素与化学递质</h4>
                                <p class="text-[11px] text-slate-500">婴儿大脑通过激素调控情绪与生理发育。奶爸的行为是孩子内分泌系统的最高“调节阀”。</p>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            
                            <!-- Hormone 1: 皮质醇 -->
                            <div class="bg-gradient-to-br from-rose-50 to-orange-50/40 border border-rose-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-rose-200/80 pb-2">
                                    <span class="font-extrabold text-rose-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-triangle-exclamation text-rose-600"></i>
                                        <span>1. 皮质醇 (Cortisol) —— 压力与应激激素</span>
                                    </span>
                                    <span class="bg-rose-200 text-rose-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">需严格管控下调</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>⚡ 何时飙升分泌：</strong> 长时间哭闹无回应、缺乏安全感、惊吓受暴击、过度刺激、父子冷漠隔离或家庭吵闹应激。</p>
                                    <p><strong>⚠️ 高皮质醇毒性危害：</strong> 持续高水平皮质醇会透过血脑屏障，直接损害海马体突触连接，导致杏仁核过度警觉（易惊恐、敏感），并阻碍前额叶皮层正常发育。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-rose-200 text-rose-950 font-medium">
                                        🛡️ <strong>奶爸抑制皮质醇方案：</strong> 哭闹时进行“3-5秒敏锐回应”；进行胸对胸皮肤接触（Kangaroo Care）；用沉稳低频的男低音安抚；提供预测性强的规律作息。
                                    </div>
                                </div>
                            </div>

                            <!-- Hormone 2: 催产素 -->
                            <div class="bg-gradient-to-br from-pink-50 to-rose-50/30 border border-pink-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-pink-200/80 pb-2">
                                    <span class="font-extrabold text-pink-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-heart text-pink-600"></i>
                                        <span>2. 催产素 (Oxytocin) —— 爱的连接与依恋激素</span>
                                    </span>
                                    <span class="bg-pink-200 text-pink-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">安全依恋基石</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>💖 何时爆发分泌：</strong> 温和的眼睛注视对视、皮肤接触、轻柔抚触按摩、拥抱贴贴、听到爸爸温暖哼唱时。</p>
                                    <p><strong>✨ 神经生理作用：</strong> 催产素是皮质醇的天然“解毒剂”！它能迅速关闭杏仁核警报，降低心率与血压，诱发深层信任感与安全感，奠定安全型依恋基础。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-pink-200 text-pink-950 font-medium">
                                        🚀 <strong>奶爸提升催产素方案：</strong> 每天至少15分钟无手机专注抚触；喂奶时维持微笑对视；孩子受惊时敞开双臂拥抱充能。
                                    </div>
                                </div>
                            </div>

                            <!-- Hormone 3: 内啡肽 -->
                            <div class="bg-gradient-to-br from-amber-50 to-yellow-50/40 border border-amber-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-amber-200/80 pb-2">
                                    <span class="font-extrabold text-amber-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-smile-beam text-amber-600"></i>
                                        <span>3. 内啡肽 (Endorphin) —— 天然镇痛与满足愉快激素</span>
                                    </span>
                                    <span class="bg-amber-200 text-amber-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">生理舒适与欢笑</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>🌿 何时自然释放：</strong> 有节奏的摇晃、背部轻拍、舒缓的音乐、出牙或肠绞痛缓解后、欢快打闹与咯咯大笑时。</p>
                                    <p><strong>✨ 神经生理作用：</strong> 提供天然镇痛与止痛作用，带来深层身心放松感与愉悦感，帮助婴儿平稳入睡与缓解身体不适。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-amber-200 text-amber-950 font-medium">
                                        🚀 <strong>奶爸提升内啡肽方案：</strong> 做节奏性轻拍与摇篮曲呼唤；出牙期提供冰镇咬咬乐；多玩“躲猫猫（Peek-a-boo）”引发咯咯笑。
                                    </div>
                                </div>
                            </div>

                            <!-- Hormone 4: 多巴胺与血清素 -->
                            <div class="bg-gradient-to-br from-indigo-50 to-sky-50/40 border border-indigo-200 rounded-xl p-4 space-y-2.5">
                                <div class="flex justify-between items-center border-b border-indigo-200/80 pb-2">
                                    <span class="font-extrabold text-indigo-900 flex items-center gap-1.5 text-xs sm:text-sm">
                                        <i class="fa-solid fa-sun text-indigo-600"></i>
                                        <span>4. 多巴胺&血清素 (Dopamine & Serotonin) —— 探索与情绪稳定</span>
                                    </span>
                                    <span class="bg-indigo-200 text-indigo-900 text-[10px] px-2 py-0.5 rounded font-mono font-bold">探索动力与专注</span>
                                </div>
                                <div class="space-y-1.5 text-slate-700 leading-relaxed">
                                    <p><strong>🌟 何时激发释放：</strong> 好奇心被激活（成功抓握新玩具）、大运动尝试成功被赞许时（多巴胺）；适度户外阳光照射、高质感睡眠与均衡营养（血清素）。</p>
                                    <p><strong>✨ 神经生理作用：</strong> 多巴胺驱动自主学习与探索欲望；血清素维持整体情绪平稳与昼夜作息规律。</p>
                                    <div class="bg-white/80 p-2.5 rounded-lg border border-indigo-200 text-indigo-950 font-medium">
                                        🚀 <strong>奶爸提升方案：</strong> 每日带孩子户外晒太阳30分钟；对宝宝微小的运动突破（如第一次翻身成功）给予热情肯定与掌声。
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>
            </section>

            <!-- ================= TAB 8: 0-10岁儿童情绪理解演进 ================= -->
            <section id="tab-emotion" ref="tab-emotion" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-6">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-heart-pulse text-rose-500"></i>
                                <span>0 - 10 岁儿童情绪认知、理解力与心理演进全景路线图</span>
                            </h3>
                            <p class="text-xs text-slate-500">根据发展心理学 Pons & Harris 情绪理解三阶模型：揭密孩子为何要到 10 岁左右才能彻底理解所有复杂情绪。</p>
                        </div>
                        <span class="bg-rose-100 text-rose-800 text-xs px-2.5 py-1 rounded-full font-semibold">发展心理学</span>
                    </div>

                    <!-- Key Psychological Insight Banner -->
                    <div class="bg-gradient-to-r from-rose-50 via-amber-50 to-sky-50 border border-rose-200 p-4 rounded-xl space-y-2 text-xs">
                        <div class="font-bold text-rose-900 flex items-center space-x-2 text-sm">
                            <i class="fa-solid fa-lightbulb text-amber-500"></i>
                            <span>💡 奶爸必读：为什么心理学研究表明“孩子要到 10 岁左右才能彻底理解所有类型的情绪”？</span>
                        </div>
                        <p class="text-slate-700 leading-relaxed">
                            情绪理解并非一蹴而就！幼儿期（2-4岁）只能理解**基本外在情绪（喜怒哀惧）**；5-7岁开始理解**心理信念与外在表情掩饰（如失望时佯装微笑）**；而只有到了 **9-10 岁（小学高年级）**，大脑的心智理论（Theory of Mind）与前额叶网络高度整合后，孩子才能真正掌握**矛盾/混合情绪（Ambivalent Emotions，如同时感到又高兴又害怕）**、**元情绪（Meta-emotions）**以及**道德内疚与自我反思**。
                        </p>
                    </div>

                    <!-- 4 Developmental Stages of Emotion Understanding -->
                    <div class="space-y-6">
                        
                        <!-- Phase 1 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-sky-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-sky-600 text-white flex items-center justify-center text-xs">1</span>
                                    <span>阶段一：0 - 2 岁 (情绪直觉、依恋与自我意识萌芽)</span>
                                </h4>
                                <span class="bg-sky-100 text-sky-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">基本情绪识别</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li><strong>0-6个月：</strong> 依赖“情绪传染”，通过声音与面部辨别喜怒哀惧。</li>
                                        <li><strong>8-12个月：</strong> 出现“社会性参照”，遇到陌生环境时观察奶爸的面部表情来判断安全与否。</li>
                                        <li><strong>18-24个月：</strong> “镜像自我认知”完成，首次萌发**自我意识情绪（羞耻 Shame、骄傲 Pride）**。</li>
                                    </ul>
                                </div>
                                <div class="bg-sky-50 border border-sky-100 p-3 rounded-lg space-y-1 text-sky-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">提供稳定的安全依恋。面对宝宝哭闹，及时给予回应与抚触，让宝宝的大脑建立“外界是安全可预测”的底层信念。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Phase 2 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-emerald-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs">2</span>
                                    <span>阶段二：2 - 5 岁 (外在因果理解与初步共情)</span>
                                </h4>
                                <span class="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">外在因果与标签</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li>能明确建立“外在事件 ➜ 情绪”的因果链条（如：“玩具抢走了，所以我生气的”）。</li>
                                        <li>掌握情绪词汇（高兴、难过、害怕、生气），能够为自己和同伴的情绪贴标签。</li>
                                        <li>出现初步的同理心与安慰行为（如看到别人哭会把自己的小熊送给对方）。</li>
                                    </ul>
                                </div>
                                <div class="bg-emerald-50 border border-emerald-100 p-3 rounded-lg space-y-1 text-emerald-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">广泛开展“情绪命名”。当{{this.userInfo.chlidName}}发脾气时，帮她表达出心中感受：“你现在很失望对不对？”被理解的感受能迅速平复情绪。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Phase 3 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-amber-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-xs">3</span>
                                    <span>阶段三：5 - 7 岁 (心理信念、内心想法与表现规则)</span>
                                </h4>
                                <span class="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">表情掩饰与意图理解</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li>理解“愿望与信念”决定情绪：明白两个人面对同一种情况，因为期待不同会产生完全不同的情绪。</li>
                                        <li>掌握**情绪表达规则**：明白收到不喜欢的礼物时，为了礼貌可以在脸上佯装微笑。</li>
                                        <li>能区分“真实的内心感受”与“外表展现的表情”。</li>
                                    </ul>
                                </div>
                                <div class="bg-amber-50 border border-amber-100 p-3 rounded-lg space-y-1 text-amber-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">多通过绘本讨论角色内心世界：“你觉得小熊虽然在笑，他心里真的开心吗？”鼓励孩子接纳真诚的内心情感，不强求盲目压抑。</p>
                                </div>
                            </div>
                        </div>

                        <!-- Phase 4 -->
                        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                            <div class="flex justify-between items-center border-b border-slate-200 pb-2">
                                <h4 class="font-bold text-sm text-purple-800 flex items-center gap-2">
                                    <span class="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">4</span>
                                    <span>阶段四：8 - 10 岁 (矛盾/混合情绪、道德反思与完全成熟)</span>
                                </h4>
                                <span class="bg-purple-100 text-purple-800 text-[10px] px-2 py-0.5 rounded font-mono font-bold">情绪认知终极成熟阶段</span>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div class="space-y-1.5">
                                    <strong class="text-slate-800">🧠 情绪理解发展特征（终极突破）：</strong>
                                    <ul class="list-disc list-inside text-slate-600 space-y-1 leading-relaxed">
                                        <li><strong>理解矛盾/混合情绪：</strong> 彻底明白人可以**同时对同一件事产生两种对立的情绪**（如：即将去新学校，既感到兴奋又感到害怕与离别悲伤）。</li>
                                        <li><strong>掌握认知重塑调节：</strong> 明白可以通过改变看待问题的想法来改变自己的情绪。</li>
                                        <li><strong>深化道德元情绪：</strong> 深刻体验愧疚、内疚与良心谴责，情绪理解能力正式达到成年人水平！</li>
                                    </ul>
                                </div>
                                <div class="bg-purple-50 border border-purple-100 p-3 rounded-lg space-y-1 text-purple-900">
                                    <strong>👨‍👦 奶爸育儿应对策略：</strong>
                                    <p class="leading-relaxed">做{{this.userInfo.chlidName}}的知心朋友与人生导师。当他面对复杂矛盾心态时（如又想赢又怕输），引导她表达并接纳这种复杂的心理，教她学会认知重塑。</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <!-- ================= TAB 10: WHO 生长曲线与评估 ================= -->
            <section id="tab-charts" ref="tab-charts" class="tab-content hidden space-y-6">
                <div class="bg-white rounded-xl border border-slate-200 p-5 shadow-2xs space-y-4">
                    <div class="flex justify-between items-center border-b border-slate-100 pb-3">
                        <div>
                            <h3 class="text-base font-bold text-slate-800 flex items-center gap-2">
                                <i class="fa-solid fa-pen-ruler text-purple-600"></i>
                                <span>{{this.userInfo.chlidName}}孩子测量数据录入与对比分析</span>
                            </h3>
                            <p class="text-xs text-slate-500">直接录入月龄与测量数据，实时自动绘制曲线图并与 WHO 国际百分位标准比对生成智能评价。</p>
                        </div>
                        <span class="text-xs bg-purple-100 text-purple-700 px-2.5 py-1 rounded-full font-semibold">数据录入</span>
                    </div>

                    <form id="growthEntryForm" ref="growthEntryForm" @submit.prevent="saveGrowthMeasurement($event)" class="bg-slate-50 border border-slate-200 p-4 rounded-xl text-xs space-y-3">
                        <div class="font-bold text-slate-800 text-xs">📏 新增一条发育测量数据：</div>
                        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">月龄 (0-36个月) *</label>
                                <input type="number" min="0" max="36" id="entryMonth" ref="entryMonth" required placeholder="如: 3" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">体重 (kg) *</label>
                                <input type="number" step="0.1" id="entryWeight" ref="entryWeight" required placeholder="如: 6.5" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">身高/身长 (cm) *</label>
                                <input type="number" step="0.1" id="entryHeight" ref="entryHeight" required placeholder="如: 61.5" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                            <div>
                                <label class="block text-slate-600 font-medium mb-1">头围 (cm)</label>
                                <input type="number" step="0.1" id="entryHead" ref="entryHead" placeholder="如: 40.2" class="w-full px-3 py-1.5 border border-slate-300 rounded-lg">
                            </div>
                        </div>
                        <div class="flex justify-between items-center pt-1">
                            <span class="text-[11px] text-slate-400">对比标准：WHO 0-3岁男孩百分位数标准 (P3, P50, P97)</span>
                            <button type="submit" class="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg font-semibold transition">录入并生成分析</button>
                        </div>
                    </form>

                    <!-- Growth Summary Card -->
                    <div id="growthSummaryCard" class="bg-slate-900 text-white p-4 rounded-xl space-y-2 text-xs shadow-md">
                        <div class="flex items-center justify-between border-b border-slate-700 pb-2">
                            <span class="font-bold flex items-center gap-1.5 text-purple-300">
                                <i class="fa-solid fa-square-poll-vertical"></i>
                                <span>{{this.userInfo.chlidName}}当前生长发育评估总结</span>
                            </span>
                            <span id="latestMonthTag" ref="latestMonthTag" class="bg-purple-500/30 text-purple-200 px-2 py-0.5 rounded font-mono text-[10px]">最新月龄: {{this.latestMonth || 0}}个月</span>
                        </div>
                        <p id="growthSummaryText" ref="growthSummaryText" class="text-slate-200 leading-relaxed pt-1">
                            加载生长评价中...
                        </p>
                    </div>

                    <!-- Charts Display -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                        <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <h4 class="text-xs font-bold text-slate-700 mb-2 text-center">体重发育曲线对比 (kg)</h4>
                            <div class="h-64">
                                <canvas id="weightChartCanvas" ref="weightChartCanvas"></canvas>
                            </div>
                        </div>
                        <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                            <h4 class="text-xs font-bold text-slate-700 mb-2 text-center">身高/身长发育曲线对比 (cm)</h4>
                            <div class="h-64">
                                <canvas id="heightChartCanvas" ref="heightChartCanvas"></canvas>
                            </div>
                        </div>
                    </div>

                    <!-- History Growth Records Container (Matching Fetal Checkup Design) -->
                    <div class="space-y-3 pt-4 border-t border-slate-100">
                        <h4 class="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                            <i class="fa-solid fa-clock-rotate-left text-purple-600"></i>
                            <span>📋 历史生长发育测量档案库：</span>
                        </h4>
                        <div id="growthHistoryContainer" ref="growthHistoryContainer" @click="delegateClick" class="space-y-3">
                            <!-- Dynamic rendered growth history cards -->
                        </div>
                    </div>
                </div>
            </section>

            <!-- ================= TAB 11: 奶爸心情微语 (随手记动态) ================= -->
            <section id="tab-mood" ref="tab-mood" class="tab-content hidden space-y-6">
                <div class="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-2xs space-y-5">

                    <!-- Section Title -->
                    <div class="flex items-center space-x-3 border-b border-slate-100 pb-4">
                        <div class="w-11 h-11 rounded-2xl bg-gradient-to-tr from-amber-400 to-rose-400 flex items-center justify-center text-white text-xl font-bold shadow-md shadow-amber-500/20">
                            <i class="fa-solid fa-heart-pulse"></i>
                        </div>
                        <div>
                            <h3 class="text-base font-extrabold text-slate-800">奶爸心情微语</h3>
                            <p class="text-xs text-slate-500 mt-0.5">随手记录育儿过程中的喜怒哀乐、瞬间感悟，支持配图与视频。</p>
                        </div>
                    </div>

                    <!-- Post Input Box -->
                    <form ref="moodPostForm" @submit.prevent="saveMoodPost()" class="relative bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 shadow-inner">
                        <div class="flex items-center space-x-2">
                            <span class="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center shadow-xs overflow-hidden" id="moodInputAvatar" ref="moodInputAvatar">
                                <img v-if="avatarUrl" :src="avatarUrl" alt="奶爸头像" class="w-full h-full object-cover">
                                <i v-else class="fa-solid fa-baby text-sm"></i>
                            </span>
                            <span class="text-xs font-bold text-slate-700">发表一条心情随笔...</span>
                        </div>

                        <!-- Mood Tag Select & Textarea -->
                        <div class="space-y-2">
                            <textarea id="moodTextInput" ref="moodTextInput" rows="3" required placeholder="这一刻在想什么？比如：今天听了胎心音，心里踏实多了..." class="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500 leading-relaxed"></textarea>
                        </div>

                        <!-- Attachments & Controls -->
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-slate-200/80">
                            <div class="flex flex-wrap items-center gap-2 text-xs">
                                <!-- Mood Tag selector -->
                                <select id="moodTagSelect" ref="moodTagSelect" class="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700">
                                    <template v-if="moodList.length > 1">
                                        <option v-for="item in moodList" :key="item.id || item.dictName" :value="item.id">{{ item.dictName }}</option>
                                    </template>
                                </select>

                                <!-- Upload Button -->
                                <label class="cursor-pointer bg-white border border-slate-300 hover:border-amber-400 hover:bg-amber-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center space-x-1.5">
                                    <i class="fa-solid fa-image text-amber-500"></i>
                                    <span>配图/视频</span>
                                    <input type="file" id="moodFileInput" ref="moodFileInput" accept="image/*,video/*" multiple @change="handleMoodFileUpload($event)" class="hidden">
                                </label>

                                <!-- Record Button（与"新建时光成长日记"录音按钮同款功能与样式） -->
                                <button type="button" @click="toggleMoodRecord()" :class="moodIsRecording ? 'bg-rose-100 hover:bg-rose-200 text-rose-700 px-2 py-1 rounded text-[11px] whitespace-nowrap font-semibold animate-pulse' : 'bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-1 rounded text-[11px] whitespace-nowrap font-semibold'">{{ moodIsRecording ? '停止' : '录音' }}</button>
                            </div>

                            <!-- 右侧操作按钮：新建显示"发布微语"，编辑显示"取消更新 / 更新微语"（紧挨排列） -->
                            <div class="flex items-center gap-2">
                                <template v-if="editingMoodId">
                                    <button type="button" :disabled="moodSaving" @click="cancelEditMood()" class="bg-slate-200 hover:bg-slate-300 disabled:opacity-60 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold shadow-sm transition flex items-center justify-center space-x-1.5">
                                        <span>取消更新</span>
                                    </button>
                                    <button type="button" :disabled="moodSaving" @click="updateMoodPost()" class="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-2 rounded-xl text-xs font-bold shadow-sm transition flex items-center justify-center space-x-1.5">
                                        <i v-if="moodSaving" class="fa-solid fa-circle-notch fa-spin"></i>
                                        <span>{{ moodSaving ? '更新中...' : '更新微语' }}</span>
                                        <i v-if="!moodSaving" class="fa-solid fa-floppy-disk"></i>
                                    </button>
                                </template>
                                <button v-else type="submit" :disabled="moodSaving" class="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-2 rounded-xl text-xs font-bold shadow-sm transition flex items-center justify-center space-x-1.5">
                                    <i v-if="moodSaving" class="fa-solid fa-circle-notch fa-spin"></i>
                                    <span>{{ moodSaving ? '发布中...' : '发布微语' }}</span>
                                    <i v-if="!moodSaving" class="fa-solid fa-paper-plane"></i>
                                </button>
                            </div>
                        </div>

                        <!-- 已上传配图/视频/录音列表（数量不限，可多次上传、逐个删除；录音可预览播放） -->
                        <div v-if="moodFiles.length > 0" class="grid grid-cols-6 gap-1.5 pt-1">
                            <!-- 图片 / 视频缩略图 -->
                            <div v-for="(item, idx) in moodFiles" v-show="item.type !== 'audio'" :key="item.key" class="relative group aspect-square rounded-md overflow-hidden border border-amber-200 bg-slate-100">
                                <img v-if="item.type === 'image'" :src="item.preview" class="w-full h-full object-cover" alt="">
                                <video v-else :src="item.preview" class="w-full h-full object-cover" muted preload="metadata"></video>
                                <span v-if="item.type === 'video'" class="absolute inset-0 flex items-center justify-center bg-black/25 text-white text-xs pointer-events-none">
                                    <i class="fa-solid fa-play"></i>
                                </span>
                                <div v-if="item.status === 'uploading'" class="absolute inset-0 bg-black/40 flex items-center justify-center text-white">
                                    <i class="fa-solid fa-circle-notch fa-spin text-xs"></i>
                                </div>
                                <button type="button" @click="removeMoodFile(idx)" title="移除" class="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/55 hover:bg-red-600 text-white text-[9px] flex items-center justify-center">
                                    <i class="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                            <!-- 录音卡片：可播放预览、可删除 -->
                            <div v-for="(item, idx) in moodFiles" v-show="item.type === 'audio'" :key="'audio-'+item.key" class="relative col-span-6 sm:col-span-3 flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-2 py-1.5">
                                <i class="fa-solid fa-microphone text-purple-500 text-sm shrink-0"></i>
                                <audio :src="item.status === 'done' && item.url ? resolveMediaUrl(item.url) : item.preview" controls class="flex-1 min-w-0 h-7"></audio>
                                <span v-if="item.status === 'uploading'" class="text-[10px] text-purple-400 shrink-0 flex items-center gap-1">
                                    <i class="fa-solid fa-circle-notch fa-spin"></i>上传中
                                </span>
                                <button type="button" @click="removeMoodFile(idx)" title="删除录音" class="shrink-0 w-4 h-4 rounded-full bg-purple-200 hover:bg-red-500 hover:text-white text-purple-600 text-[9px] flex items-center justify-center">
                                    <i class="fa-solid fa-xmark"></i>
                                </button>
                            </div>
                        </div>
                        <!-- 发布加载遮罩 -->
                        <div v-if="moodSaving" class="absolute inset-0 bg-white/70 backdrop-blur-xs rounded-2xl flex flex-col items-center justify-center gap-3 z-10">
                            <i class="fa-solid fa-paper-plane text-amber-500 text-3xl fa-bounce"></i>
                            <p class="text-sm font-semibold text-amber-800">正在发布心情微语，请稍候...</p>
                        </div>
                    </form>

                    <!-- Mood Posts Feed List -->
                    <div class="space-y-4">
                        <div class="flex flex-wrap justify-between items-center border-b border-slate-100 pb-2 gap-2">
                            <h4 class="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                <i class="fa-solid fa-bars-staggered text-amber-500"></i>
                                <span>奶爸心情微语</span>
                            </h4>
                            <span class="text-[11px] text-slate-400 font-mono">最新发布在前</span>
                        </div>

                        <!-- Category Filter Buttons（与时光成长日记同款，分类来自 moodList 字典） -->
                        <div class="flex flex-wrap gap-2 text-xs">
                            <button @click="filterMood('ALL')" :class="['mood-filter-btn px-3 py-1 rounded-lg font-medium transition', currentMoodCategory === 'ALL' ? 'active bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-amber-50']">全部心情 ({{ moodPosts.length }})</button>
                            <button v-for="item in moodList" :key="item.id || item.dictName" @click="filterMood(item.id)" :class="['mood-filter-btn px-3 py-1 rounded-lg font-medium transition', currentMoodCategory === item.id ? 'active bg-amber-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-amber-50']">{{ item.dictName }} ({{ moodCountByType(item.id) }})</button>
                        </div>

                        <div id="moodFeedContainer" ref="moodFeedContainer" @click="delegateClick" class="space-y-4">
                            <!-- Dynamic rendering of mood posts -->
                        </div>
                    </div>

                </div>
            </section>

        </div>

        <div id="icp" class="mt-auto py-4 text-center border-t border-slate-100 bg-white/60">
            <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-sky-600 transition font-mono">
                <i class="fa-solid fa-shield-halved text-[13px]"></i>
                <span>粤ICP备2026121758-1</span>
            </a>
        </div>
    </main>

    <!-- Modal 1: New Growth Diary Modal -->
    <div id="quickAddModal" ref="quickAddModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4" @click.self="closeModal('quickAddModal')">
        <div class="bg-white rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button @click="closeModal('quickAddModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-book"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">{{ editingDiaryId ? '编辑时光成长日记' : '新建时光成长日记' }} (支持本地多媒体上传)</h3>
                    <p class="text-[11px] text-slate-500">直接上传本地的照片、短视频或录音文件，随时记录秉宣成长。</p>
                </div>
            </div>

            <!-- Quick Template Shortcuts -->
            <div class="space-y-1">
                <label class="block text-[11px] font-bold text-slate-600">⚡ 一键应用预设日记模板：</label>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[11px]">
                    <button @click="applyWolaiTemplate('daily')" class="py-1 px-2 bg-slate-100 hover:bg-sky-50 text-slate-700 hover:text-sky-700 border border-slate-200 rounded-lg text-left transition truncate">🥣 每日育儿复盘</button>
                    <button @click="applyWolaiTemplate('checkup')" class="py-1 px-2 bg-slate-100 hover:bg-rose-50 text-slate-700 hover:text-rose-700 border border-slate-200 rounded-lg text-left transition truncate">🏥 孕期产检随记</button>
                    <button @click="applyWolaiTemplate('milestone')" class="py-1 px-2 bg-slate-100 hover:bg-amber-50 text-slate-700 hover:text-amber-700 border border-slate-200 rounded-lg text-left transition truncate">🏆 首次里程碑</button>
                    <button @click="applyWolaiTemplate('voice')" class="py-1 px-2 bg-slate-100 hover:bg-purple-50 text-slate-700 hover:text-purple-700 border border-slate-200 rounded-lg text-left transition truncate">🎙️ 爸爸声音日记</button>
                </div>
            </div>

            <!-- Form Inputs -->
            <form id="diaryEntryForm" ref="diaryEntryForm" @submit.prevent="onDiarySubmit">
            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">日记标题 *</label>
                    <input type="text" id="diaryTitleInput" ref="diaryTitleInput" required placeholder="请输入日记标题..." class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 font-bold text-slate-800">
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">分类标签 *</label>
                        <select id="diaryCategoryInput" ref="diaryCategoryInput" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                            <option v-for="(item, index) in diaryList" :key="item.id || item.dictName" :value="item.id" :selected="index === 0">{{ item.dictName }}</option>
                        </select>
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">记录日期 *</label>
                        <input type="date" id="diaryDateInput" ref="diaryDateInput" required @click="onDatePickerClick($event)" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 cursor-pointer cursor-pointer">
                    </div>
                </div>

                
                <div>
                    <label class="block font-medium text-slate-700 mb-1">日记正文内容 *</label>
                    <!-- v-if 控制弹窗打开后再创建编辑器（在 display:none 中创建会导致中文输入报 Cannot resolve a DOM point） -->
                    <!-- flex 列布局：Toolbar 固定高度，编辑区 flex-1 占满剩余高度，内容超出时在编辑区内部上下滚动 -->
                    <div v-if="diaryEditorVisible" class="bg-white rounded-lg overflow-hidden border border-slate-300 focus-within:ring-1 focus-within:ring-sky-500 h-[350px] flex flex-col">
                        <!-- Toolbar 等编辑器实例就绪后再挂载，否则 :editor 为 null 导致工具栏不显示 -->
                        <Toolbar v-if="diaryEditorReady" :editor="getDiaryEditor()" :defaultConfig="diaryToolbarConfig" mode="default" class="border-b border-slate-200 shrink-0" />
                        <!-- 不使用 v-model：modelValue 变化会触发编辑器内容重置，中文输入法 compositionend 时报 Cannot resolve a DOM point -->
                        <div class="flex-1 min-h-0">
                            <Editor :defaultConfig="diaryEditorConfig" mode="default" style="height: 100%;" @onCreated="onDiaryEditorCreated" @onChange="onDiaryEditorChanged" />
                        </div>
                    </div>
                    <div v-else class="bg-white rounded-lg border border-slate-300 h-[300px] flex items-center justify-center text-xs text-slate-400">
                        编辑器加载中...
                    </div>
                </div>

                <!-- Local File Upload Box（界面隐藏：手动选择附件入口不再展示，但 DOM 与上传逻辑保留，录音完成后仍经此通道上传服务器） -->
                <div class="bg-sky-50/60 border-2 border-dashed border-sky-200 p-3.5 rounded-xl space-y-2 hidden">
                    <label class="block font-bold text-sky-900 text-xs flex items-center justify-between">
                        <span class="flex items-center gap-1.5">
                            <i class="fa-solid fa-cloud-arrow-up text-sky-600"></i>
                            <span>上传本地图片/短视频/录音附件：</span>
                        </span>
                        <span class="text-[10px] text-slate-400 font-normal">支持照片、.mp4等</span>
                    </label>
                    
                    <input type="file" id="diaryLocalFileInput" ref="diaryLocalFileInput" accept="image/*,video/*,audio/*" @change="handleLocalFileUpload($event)" class="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-sky-600 file:text-white hover:file:bg-sky-700 cursor-pointer">

                    <div id="localFilePreviewBadge" ref="localFilePreviewBadge" class="hidden bg-white p-2 rounded-lg border border-sky-200 text-[11px] text-slate-700 flex items-center justify-between font-mono">
                        <span id="localFileNameText" ref="localFileNameText" class="truncate max-w-xs">已选择文件</span>
                        <span id="localFileTypeTag" ref="localFileTypeTag" class="bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-bold">照片</span>
                    </div>
                </div>

                <!-- Web URL & Voice simulation optional inputs -->
                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">网络媒体链接 (可选)</label>
                        <input type="text" id="diaryMediaInput" ref="diaryMediaInput" placeholder="https://..." class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs">
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">语音时长 (如 01:30)</label>
                        <div class="flex space-x-1">
                            <input type="text" id="diaryAudioInput" ref="diaryAudioInput" placeholder="如 01:30" class="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs">
                            <button type="button" @click="simulateVoiceRecord()" :class="isRecording ? 'bg-rose-100 hover:bg-rose-200 text-rose-700 px-2 py-1 rounded text-[11px] whitespace-nowrap font-semibold animate-pulse' : 'bg-purple-100 hover:bg-purple-200 text-purple-700 px-2 py-1 rounded text-[11px] whitespace-nowrap font-semibold'">{{ isRecording ? '停止' : '录音' }}</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" @click="closeModal('quickAddModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button v-if="!editingDiaryId" type="submit" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-xs">保存日记</button>
                <button v-else type="submit" class="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold shadow-xs">更新日记</button>
            </div>
            </form>
        </div>
    </div>

    <!-- Modal 2: Read Diary Detail Modal -->
    <div id="diaryDetailModal" ref="diaryDetailModal" class="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4" @click.self="closeModal('diaryDetailModal')">
        <div class="bg-white rounded-2xl max-w-2xl w-full shadow-2xl relative max-h-[90vh] flex flex-col overflow-hidden">
            <button @click="closeModal('diaryDetailModal')" class="absolute top-4 right-4 z-10 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <!-- Header info（固定，不随内容滚动） -->
            <div class="space-y-1.5 border-b border-slate-100 px-6 pt-6 pb-3 shrink-0">
                <div class="flex items-center space-x-2">
                    <span id="detailCategoryTag" ref="detailCategoryTag" class="bg-sky-100 text-sky-800 text-xs px-2.5 py-0.5 rounded-full font-bold">日常</span>
                    <span id="detailDate" ref="detailDate" class="text-xs text-slate-400 font-mono">2026-08-15</span>
                </div>
                <h3 id="detailTitle" ref="detailTitle" class="text-lg font-extrabold text-slate-800 pr-6">日记标题</h3>
            </div>

            <!-- 中间内容区（仅此区域滚动） -->
            <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 py-4 space-y-4">
                <!-- Main text body -->
                <div id="detailContent" ref="detailContent" class="diary-rich-content text-xs sm:text-sm text-slate-700 leading-relaxed font-sans bg-slate-50 p-4 rounded-xl border border-slate-200/80">
                    日记正文内容...
                </div>

                <!-- Media Preview（多图/多视频/音频，动态渲染；图片点击进入全屏多图查看） -->
                <div id="detailMediaBox" ref="detailMediaBox" class="hidden space-y-2">
                    <label class="block text-xs font-bold text-slate-700">📷 附件媒体：</label>
                    <div id="detailMediaGrid" ref="detailMediaGrid" class="grid grid-cols-3 gap-2"></div>
                </div>

                <!-- Audio Player Box -->
                <div id="detailAudioBox" ref="detailAudioBox" class="hidden bg-purple-50 border border-purple-200 p-3 rounded-xl space-y-2">
                    <div class="flex items-center justify-between">
                        <span class="text-xs font-bold text-purple-900 flex items-center gap-1.5">
                            <i class="fa-solid fa-microphone text-purple-600"></i>
                            <span>奶爸声音留声机</span>
                        </span>
                        <span id="detailAudioDuration" ref="detailAudioDuration" class="text-[10px] bg-purple-200 text-purple-800 px-2 py-0.5 rounded font-mono font-bold">01:15</span>
                    </div>
                    <button @click="playDetailVoice()" class="w-full py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-semibold transition flex items-center justify-center space-x-1">
                        <i class="fa-solid fa-volume-high"></i>
                        <span>播放声音留声机</span>
                    </button>
                </div>
            </div>

            <!-- Footer actions（固定，不随内容滚动） -->
            <div class="flex justify-between items-center px-6 pt-3 pb-4 border-t border-slate-100 shrink-0">
                <button v-if="isLoggedIn" id="deleteDetailBtn" ref="deleteDetailBtn" class="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-xs font-semibold transition">删除此日记</button>
                <span v-else></span>
                <div class="flex items-center space-x-2">
                    <button v-if="isLoggedIn" @click="editDiaryFromDetail()" class="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold transition flex items-center space-x-1">
                        <i class="fa-solid fa-pen-to-square"></i>
                        <span>编辑</span>
                    </button>
                    <button @click="closeModal('diaryDetailModal')" class="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition">关闭</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal 3: Add Media to Growth Album Modal -->
    <div id="addAlbumMediaModal" ref="addAlbumMediaModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4" @click.self="closeModal('addAlbumMediaModal')">
        <div class="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button @click="closeModal('addAlbumMediaModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-images"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">{{ editingAlbumId ? '编辑影像素材' : '添加影像素材到秉宣相册' }}</h3>
                    <p class="text-[11px] text-slate-500">上传本地图片或短视频，按主题归档。</p>
                </div>
            </div>

            <form id="albumMediaForm" ref="albumMediaForm" @submit.prevent="editingAlbumId ? updateAlbumMedia() : saveNewAlbumMedia()">
            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">素材标题/简介 *</label>
                    <input type="text" id="albumMediaTitleInput" ref="albumMediaTitleInput" required placeholder="例如：18周B超小脸照 / 秉宣第一次抬头" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500 font-semibold">
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">主题分类 *</label>
                        <select id="albumMediaTagInput" ref="albumMediaTagInput" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500">
                            <template v-if="albumList.length > 0">
                                <option v-for="item in albumList" :key="item.id" :value="item.id">{{ item.dictName }}</option>
                            </template>
                        </select>
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">拍摄日期 *</label>
                        <input type="date" id="albumMediaDateInput" ref="albumMediaDateInput" required @click="onDatePickerClick($event)" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500 cursor-pointer">
                    </div>
                </div>

                <!-- Local File Upload Box for Album -->
                <div class="bg-teal-50/60 border-2 border-dashed border-teal-200 p-3.5 rounded-xl space-y-2">
                    <label class="block font-bold text-teal-900 text-xs flex items-center justify-between">
                        <span class="flex items-center gap-1.5">
                            <i class="fa-solid fa-cloud-arrow-up text-teal-600"></i>
                            <span>选择本地图片或视频上传：</span>
                        </span>
                        <span class="text-[10px] text-slate-400 font-normal">图片/视频数量不限，可多次上传</span>
                    </label>

                    <input type="file" id="albumLocalFileInput" ref="albumLocalFileInput" accept="image/*,video/*" multiple @change="handleAlbumLocalFileUpload($event)" class="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-600 file:text-white hover:file:bg-teal-700 cursor-pointer">

                    <!-- 已上传素材缩略图列表（可多次上传、逐个删除） -->
                    <div v-if="albumFiles.length > 0" class="grid grid-cols-3 gap-2 pt-1">
                        <div v-for="(item, idx) in albumFiles" :key="item.key" class="relative group aspect-square rounded-lg overflow-hidden border border-teal-200 bg-slate-100">
                            <!-- 图片缩略图 -->
                            <img v-if="item.type === 'image'" :src="item.preview" class="w-full h-full object-cover" alt="">
                            <!-- 视频缩略图 -->
                            <video v-else :src="item.preview" class="w-full h-full object-cover" muted preload="metadata"></video>
                            <span v-if="item.type === 'video'" class="absolute inset-0 flex items-center justify-center bg-black/25 text-white text-lg pointer-events-none">
                                <i class="fa-solid fa-play"></i>
                            </span>
                            <!-- 上传中遮罩 -->
                            <div v-if="item.status === 'uploading'" class="absolute inset-0 bg-black/40 flex items-center justify-center text-white">
                                <i class="fa-solid fa-circle-notch fa-spin text-lg"></i>
                            </div>
                            <!-- 删除按钮 -->
                            <button type="button" @click="removeAlbumFile(idx)" title="移除" class="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 hover:bg-red-600 text-white text-[10px] flex items-center justify-center">
                                <i class="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                    <p v-if="albumFiles.length > 0" class="text-[10px] text-slate-500">已选 {{ albumFiles.length }} 个素材（{{ albumImageCount() }} 张图片 / {{ albumFiles.length - albumImageCount() }} 个视频）</p>
                </div>

                <div>
                    <label class="block font-medium text-slate-700 mb-1">网络链接 (可选替代方案)</label>
                    <input type="text" id="albumMediaUrlInput" ref="albumMediaUrlInput" placeholder="https://..." class="w-full px-3 py-2 border border-slate-300 rounded-lg">
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" :disabled="albumSaving" @click="closeModal('addAlbumMediaModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg text-xs font-medium text-slate-600">{{ editingAlbumId ? '取消编辑' : '取消' }}</button>
                <button v-if="!editingAlbumId" type="submit" :disabled="albumSaving" class="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5">
                    <i v-if="albumSaving" class="fa-solid fa-circle-notch fa-spin"></i>
                    <span>{{ albumSaving ? '保存中...' : '存入成长相册' }}</span>
                </button>
                <button v-else type="submit" :disabled="albumSaving" class="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5">
                    <i v-if="albumSaving" class="fa-solid fa-circle-notch fa-spin"></i>
                    <span>{{ albumSaving ? '更新中...' : '更新成长相册' }}</span>
                </button>
            </div>
            </form>

            <!-- 保存加载遮罩 -->
            <div v-if="albumSaving" class="absolute inset-0 bg-white/70 backdrop-blur-xs rounded-2xl flex flex-col items-center justify-center gap-3 z-10">
                <i class="fa-solid fa-cloud-arrow-up text-teal-600 text-3xl fa-bounce"></i>
                <p class="text-sm font-semibold text-teal-800">{{ editingAlbumId ? '正在更新成长相册，请稍候...' : '正在存入成长相册，请稍候...' }}</p>
            </div>
        </div>
    </div>

    <!-- 导入素材到相册：仅选择新图片/视频追加到指定相册，标题只读，不读取已有素材 -->
    <div id="importAlbumMediaModal" ref="importAlbumMediaModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4" @click.self="closeModal('importAlbumMediaModal')">
        <div class="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
            <button @click="closeModal('importAlbumMediaModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-file-import"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">导入素材到相册</h3>
                    <p class="text-[11px] text-slate-500">新上传的图片/视频将追加到该相册已有素材后面。</p>
                </div>
            </div>

            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">素材标题/简介 *</label>
                    <input type="text" id="importAlbumTitleInput" ref="importAlbumTitleInput" readonly class="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 font-semibold cursor-not-allowed">
                </div>

                <!-- 本地图片/视频上传（仅本次新增，不读取已有素材） -->
                <div class="bg-sky-50/60 border-2 border-dashed border-sky-200 p-3.5 rounded-xl space-y-2">
                    <label class="block font-bold text-sky-900 text-xs flex items-center justify-between">
                        <span class="flex items-center gap-1.5">
                            <i class="fa-solid fa-cloud-arrow-up text-sky-600"></i>
                            <span>选择本地图片或视频上传：</span>
                        </span>
                        <span class="text-[10px] text-slate-400 font-normal">图片/视频数量不限，可多次上传</span>
                    </label>

                    <input type="file" id="importAlbumFileInput" ref="importAlbumFileInput" accept="image/*,video/*" multiple @change="handleImportAlbumFileUpload($event)" class="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-sky-600 file:text-white hover:file:bg-sky-700 cursor-pointer">

                    <!-- 本次新增素材缩略图（可逐个删除） -->
                    <div v-if="importAlbumFiles.length > 0" class="grid grid-cols-3 gap-2 pt-1">
                        <div v-for="(item, idx) in importAlbumFiles" :key="item.key" class="relative group aspect-square rounded-lg overflow-hidden border border-sky-200 bg-slate-100">
                            <img v-if="item.type === 'image'" :src="item.preview" class="w-full h-full object-cover" alt="">
                            <video v-else :src="item.preview" class="w-full h-full object-cover" muted preload="metadata"></video>
                            <span v-if="item.type === 'video'" class="absolute inset-0 flex items-center justify-center bg-black/25 text-white text-lg pointer-events-none">
                                <i class="fa-solid fa-play"></i>
                            </span>
                            <div v-if="item.status === 'uploading'" class="absolute inset-0 bg-black/40 flex items-center justify-center text-white">
                                <i class="fa-solid fa-circle-notch fa-spin text-lg"></i>
                            </div>
                            <button type="button" @click="removeImportAlbumFile(idx)" title="移除" class="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/55 hover:bg-red-600 text-white text-[10px] flex items-center justify-center">
                                <i class="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                    <p v-if="importAlbumFiles.length > 0" class="text-[10px] text-slate-500">本次新增 {{ importAlbumFiles.length }} 个素材（{{ importImageCount() }} 张图片 / {{ importAlbumFiles.length - importImageCount() }} 个视频）</p>
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" :disabled="albumSaving" @click="closeModal('importAlbumMediaModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button type="button" :disabled="albumSaving" @click="confirmImportAlbumMedia()" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5">
                    <i v-if="albumSaving" class="fa-solid fa-circle-notch fa-spin"></i>
                    <i v-else class="fa-solid fa-file-import"></i>
                    <span>{{ albumSaving ? '导入中...' : '导入素材' }}</span>
                </button>
            </div>

            <!-- 导入加载遮罩 -->
            <div v-if="albumSaving" class="absolute inset-0 bg-white/70 backdrop-blur-xs rounded-2xl flex flex-col items-center justify-center gap-3 z-10">
                <i class="fa-solid fa-file-import text-sky-600 text-3xl fa-bounce"></i>
                <p class="text-sm font-semibold text-sky-800">正在导入素材到相册，请稍候...</p>
            </div>
        </div>
    </div>

    <!-- 删除相册确认对话框：提示删除相册后其下所有图片/视频素材都会一并删除 -->
    <div id="confirmDeleteAlbumModal" ref="confirmDeleteAlbumModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4" @click.self="closeModal('confirmDeleteAlbumModal')">
        <div class="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4 relative">
            <div class="flex items-center space-x-3">
                <span class="w-11 h-11 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xl shrink-0">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">确认删除该相册？</h3>
                    <p class="text-[11px] text-slate-500">此操作不可恢复</p>
                </div>
            </div>
            <div class="bg-rose-50 border border-rose-200 rounded-xl p-3 text-xs text-rose-700 leading-relaxed">
                删除相册「<strong>{{ pendingDeleteAlbumTitle }}</strong>」后，其中的
                <strong>所有图片和视频素材都会被永久删除</strong>，且无法恢复。确定要继续吗？
            </div>
            <div class="flex justify-end space-x-2 pt-1 border-t border-slate-100">
                <button type="button" @click="closeModal('confirmDeleteAlbumModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button type="button" @click="confirmDeleteAlbum()" class="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5">
                    <i class="fa-solid fa-trash-can"></i>
                    <span>确认删除</span>
                </button>
            </div>
        </div>
    </div>

    <!-- 心情微语/相册多图查看器：全屏沉浸查看，支持上一张/下一张切换 -->
    <div id="moodMediaModal" ref="moodMediaModal" class="fixed inset-0 bg-black/95 backdrop-blur-md z-[60] hidden" @click.self="closeModal('moodMediaModal')">
        <!-- 顶部栏：关闭按钮 + 计数 -->
        <div class="absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 py-3 z-20 bg-gradient-to-b from-black/70 to-transparent">
            <span v-if="moodViewer.images.length > 1" class="text-white/90 text-xs font-mono bg-white/10 border border-white/15 px-3 py-1 rounded-full">
                {{ moodViewer.index + 1 }} / {{ moodViewer.images.length }}
            </span>
            <span v-else class="text-white/40 text-xs font-mono"></span>
            <button @click="closeModal('moodMediaModal')" class="w-10 h-10 rounded-full bg-white/10 hover:bg-rose-500 text-white flex items-center justify-center transition text-lg" title="关闭">
                <i class="fa-solid fa-xmark"></i>
            </button>
        </div>

        <!-- 全屏图片展示区（支持触摸左右滑动切换、双指/双击缩放） -->
        <div class="absolute inset-0 flex items-center justify-center px-14 sm:px-20 overflow-hidden" @click.self="closeModal('moodMediaModal')">
            <img :src="moodViewer.images[moodViewer.index] ? moodViewer.images[moodViewer.index].url : ''" alt="Mood media preview"
                class="max-w-full max-h-full object-contain select-none touch-none transition-transform duration-150 ease-out"
                :style="{ transform: `translate(${moodViewer.tx}px, ${moodViewer.ty}px) scale(${moodViewer.scale})` }"
                draggable="false"
                @touchstart="onViewerTouchStart"
                @touchmove.prevent="onViewerTouchMove"
                @touchend="onViewerTouchEnd"
                @dblclick="onViewerImageDblClick">

            <!-- 上一张 / 下一张（仅多图时显示） -->
            <template v-if="moodViewer.images.length > 1">
                <button @click="prevMoodMedia" class="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition text-lg" title="上一张">
                    <i class="fa-solid fa-chevron-left"></i>
                </button>
                <button @click="nextMoodMedia" class="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition text-lg" title="下一张">
                    <i class="fa-solid fa-chevron-right"></i>
                </button>
            </template>
        </div>

        <!-- 底部信息条（相册/心情多图场景：标签/标题/拍摄时间 + 删除此张图片） -->
        <div v-if="moodViewer.meta" class="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/85 to-transparent px-4 sm:px-6 pt-8 pb-4">
            <div class="w-full flex items-center justify-between gap-3 text-white text-xs">
                <div class="space-y-0.5 min-w-0 text-left">
                    <div class="flex items-center space-x-2">
                        <span class="bg-teal-500/25 text-teal-200 border border-teal-400/30 px-2 py-0.5 rounded text-[10px] font-mono shrink-0">{{ moodViewer.meta.tag }}</span>
                        <h4 class="font-bold text-sm text-slate-100 truncate">{{ moodViewer.meta.title }}</h4>
                    </div>
                    <span v-if="moodViewer.meta.date" class="text-[11px] text-slate-400 font-mono">{{ moodViewer.meta.date }}</span>
                </div>
                <button v-if="moodViewer.meta.deletable" @click="deleteViewerMedia" class="shrink-0 text-rose-300 hover:text-white text-xs px-3 py-2 bg-rose-500/25 hover:bg-rose-500/50 border border-rose-400/30 rounded-lg transition flex items-center gap-1.5 ml-auto">
                    <i class="fa-solid fa-trash-can"></i>
                    <span>删除此张图片</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Modal 5: Site & Profile Settings Modal -->
    <div id="siteSettingsModal" ref="siteSettingsModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4" @click.self="closeModal('siteSettingsModal')">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 relative">
            <button @click="closeModal('siteSettingsModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-sliders"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">个人信息与网站设置</h3>
                    <p class="text-[11px] text-slate-500">自定义奶爸名称、宝宝名称、网站标题与头像。</p>
                </div>
            </div>

            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">网站标题 *</label>
                    <input type="text" id="settingsSiteTitleInput" ref="settingsSiteTitleInput" placeholder="如：王秉宣的成长纪事" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500 font-bold">
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">奶爸昵称 *</label>
                        <input type="text" id="settingsDadNameInput" ref="settingsDadNameInput" placeholder="如：独爱秉宣的奶爸" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                    </div>
                    <div>
                        <label class="block font-medium text-slate-700 mb-1">宝宝姓名 *</label>
                        <input type="text" id="settingsBabyNameInput" ref="settingsBabyNameInput" placeholder="如：王秉宣" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                    </div>
                </div>

                <div>
                    <label class="block font-medium text-slate-700 mb-1">奶爸状态标签</label>
                    <input type="text" id="settingsDadStatusInput" ref="settingsDadStatusInput" placeholder="如：奶爸备养中 / 宣宣快乐成长中" class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500">
                </div>

                <!-- Custom Avatar Upload -->
                <div class="bg-slate-50 border border-slate-200 p-3 rounded-xl space-y-2">
                    <label class="block font-bold text-slate-700 text-xs">🖼️ 奶爸头像设置：</label>
                    <div class="flex items-center space-x-3">
                        <div id="settingsAvatarPreview" ref="settingsAvatarPreview" class="w-12 h-12 rounded-2xl bg-sky-500 text-white flex items-center justify-center font-bold text-lg overflow-hidden border border-slate-200">
                            <i class="fa-solid fa-baby"></i>
                        </div>
                        <div class="flex-1 space-y-1">
                            <label class="cursor-pointer bg-white border border-slate-300 hover:bg-sky-50 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-medium transition inline-block">
                                <i class="fa-solid fa-upload mr-1 text-sky-600"></i> 上传自定义头像
                                <input type="file" accept="image/*" @change="handleAvatarUpload($event)" class="hidden">
                            </label>
                            <button @click="resetAvatarToDefault()" type="button" class="text-[11px] text-slate-400 hover:text-rose-500 underline block">恢复默认图标</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button @click="closeModal('siteSettingsModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button @click="saveSiteSettings()" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-xs">保存修改</button>
            </div>
        </div>
    </div>

    <!-- Modal 6: Login Modal -->
    <div id="loginModal" ref="loginModal" class="fixed inset-0 bg-black/50 backdrop-blur-xs z-50 flex items-center justify-center hidden p-4">
        <div class="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 relative">
            <button @click="closeModal('loginModal')" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div class="flex items-center space-x-2 border-b border-slate-100 pb-3">
                <span class="w-8 h-8 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center text-sm font-bold">
                    <i class="fa-solid fa-user-lock"></i>
                </span>
                <div>
                    <h3 class="text-base font-bold text-slate-800">登录编辑中心</h3>
                    <p class="text-[11px] text-slate-500">验证身份后方可对宝宝成长记录进行编辑。</p>
                </div>
            </div>

            <!-- Privacy & Authorship Warning -->
            <div class="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
                <i class="fa-solid fa-shield-halved text-amber-600 mt-0.5"></i>
                <div class="text-[11px] text-amber-900 leading-relaxed space-y-1">
                    <p class="font-bold">⚠️ 仅允许本人（宝宝直系监护人）操作</p>
                    <p>本系统记录包含王秉宣小朋友的影像、成长数据及奶爸私人感悟，属于家庭成员隐私资产。为保护宝宝肖像权与家庭数据安全，<strong>非本人请勿尝试登录或编辑</strong>，所有编辑操作都应获得奶爸本人授权确认。</p>
                </div>
            </div>

            <!-- Login Form -->
            <form id="loginForm" ref="loginForm" @submit.prevent="handleLogin">
            <div class="space-y-3 text-xs">
                <div>
                    <label class="block font-medium text-slate-700 mb-1">账号 *</label>
                    <input
                        v-model="loginAccount"
                        type="text"
                        required
                        placeholder="请输入账号..."
                        class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500"
                    >
                </div>
                <div>
                    <label class="block font-medium text-slate-700 mb-1">密码 *</label>
                    <input
                        v-model="loginPassword"
                        type="password"
                        required
                        placeholder="请输入密码..."
                        class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-sky-500"
                    >
                </div>
            </div>

            <div class="flex justify-end space-x-2 pt-2 border-t border-slate-100">
                <button type="button" @click="closeModal('loginModal')" class="px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-600">取消</button>
                <button type="submit" class="px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-xs font-semibold shadow-xs">
                    <i class="fa-solid fa-right-to-bracket mr-1.5"></i>登录
                </button>
            </div>
            </form>
        </div>
    </div>
  </div>
</template>

<script>
/* global Chart */
import './assets/scripts/chart.js'
import './assets/scripts/tailwind.js'
import './assets/scripts/tailwind.config.js'
import * as indexjs from './assets/libs/index.js'
import { setToken, isTokenValid } from './assets/libs/storage.js'
import { upload_image, resolveMediaUrl, getDictValuesByField } from './assets/libs/utils.js'
// wangEditor 富文本编辑器（日记正文）——使用维护版 wangEditor-next，已修复 5.1.23 中文输入法
// "Cannot resolve a DOM point from Slate point" 的 composition 链路 bug
import { Editor, Toolbar } from '@wangeditor-next/editor-for-vue'
import '@wangeditor-next/editor/dist/css/style.css'

// Global Application State Object
let weightChart = null;
let heightChart = null;
// wangEditor 编辑器实例（模块级保存，避免放入 data 被 Vue 深度响应式代理破坏 Slate 内部结构，
// 否则中文输入时会报 "Cannot resolve a DOM point from Slate point"）
let diaryEditorRaw = null;
// 编辑器内容（模块级非响应式，避免 Vue 响应式干扰 Slate 内部 DOM；保存时直接 getHtml 取值）
let diaryEditorHtml = '';
// 当前详情弹窗正在展示的日记 id（供“编辑”按钮定位数据）
let detailDiaryId = null;

export default {
    name: 'App',
    components: { Editor, Toolbar }, // wangEditor 富文本编辑器组件
    data() {
        return {
            userInfo: {},
            siteTitle: "王秉宣的成长纪事",
            dadName: "独爱秉宣的奶爸",
            babyName: "王秉宣",
            dadStatus: "奶爸备养中",
            
            avatarUrl: null,
            avatarPath: null, // 头像相对路径（用于存库）
            isRecording: false, // 是否正在录音
            mediaRecorder: null, // MediaRecorder 实例
            recordStartTs: 0, // 录音开始时间戳
            dueDate: "2027-06-15",
            embryoType: "D5",
            transferDate: "2026-09-27",
            currentDiaryCategory: "ALL",
            currentFoodMonth: 1,
            // wangEditor 富文本编辑器（日记正文）
            // 实例保存在模块级变量 diaryEditorRaw（非响应式），此处仅用 ready 标记触发模板更新
            diaryEditorReady: false, // 编辑器是否已创建
            diaryEditorVisible: false, // 是否渲染编辑器（弹窗打开后置 true，避免在隐藏容器中创建）
            diaryToolbarConfig: {},
            editingDiaryId: null, // 正在编辑的日记 id（null=新建模式；有值=更新模式）
            diaryEditorConfig: {
                placeholder: '记录秉宣今天的成长瞬间、护理总结或心里话...',
                MENU_CONF: {
                    uploadImage: {
                        // 默认仅 2MB，手机照片普遍超限会被直接拒绝（表现为无法上传/预览），放宽到 10MB
                        maxFileSize: 10 * 1024 * 1024,
                        allowedFileTypes: ['image/*'],
                        // 复用项目统一上传接口（含鉴权），成功后插入图片
                        customUpload: async (file, insertFn) => {
                            try {
                                const data = await upload_image(file);
                                const raw = String((data && (data.url || data.fileName || data.filePath)) || '');
                                if (!raw) throw new Error('上传失败：服务器未返回文件地址');
                                // 后端返回的 url 常是带 host 的完整地址（host 可能是内网/不可达），
                                // 先剥成相对路径再拼本站点 BASE_URL，与附件上传 handleLocalFileUpload 口径一致
                                const rel = raw.replace(/^https?:\/\/[^/]+/i, '');
                                insertFn(resolveMediaUrl(rel), file.name, '');
                            } catch (err) {
                                this.showToast((err && err.message) || '图片上传失败', 'error');
                            }
                        }
                    },
                    uploadVideo: {
                        // 视频默认 10MB，放宽到 100MB；后端上传接口支持 mp4
                        maxFileSize: 100 * 1024 * 1024,
                        allowedFileTypes: ['video/*'],
                        // 未配置时编辑器会请求不存在的默认地址，导致视频无法上传
                        customUpload: async (file, insertFn) => {
                            try {
                                const data = await upload_image(file);
                                const raw = String((data && (data.url || data.fileName || data.filePath)) || '');
                                if (!raw) throw new Error('上传失败：服务器未返回文件地址');
                                // 同图片：先剥域名再拼本站点 BASE_URL，避免后端返回不可达 host
                                const rel = raw.replace(/^https?:\/\/[^/]+/i, '');
                                insertFn(resolveMediaUrl(rel));
                            } catch (err) {
                                this.showToast((err && err.message) || '视频上传失败', 'error');
                            }
                        }
                    }
                }
            },
            uploadedLocalFile: null,
            orgtreeList: [], // 多级字典（组织树）
            dailyList: [], // 日常护理记录列表
            diaryList: [], // 时光成长日记记录列表
            albumList: [], // 时光成长相册记录列表
            moodList: [], // 心情分类字典列表
            currentMoodCategory: 'ALL', // 心情微语当前分类筛选（'ALL' 或 moodList 字典 id）
            pregnancyList: [], // 预产期推算算法记录列表
            maxSize: 10000000, // 全部显示的最大数
            latestMonth: 0, // 最新月龄，用于显示在页面上
            // p50 WHO数据 用于charts图表显示
            p50months : [0, 1, 2, 3, 4, 5, 6, 9, 12, 18, 24, 36],
            p50Weight : [3.3, 4.5, 5.6, 6.4, 7.0, 7.5, 7.9, 8.9, 9.6, 10.9, 12.2, 14.3], 
            p50Height : [49.9, 54.7, 58.4, 61.4, 63.9, 65.9, 67.6, 72.0, 75.7, 82.3, 87.8, 96.1],

            // Mood Posts state
            editingMoodId: null,  // 正在编辑的心情微语 id（null 表示新建）
            moodFiles: [],        // 心情微语已选/已上传配图视频：{key,type,preview,status,url?,name}
            moodSaving: false,    // 心情微语发布加载态
            moodFileSeq: 0,       // 心情配图缩略图项唯一序号
            // 心情多图查看器：点击动态配图弹出，images 为当前动态的图片列表，index 为当前张
            // meta 为可选的素材信息（相册多图场景：标签/标题/日期/删除），心情动态场景为 null
            moodViewer: { images: [], index: 0, meta: null, scale: 1, tx: 0, ty: 0 },
            viewerTouch: null,   // 触摸手势临时状态（非持久）：起始点/双指距离/缩放/平移等
            // 心情录音状态（与"新建时光成长日记"录音同款逻辑）
            moodIsRecording: false,
            moodMediaRecorder: null,
            moodRecordStartTs: 0,

            // Login Modal state
            loginAccount: "",
            loginPassword: "",
            isLoggedIn: false,

            moodPosts: [],

            // Growth Album state
            currentAlbumType: "ALL",
            currentAlbumTag: "ALL",
            albumFiles: [],        // 相册弹窗已选/已上传素材列表：{key,type,preview,status,url?,name}
            albumSaving: false,    // 相册素材保存加载态
            editingAlbumId: null,  // 相册编辑模式：当前编辑的素材 id（null 为新建）
            albumFileSeq: 0,       // 缩略图项唯一序号
            importAlbumFiles: [],  // 导入素材弹窗本次新增素材：{key,type,preview,status,url?,name}
            importingAlbumId: null,// 导入素材目标相册 id
            importFileSeq: 0,      // 导入缩略图项唯一序号
            pendingDeleteAlbumId: null,    // 待确认删除的相册 id
            pendingDeleteAlbumTitle: '',   // 待确认删除的相册标题（确认框展示用）

            albumMediaList: [],

            // Fetal checkup history records
            fetalRecords: [],

            // Daily tracker quick logs
            trackerLogs: [],

            // Diaries DB
            diaries: [],

            // 4 Months Solid Food Plan Database (120 days = 40 3-day blocks)
            foodStatus: [], // 食物状态
            foodPlanMonths: {
                1: [
                    {dayRange: "第 1-3 天", food: "高铁强化米粉 (单一冲调)", texture: "稀糊状 (1勺米粉+4勺温水)", note: "连续观察3天皮疹与腹泻，每日1次，上午10点喂。" },
                    {dayRange: "第 4-6 天", food: "高铁米粉 + 细腻土豆泥", texture: "蒸熟压烂取泥，混合米粉", note: "观察3天，补充优质碳水与钾元素。" },
                    {dayRange: "第 7-9 天", food: "高铁米粉 + 细腻南瓜泥", texture: "蒸熟去皮压泥，温和易吸收", note: "天然微甜易接受，促进胃肠蠕动。" },
                    {dayRange: "第 10-12 天", food: "高铁米粉 + 胡萝卜蒸泥", texture: "去皮切片蒸透捣泥", note: "富含胡萝卜素，保护视力与肠道黏膜。" },
                    {dayRange: "第 13-15 天", food: "高铁米粉 + 焯水菠菜泥", texture: "水焯去除草酸后研磨成细泥", note: "补充植物铁质与丰富叶酸。" },
                    {dayRange: "第 16-18 天", food: "高铁米粉 + 高铁猪肉泥/猪肝泥", texture: "煮透研磨细腻肉泥", note: "极度重要！补充6月龄后自身消耗掉的血红素铁。" },
                    {dayRange: "第 19-21 天", food: "高铁米粉 + 甜小油菜泥", texture: "取嫩叶焯水打成泥", note: "补充膳食纤维，保持大便通畅。" },
                    {dayRange: "第 22-24 天", food: "高铁米粉 + 苹果刮泥/蒸苹果泥", texture: "鲜苹果刮细泥或蒸泥", note: "酸甜口感适度刺激味觉萌发。" },
                    {dayRange: "第 25-27 天", food: "高铁米粉 + 铁棍山药泥", texture: "去皮蒸熟压细泥", note: "健脾养胃，质地顺滑好吞咽。" },
                    {dayRange: "第 28-30 天", food: "高铁米粉 + 红薯/紫薯蒸泥", texture: "蒸软去皮捣极细泥", note: "补充微量元素，预防婴儿便秘。" }
                ],
                2: [
                    {dayRange: "第 31-33 天", food: "牛肉胡萝卜复合泥", texture: "细牛肉与胡萝卜同蒸捣泥", note: "每日1-2次，每次大半碗，高密度补铁。" },
                    {dayRange: "第 34-36 天", food: "深海鳕鱼山药泥", texture: "去刺清蒸捣碎", note: "富含DHA与蛋白质，严格观察海鲜过敏。" },
                    {dayRange: "第 37-39 天", food: "熟蛋黄泥 (1/4个起)", texture: "水煮蛋剥取1/4研碎混温水", note: "蛋白易过敏，8个月前仅吃熟蛋黄。" },
                    {dayRange: "第 40-42 天", food: "熟蛋黄泥 (1/2个)", texture: "蛋黄研碎混合米粉", note: "逐步增加蛋黄比例，补充卵磷脂。" },
                    {dayRange: "第 43-45 天", food: "鸡肉菠菜糊", texture: "去皮鸡胸肉蒸熟+菠菜泥", note: "丰富优质蛋白与多种维生素搭配。" },
                    {dayRange: "第 46-48 天", food: "鲜虾番茄泥", texture: "去虾线清蒸打泥+番茄蒸泥", note: "鲜香开胃，促进铁与钙吸收。" },
                    {dayRange: "第 49-51 天", food: "豆腐南瓜细腻稠泥", texture: "植物嫩豆腐+南瓜同蒸", note: "优质植物蛋白，质地软嫩。" },
                    {dayRange: "第 52-54 天", food: "猪肉冬瓜碎米糊", texture: "质地稍增厚，带微小软颗粒", note: "引导舌头前后推挤小颗粒。" },
                    {dayRange: "第 55-57 天", food: "熟蛋黄泥 (1个完整蛋黄)", texture: "整颗蛋黄加水或米汤研糊", note: "完全解锁整颗蛋黄摄入。" },
                    {dayRange: "第 58-60 天", food: "三鲜复合泥 (牛肉+胡萝卜+西蓝花)", texture: "蒸熟软碎块复合泥", note: "第2个月辅食顺利过渡！" }
                ],
                3: [
                    {dayRange: "第 61-63 天", food: "碎碎小烂粥 + 猪肉碎末", texture: "稠厚小颗粒烂粥", note: "锻炼牙龈咬合与舌头上下抵压能力。" },
                    {dayRange: "第 64-66 天", food: "软小面条 + 鸡肉南瓜汤", texture: "碎切小段软面条，无盐清汤", note: "主食多样化，口感丰富。" },
                    {dayRange: "第 67-69 天", food: "熟蒸胡萝卜条/西蓝花朵 (手指食物)", texture: "软熟长条状 (约5cm)", note: "引导宝宝用手抓取自主进食，锻炼手眼协调。" },
                    {dayRange: "第 70-72 天", food: "鳕鱼碎末小烂粥 + 碎青菜", texture: "稠烂粥配细碎鱼肉粒", note: "培养独立进食兴趣。" },
                    {dayRange: "第 73-75 天", food: "软蒸苹果块 / 香蕉段", texture: "软熟小块，一捏即软", note: "水果手指食物尝试，捏取吞咽。" },
                    {dayRange: "第 76-78 天", food: "牛肉菠菜烂碎面", texture: "小段短面条粒", note: "咀嚼肌发力锻炼。" },
                    {dayRange: "第 79-81 天", food: "软豆腐土豆饼", texture: "煎/蒸软软的小薄饼", note: "手抓咀嚼练习，香软易消化。" },
                    {dayRange: "第 82-84 天", food: "虾仁南瓜碎碎粥", texture: "虾仁切碎泥与南瓜小粒烂粥", note: "补充矿物质与天然清甜味。" },
                    {dayRange: "第 85-87 天", food: "猪肝西蓝花微颗粒糊", texture: "小颗粒状蒸碎糊", note: "持续补铁防贫血。" },
                    {dayRange: "第 88-90 天", food: "蛋黄红薯小饭团", texture: "软烂小饭团手抓食", note: "第3个月顺利完成！" }
                ],
                4: [
                    {dayRange: "第 91-93 天", food: "软小肉丸 (牛肉/猪肉)", texture: "蒸熟软嫩易捏碎小丸子", note: "用手抓咬肉丸，感受肉质纤维。" },
                    {dayRange: "第 94-96 天", food: "蔬菜蛋黄蒸糕", texture: "胡萝卜/西蓝花+蛋黄蒸糕切块", note: "松软手抓食物，营养均衡。" },
                    {dayRange: "第 97-99 天", food: "鲜虾豆腐手指条", texture: "鲜虾豆腐小指条", note: "高钙与蛋白组合，自如抓咬。" },
                    {dayRange: "第 100-102 天", food: "无盐小馄饨 (鲜肉蔬菜馅)", texture: "小皮软馄饨，剪成半粒", note: "感受面皮与馅料混合口感。" },
                    {dayRange: "第 103-105 天", food: "番茄牛肉软烂米饭", texture: "从稀粥过渡到软烂小烩饭", note: "逐步建立一日三餐主食习惯。" },
                    {dayRange: "第 106-108 天", food: "鳕鱼蔬菜小软饼", texture: "软嫩薄饼，自由抓嚼", note: "练习门牙咬断与槽齿咀嚼。" },
                    {dayRange: "第 109-111 天", food: "鸡肉紫菜小汤面", texture: "碎面条+碎紫菜+去皮鸡肉末", note: "丰富风味感知。" },
                    {dayRange: "第 112-114 天", food: "杂粮南瓜软饭 + 蒸肉饼", texture: "软烂饭配小碎肉饼", note: "接近家庭共餐食物形态。" },
                    {dayRange: "第 115-117 天", food: "菠菜猪肝蒸蛋黄软糕", texture: "营养密度极高的小软糕", note: "全方位补充铁、锌、维A。" },
                    {dayRange: "第 118-120 天", food: "多样化家庭共享软餐 (虾仁蒸蛋+软米饭+焯青菜)", texture: "家庭软餐同桌进食", note: "🎉 恭喜！顺利完成4个月（120天）辅食解锁全打卡！" }
                ]
            },

            growthHistory: [] // WHO 生长曲线记录
        };
    },
    watch: {
        // 网站标题变化时同步浏览器标签页 title
        siteTitle(val) {
            document.title = val;
        }
    },
    methods: {
    // 将服务器相对路径解析为完整媒体地址（模板中需要使用，故暴露为方法）
    resolveMediaUrl(url) {
        return resolveMediaUrl(url);
    },
    // Modal Helpers
    openModal(modalId) {
        const el = this.$refs[modalId];
        if (el) el.classList.remove('hidden');
        if (modalId === 'loginModal') {
            this.loginAccount = "";
            this.loginPassword = "";
        }
        // 日记弹窗打开后再创建富文本编辑器（在 display:none 中创建会导致中文输入报错）
        if (modalId === 'quickAddModal') {
            // 注意：打开/关闭弹窗都不重置表单，已填内容保留；仅在“保存成功”后由 resetDiaryForm 清空
            // 从“写日记”入口打开时强制为新建模式（编辑模式由 editDiaryFromDetail 设置）
            this.editingDiaryId = null;
            const dateInput = this.$refs.diaryDateInput;
            if (dateInput && !dateInput.value) {
                // 首次打开（日期为空）时默认记录日期为今天
                const now = new Date();
                const y = now.getFullYear();
                const m = String(now.getMonth() + 1).padStart(2, '0');
                const d = String(now.getDate()).padStart(2, '0');
                dateInput.value = `${y}-${m}-${d}`;
            }
            // 等弹窗 DOM 完全展示后再创建编辑器；编辑器实例一旦创建便常驻（关闭仅隐藏弹窗），内容得以保留
            this.$nextTick(() => {
                this.diaryEditorVisible = true;
            });
        }
    },
    /**
     * 带登录守卫的弹窗打开：
     * - 已登录：正常弹出指定弹窗
     * - 未登录：弹出 loginModal 登录框
     */
    openModalGuarded(modalId) {
        if (!this.requireLogin()) return;
        this.openModal(modalId);
    },
    closeModal(modalId) {
        const el = this.$refs[modalId];
        if (el) el.classList.add('hidden');
        if (modalId === 'loginModal') {
            this.loginAccount = "";
            this.loginPassword = "";
        }
        // 关闭日记弹窗时若仍在录音，停止并释放麦克风
        if (modalId === 'quickAddModal' && this.isRecording) {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.isRecording = false;
        }
        // 关闭日记弹窗：
        // - 编辑模式（editingDiaryId 有值，从详情“编辑”进入）：关闭即清空回填内容并退出编辑态，
        //   避免再次打开“新建”时残留上次编辑的数据；
        // - 新建模式：不销毁编辑器、不重置，已填内容保留；编辑器实例常驻，弹窗仅 hidden 隐藏。
        if (modalId === 'quickAddModal' && this.editingDiaryId) {
            this.resetDiaryForm();
        }
        // 关闭相册素材弹窗时，退出编辑态并清空表单与缩略图，避免下次打开残留
        if (modalId === 'addAlbumMediaModal') {
            this.editingAlbumId = null;
            this.resetAlbumForm();
        }
        // 关闭导入素材弹窗时，退出导入态并清空本次新增素材
        if (modalId === 'importAlbumMediaModal') {
            this.cancelImportAlbum();
        }
        // 关闭删除相册确认框时，清空待删除状态（取消/点遮罩）
        if (modalId === 'confirmDeleteAlbumModal') {
            this.pendingDeleteAlbumId = null;
            this.pendingDeleteAlbumTitle = '';
        }
    },

    /**
     * 销毁 wangEditor 实例并移除编辑器渲染（v-if 置 false）
     */
    destroyDiaryEditor() {
        if (diaryEditorRaw) {
            diaryEditorRaw.destroy();
            diaryEditorRaw = null;
        }
        this.diaryEditorReady = false;
        this.diaryEditorVisible = false;
    },

    /**
     * 重置日记新增弹窗表单（标题/分类/日期/正文/附件/语音等），
     * 防止再次打开时残留上次内容；记录日期默认回填今天。
     */
    resetDiaryForm() {
        const titleInput = this.$refs.diaryTitleInput;
        const categoryInput = this.$refs.diaryCategoryInput;
        const dateInput = this.$refs.diaryDateInput;
        const mediaInput = this.$refs.diaryMediaInput;
        const audioInput = this.$refs.diaryAudioInput;
        const localFileInput = this.$refs.diaryLocalFileInput;
        const badge = this.$refs.localFilePreviewBadge;

        if (titleInput) titleInput.value = '';
        if (categoryInput) categoryInput.selectedIndex = 0;
        if (mediaInput) mediaInput.value = '';
        if (audioInput) audioInput.value = '';
        if (localFileInput) localFileInput.value = '';
        if (badge) badge.classList.add('hidden');
        // 记录日期默认今天
        if (dateInput) {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const d = String(now.getDate()).padStart(2, '0');
            dateInput.value = `${y}-${m}-${d}`;
        }
        // 附件与录音数据
        this.uploadedLocalFile = null;
        if (this.isRecording) this.isRecording = false;
        // 退出编辑模式，回到“新建”状态
        this.editingDiaryId = null;
        // 富文本内容：编辑器实例常驻，需显式清空（仅保存成功后调用此重置）
        diaryEditorHtml = '';
        if (diaryEditorRaw && typeof diaryEditorRaw.clear === 'function') {
            try { diaryEditorRaw.clear(); } catch (e) { /* 忽略清空异常 */ }
        }
    },

    /**
     * 轻提示：顶部浮层，自动消失，不阻塞操作。
     * type: 'success' | 'error' | 'info'；duration: 毫秒，默认 1500
     */
    showToast(msg, type = 'info', duration = 1500) {
        const colors = {
            success: 'bg-emerald-600',
            error: 'bg-rose-600',
            info: 'bg-slate-800'
        };
        const icons = {
            success: 'fa-circle-check',
            error: 'fa-circle-exclamation',
            info: 'fa-circle-info'
        };

        let wrap = document.getElementById('appToastWrap');
        if (!wrap) {
            wrap = document.createElement('div');
            wrap.id = 'appToastWrap';
            wrap.className = 'fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2 pointer-events-none';
            document.body.appendChild(wrap);
        }

        const el = document.createElement('div');
        el.className = `${colors[type] || colors.info} text-white text-xs font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 max-w-[90vw] opacity-0 transition-all duration-300`;
        el.innerHTML = `<i class="fa-solid ${icons[type] || icons.info}"></i><span></span>`;
        el.querySelector('span').innerText = msg;
        wrap.appendChild(el);

        requestAnimationFrame(() => {
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
        });

        setTimeout(() => {
            el.style.opacity = '0';
            el.style.transform = 'translateY(-8px)';
            setTimeout(() => {
                el.remove();
                if (wrap && !wrap.children.length) wrap.remove();
            }, 300);
        }, duration);
    },

    /**
     * 登录按钮逻辑。
     * 目前仅做前端占位：验证账号密码是否非空，并设置 isLoggedIn 标志位。
     * 后续接入真实后端鉴权时，替换为 fetch/axios 调用即可。
     */
    handleLogin() {
        const username = (this.loginAccount || "").trim();
        const password = (this.loginPassword || "").trim();
        if (!username || !password) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.loginForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }
        indexjs.login({ 'username': username, 'password': password }).then((res) => {
            // handleResponse 返回的是后端 JSON body，业务成功标识为 code === 200
            if (res && res.code === 200) {
                // 保存 token，供 request.js 后续请求注入 Authorization
                const token = res.token || (res.data && res.data.token);
                if (token) setToken(token);
                this.isLoggedIn = true;
                this.showToast(`登录成功！欢迎回来，${username}`, 'success');
                this.closeModal('loginModal');
                // 登录状态变化后，重渲染依赖登录态展示的列表（编辑/删除按钮等）
                this.$nextTick(() => {
                    if (typeof this.renderMoodPosts === 'function') this.renderMoodPosts();
                    if (typeof this.renderAlbumGallery === 'function') this.renderAlbumGallery();
                });
            } else {
                this.showToast((res && res.msg) ? res.msg : '登录失败，请检查账号密码', 'error');
            }
        }).catch((err) => {
            this.showToast((err && err.msg) ? err.msg : '登录失败，请检查账号密码', 'error');
        })
    },

    /**
     * 是否已登录（同步判定函数，便于模板或逻辑中直接调用）
     */
    isLogin() {
        // 检查 localStorage 中的 token：存在且未过期才算已登录
        return isTokenValid();
    },

    /**
     * 登录守卫：所有「保存」「弹出编辑对话框」的按钮事件执行前先调用此函数。
     * - 已登录：返回 true，调用方继续执行原逻辑
     * - 未登录：弹出 loginModal，并返回 false，调用方应 return 中止原逻辑
     * 用法示例：
     *   if (!this.requireLogin()) return;
     *   // ...后续编辑/保存逻辑
     */
    requireLogin() {
        if (this.isLogin()) return true;
        this.openModal('loginModal');
        return false;
    },

    closeMobileNav() {
        const nav = this.$refs.navMenuItems;
        if (nav) {
            nav.classList.add('hidden');
            nav.classList.remove('flex');
        }
    },

    toggleMobileNav() {
        const nav = this.$refs.navMenuItems;
        if (nav) {
            nav.classList.toggle('hidden');
            nav.classList.toggle('flex');
        }
    },

    toggleTransferCalc() {
        if (!this.requireLogin()) return;
        const drawer = this.$refs.transferCalcDrawer;
        if (drawer) drawer.classList.toggle('hidden');
    },

    onDatePickerClick(e) {
        if (e.target.showPicker) {
            try { e.target.showPicker(); } catch (err) { /* noop */ }
        }
    },

    calculateAndSaveDueDate() {
        const calcTypeSelect = this.$refs.calcTypeSelect;
        const calcDateInput = this.$refs.calcDateInput;

        const calcType = calcTypeSelect ? calcTypeSelect.value : '';
        const calcDateVal = calcDateInput ? calcDateInput.value : '';

        if (!calcDateVal) {
            this.showToast("请选择日期！", 'error');
            return;
        }
        // calcType 为字典 id，从 pregnancyList 中取对应 dictName，解析"+ N天"得到推算天数
        const dict = this.pregnancyList.find(d => Number(d.id) === Number(calcType));
        const match = dict ? dict.dictName.match(/\+\s*(\d+)\s*天/) : null;
        const addDays = match ? Number(match[1]) : 280;

        const baseDate = new Date(calcDateVal);
        baseDate.setDate(baseDate.getDate() + addDays);
        const yyyy = baseDate.getFullYear();
        const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
        const dd = String(baseDate.getDate()).padStart(2, '0');

        this.dueDate = `${yyyy}-${mm}-${dd}`;
        this.transferDate = calcDateVal;
        this.embryoType = calcType;

        // 保存移植日期与推算依据到用户资料（数据库）
        this.userInfo = {
            ...this.userInfo,
            yizhidate: calcDateVal,          // 选择移植 / 月经日期
            yuchanqituisuan: calcType        // 选择推算依据（字典 id）
        };
        indexjs.updateUserInfo(this.userInfo).then(res => {
            if (res && res.code === 200) {
                this.showToast('预产期设置已保存', 'success');
            } else {
                this.showToast((res && res.msg) || '保存失败！', 'error');
            }
        }).catch(err => {
            this.showToast((err && err.message) || '保存失败！', 'error');
        });

        this.updateCountdown();
        this.toggleTransferCalc();
    },

    updateCountdown() {
        const dueDate = new Date(this.dueDate);
        const today = new Date();
        dueDate.setHours(0, 0, 0, 0);
        const todayZero = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        const diffTime = dueDate - todayZero;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const countdownEl = this.$refs.countdownDaysLarge;
        if (countdownEl) countdownEl.innerText = diffDays > 0 ? diffDays : 0;

        const dueDateTag = this.$refs.dueDateTag;
        if (dueDateTag) dueDateTag.innerText = this.dueDate;

        const totalDaysInPregnancy = 280;
        const elapsedDays = Math.max(0, totalDaysInPregnancy - diffDays);
        const currentWeeks = Math.floor(elapsedDays / 7);
        const currentDays = elapsedDays % 7;

        const gestationalWeekEl = this.$refs.gestationalWeek;
        if (gestationalWeekEl) {
            let trimester = "孕早期";
            if (currentWeeks >= 13 && currentWeeks < 28) trimester = "孕中期";
            if (currentWeeks >= 28) trimester = "孕晚期";
            gestationalWeekEl.innerText = `${trimester} · ${currentWeeks}周+${currentDays}天`;
        }

        const progressBar = this.$refs.gestationalProgressBar;
        if (progressBar) {
            const pct = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDaysInPregnancy) * 100)));
            progressBar.style.width = `${pct}%`;
        }
    },

    switchTab(tabId) {
        this.closeMobileNav();
        document.querySelectorAll('.tab-content').forEach(el => el.classList.add('hidden'));
        document.querySelectorAll('.nav-item').forEach(btn => btn.classList.remove('active'));

        const targetContent = this.$refs[`tab-${tabId}`];
        if (targetContent) targetContent.classList.remove('hidden');

        const targetBtn = this.$refs[`nav-btn-${tabId}`];
        if (targetBtn) targetBtn.classList.add('active');

        const titles = {
            'tracker': { title: '⚡ 日常护理与生长速记中心', subtitle: '一键快速登记宝宝喂养、睡眠、换尿布、生长指标、皮肤护理、用药执行、疫苗与萌牙情况。' },
            'fetal': { title: '🤰 孕期胎儿数据与产检记录', subtitle: '记录孕期各周B超测量数据、胎心率及医生指导意见。' },
            'diary': { title: '📖 时光成长日记 (按年月归档)', subtitle: '按年月分组整理的微型卡片日记，支持本地照片视频与语音备忘。' },
            'album': { title: `🖼️ ${this.userInfo.chlidName || this.babyName}时光成长相册`, subtitle: `多维度沉淀${this.userInfo.chlidName || this.babyName}的照片与视频影像资料，支持按日期与标签智能筛选和全屏沉浸浏览。` },
            'guide': { title: '🥗 0-3岁科学喂养&4个月辅食计划', subtitle: '涵盖 0-1 岁奶量安排、4个月辅食菜谱计划表与 1-3 岁幼儿膳食指南。' },
            'attachment': { title: '❤️ 安全型依恋重塑与打卡', subtitle: '回避型奶爸的依恋重塑心理机制与 0-6 岁日常依恋互动打卡。' },
            'motor': { title: '🏃 0-3岁大运动与神经肌肉发育', subtitle: '头尾法则、肌肉骨骼发展与髓鞘化运动神经学机制。' },
            'brain': { title: '🧠 0-3岁大脑脑科学 (海马体/前额叶)', subtitle: '解密婴儿失忆症、恐惧警报与发脾气的脑神经发育图谱。' },
            'emotion': { title: '❤️ 0-10岁儿童情绪理解深度演进', subtitle: '结合发展心理学：为什么孩子要到10岁才能彻底理解矛盾/复杂情绪？' },
            'charts': { title: '📈 WHO 0-3岁生长曲线与评估', subtitle: '录入身高体重，对比世界卫生组织百分位标准。' },
            'mood': { title: '💭 奶爸心情微语与点滴随感', subtitle: '记录奶爸陪伴孩子成长中的喜怒哀乐、育儿感悟与时光片段，支持本地配图与短视频。' }
        };

        if (titles[tabId]) {
            this.$refs.currentTabTitle.innerHTML = titles[tabId].title;
            this.$refs.currentTabSubtitle.innerText = titles[tabId].subtitle;
        }

        if (tabId === 'charts') {
            // 加载WHO生长曲线与数据表记录 load_data 点击加载数据
            this.initGrowthCharts();
            this.updateGrowthSummary();
            this.renderGrowthRecords();
        } else if (tabId === 'fetal') {
            // 加载孕期胎儿数据与产检记录 load_data 点击加载数据
            this.renderFetalRecords();
        } else if (tabId === 'guide') {
            // 加载0-3岁科学喂养&4个月辅食计划 load_data 点击加载数据
            this.renderFoodPlan();
        } else if (tabId === 'diary') {
            // 加载时光成长日记 load_data 点击加载数据
            this.renderGroupedDiaries();
        } else if (tabId === 'album') {
            this.loadAlbumMediaList();
        } else if (tabId === 'mood') {
            // 加载心情微语 load_data 点击加载数据
            this.renderMoodPosts();
        }
    },

    // Settings Profile Logic
    handleAvatarUpload(e) {
        if (!this.requireLogin()) return;
        const file = e.target.files[0];
        if (!file) return;
        this.updateAvatarPreview(`上传中: ${file.name}`);
        // 上传到后端，只保存相对路径
        upload_image(file).then(data => {
            const rawUrl = data.url || data.fileName || data.filePath || '';
            this.avatarPath = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
            this.avatarUrl = resolveMediaUrl(this.avatarPath);
            this.updateAvatarPreview(this.avatarUrl);
            this.showToast('头像上传成功', 'success');
        }).catch(err => {
            this.updateAvatarPreview(null);
            if (e.target) e.target.value = '';
            this.showToast((err && err.message) || '头像上传失败', 'error');
        });
    },

    resetAvatarToDefault() {
        this.avatarUrl = null;
        this.avatarPath = null;
        this.updateAvatarPreview(null);
    },

    updateAvatarPreview(url) {
        const preview = this.$refs.settingsAvatarPreview;
        if (!preview) return;
        if (url && /^https?:/.test(url)) {
            preview.innerHTML = `<img src="${url}" class="w-full h-full object-cover">`;
        } else if (url) {
            preview.innerHTML = `<span class="text-[10px] text-slate-500">${url}</span>`;
        } else {
            preview.innerHTML = `<i class="fa-solid fa-baby text-lg"></i>`;
        }
    },

    saveSiteSettings() {
        if (!this.requireLogin()) return;
        const siteTitleInput = this.$refs.settingsSiteTitleInput;
        const dadNameInput = this.$refs.settingsDadNameInput;
        const babyNameInput = this.$refs.settingsBabyNameInput;
        const dadStatusInput = this.$refs.settingsDadStatusInput;

        if (siteTitleInput && siteTitleInput.value.trim()) this.siteTitle = siteTitleInput.value.trim();
        if (dadNameInput && dadNameInput.value.trim()) this.dadName = dadNameInput.value.trim();
        if (babyNameInput && babyNameInput.value.trim()) this.babyName = babyNameInput.value.trim();
        if (dadStatusInput && dadStatusInput.value.trim()) this.dadStatus = dadStatusInput.value.trim();

        // 将表单值合并进 userInfo 后保存到数据库
        this.userInfo = {
            ...this.userInfo,
            webTitle: this.siteTitle,
            fatherName: this.dadName,
            chlidName: this.babyName,
            fatherTag: this.dadStatus,
            fatherImage: this.avatarPath || ''
        };

        indexjs.updateUserInfo(this.userInfo).then(res => {
            if (res && res.code === 200) {
                this.updateProfileUI();
                this.closeModal('siteSettingsModal');
                this.showToast('设置保存成功', 'success');
            } else {
                this.showToast((res && res.msg) || '保存失败！', 'error');
            }
        }).catch(err => {
            this.showToast((err && err.message) || '保存失败！', 'error');
        });
    },

    updateProfileUI() {
        const titleEl = this.$refs.sidebarSiteTitle;
        if (titleEl) titleEl.innerText = this.siteTitle;

        const statusEl = this.$refs.sidebarDadStatus;
        if (statusEl) statusEl.innerText = this.dadStatus;

        const avatarIcon = this.$refs.sidebarAvatarIcon;
        const avatarImg = this.$refs.sidebarAvatarImg;
        if (avatarIcon && avatarImg) {
            if (this.avatarUrl) {
                avatarIcon.classList.add('hidden');
                avatarImg.src = this.avatarUrl;
                avatarImg.classList.remove('hidden');
            } else {
                avatarImg.classList.add('hidden');
                avatarIcon.classList.remove('hidden');
            }
        }

        // moodInputAvatar 头像由模板响应式绑定 avatarUrl / dadName 展示，无需手动更新

        // 同步设置弹窗表单与头像预览为当前值
        const siteTitleInput = this.$refs.settingsSiteTitleInput;
        if (siteTitleInput) siteTitleInput.value = this.siteTitle;
        const dadNameInput = this.$refs.settingsDadNameInput;
        if (dadNameInput) dadNameInput.value = this.dadName;
        const babyNameInput = this.$refs.settingsBabyNameInput;
        if (babyNameInput) babyNameInput.value = this.babyName;
        const dadStatusInput = this.$refs.settingsDadStatusInput;
        if (dadStatusInput) dadStatusInput.value = this.dadStatus;
        this.updateAvatarPreview(this.avatarUrl);

        this.renderMoodPosts();
    },

    quickLogFeed(paramMl) {
        if (!this.requireLogin()) return;
        const inputEl = this.$refs.customFeedMl;
        const ml = paramMl || (inputEl ? inputEl.value : '') || 90;
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "喂奶速记",
            content1: `配方奶`,
            content2: `${ml} ml`,
            content3: "喂奶速记",
            content4: "喂奶",
            createTime: timeStr,
            content5: "🍼"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (inputEl) inputEl.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    quickLogSleep(paramAction) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.sleepTypeSelect;
        const action = paramAction || (selectEl ? selectEl.value : '开始入睡');
        const noteInput = this.$refs.sleepNoteInput;
        const note = noteInput && noteInput.value.trim() ? noteInput.value.trim() : "";
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "睡眠记录",
            content1: action,
            content2: note,
            content3: "睡眠记录",
            content4: "睡眠",
            createTime: timeStr,
            content5: "😴"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (noteInput) noteInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    quickLogDiaper(paramType) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.diaperTypeSelect;
        const type = paramType || (selectEl ? selectEl.value : '仅尿湿');
        const diaperInput = this.$refs.diaperNoteInput;
        const note = diaperInput && diaperInput.value.trim() ? diaperInput.value.trim() : "";
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "换尿布",
            content1: type,
            content2: note,
            content3: "换尿布",
            content4: "换尿布",
            createTime: timeStr,
            content5: "💩"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (diaperInput) diaperInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    logGrowth() {
        if (!this.requireLogin()) return;
        const wInput = this.$refs.weightInput;
        const hInput = this.$refs.heightInput;
        const hcInput = this.$refs.headInput;

        const w = wInput ? wInput.value : '';
        const h = hInput ? hInput.value : '';
        const hc = hcInput ? hcInput.value : '';
        if (!w && !h && !hc) return;

        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "生长数据测定",
            content1: `${w||'-'}kg`,
            content2: `体重:${w||'-'}kg, 身高:${h||'-'}cm, 头围:${hc||'-'}cm`,
            content3: "生长数据测定",
            content4: "生长",
            createTime: timeStr,
            content5: "📏"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (wInput) wInput.value = '';
                if (hInput) hInput.value = '';
                if (hcInput) hcInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 皮肤护理与洗澡记录
    quickLogSkin(paramAction) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.skinTypeSelect;
        const action = paramAction || (selectEl ? selectEl.value : '洗澡与抚触');
        const skinInput = this.$refs.skinNoteInput;
        const note = skinInput && skinInput.value.trim() ? skinInput.value.trim() : "";
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "皮肤护理与洗澡",
            content1: action,
            content2: note,
            content3: "皮肤护理与洗澡",
            content4: "护肤",
            createTime: timeStr,
            content5: "🧴"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (skinInput) skinInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    quickLogMed(paramMedName) {
        if (!this.requireLogin()) return;
        const selectEl = this.$refs.medSelect;
        const customInput = this.$refs.customMedInput;
        let medName = paramMedName;
        if (!medName) {
            if (customInput && customInput.value.trim()) {
                medName = customInput.value.trim();
            } else if (selectEl) {
                medName = selectEl.value;
            }
        }
        if (!medName || medName === '其他') return;
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "用药打卡",
            content1: selectEl ? selectEl.value : (paramMedName || ''),
            content2: customInput ? customInput.value.trim() : '',
            content3: "用药打卡",
            content4: "用药",
            createTime: timeStr,
            content5: "💊"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (customInput) customInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 疫苗接种记录
    quickLogVaccine() {
        if (!this.requireLogin()) return;
        const vacSelect = this.$refs.vaccineSelect;
        const vac = vacSelect ? vacSelect.value : '注射：乙肝疫苗第一针';
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "疫苗接种记录",
            content1: vac,
            content2: vac,
            content3: "疫苗接种",
            content4: "疫苗",
            createTime: timeStr,
            content5: "💉"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 出牙记录
    quickLogTeeth() {
        if (!this.requireLogin()) return;
        const toothSelect = this.$refs.teethPositionSelect;
        const tooth = toothSelect ? toothSelect.value : '下门牙左/右';
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "出牙记录",
            content1: tooth,
            content2: tooth,
            content3: "出牙记录",
            content4: "萌牙",
            createTime: timeStr,
            content5: "🦷"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 自定义速记事项
    quickLogCustomEvent() {
        if (!this.requireLogin()) return;
        const nameInput = this.$refs.customEventName;
        const noteInput = this.$refs.customEventNote;
        const name = nameInput ? nameInput.value.trim() : '';
        const note = noteInput ? noteInput.value.trim() : '';
        if (!name) return;

        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "自定义速记事项",
            content1: name,
            content2: note,
            content3: "自定义速记事项",
            content4: "其他",
            createTime: timeStr,
            content5: "📝"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                if (nameInput) nameInput.value = '';
                if (noteInput) noteInput.value = '';
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    // 安全依恋亲子互动打卡
    quickLogAttachment() {
        if (!this.requireLogin()) return;
        const attachSelect = this.$refs.attachmentInteractionSelect;
        const act = attachSelect ? attachSelect.value : '完成15分钟皮肤接触';
        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;
        let item = {
            typestr: "安全依恋亲子互动打卡",
            content1: act,
            content2: act,
            content3: "依恋重塑",
            content4: "依恋",
            createTime: timeStr,
            content5: "❤️"
        }
        indexjs.addDaily(item).then(res => {
            if (res && res.code === 200) {
                // 提交成功后，重新从数据库拉取全部数据刷新展示
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('添加日常护理记录失败:', err);
        })
    },

    filterTrackerLogs(category) {
        this.renderTrackerLogs(category);
    },

    renderTrackerLogs(filterCategory = 'ALL') {
        const container = this.$refs.trackerHistoryList;
        if (!container) return;

        // 点击“全部显示”：重新向后端查询，用 maxSize 一次性把全部数据拉回来
        if (filterCategory === 'ALL') {
            indexjs.getDailyList({ 'pageNum': 1, 'pageSize': this.maxSize })
                .then(res => {
                    if (res && res.code === 200) {
                        this.trackerLogs = res.rows || (res.data && res.data.rows) || res.data || [];
                        this.drawTrackerLogs(this.trackerLogs);
                    }
                })
                .catch(err => {
                    console.error('查询日常护理记录失败:', err);
                });
            return;
        }

        // 指定分类：基于已加载的数据在前端过滤
        const filtered = this.trackerLogs.filter(item => item.tag === filterCategory || item.category === filterCategory);
        this.drawTrackerLogs(filtered);
    },

    // 纯渲染：把传入的记录列表绘制到打卡容器
    drawTrackerLogs(list) {
        const container = this.$refs.trackerHistoryList;
        if (!container) return;

        if (!list || list.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-6">暂无此类打卡记录。</div>`;
            return;
        }

        container.innerHTML = list.map(log => `
            <div class="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-center text-xs hover:bg-sky-50/50 transition">
                <div class="flex items-center space-x-3">
                    <span class="text-lg">${log.content5 || '📝'}</span>
                    <div>
                        <div class="flex items-center space-x-2">
                            <span class="font-bold text-slate-800">${log.typestr}</span>
                            <span class="bg-sky-100 text-sky-800 text-[10px] px-2 py-0.2 rounded font-semibold">${log.content1}</span>
                        </div>
                        <p class="text-slate-500 text-[11px] mt-0.5 txtleft">${log.content2}</p>
                    </div>
                </div>
                <div class="flex items-center space-x-2">
                    <span class="text-slate-400 font-mono text-[10px]">${log.createTime}</span>
                    <button data-action="deleteTrackerLog" data-id="${log.id}" class="text-slate-400 hover:text-rose-600 text-xs transition" title="删除此条记录">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>
            </div>
        `).join('');
    },

    // 从数据库刷新日常护理打卡列表：默认只显示前 10 条
    refreshTrackerLogs() {
        const container = this.$refs.trackerHistoryList;
        if (!container) return Promise.resolve();
        return indexjs.getDailyList({ 'pageNum': 1, 'pageSize': 10 })
            .then(res => {
                if (res && res.code === 200) {
                    this.trackerLogs = res.rows || (res.data && res.data.rows) || res.data || [];
                    this.drawTrackerLogs(this.trackerLogs);
                }
            })
            .catch(err => {
                console.error('查询日常护理记录失败:', err);
            });
    },

    deleteTrackerLog(id) {
        if (!this.requireLogin()) return;
        if (!id) return;
        indexjs.deleteDaily(id).then(res => {
            if (res && res.code === 200) {
                this.refreshTrackerLogs();
            }
        }).catch(err => {
            console.error('删除日常护理记录失败:', err);
        })
    },

    handleMoodFileUpload(e) {
        if (!this.requireLogin()) return;
        const picked = Array.from(e.target.files || []);
        if (e.target) e.target.value = '';
        if (picked.length === 0) return;

        // 图片、视频数量均不限，可多次选择追加；逐个加入缩略图列表
        picked.forEach(file => {
            const type = (file.type && file.type.startsWith('video/')) ? 'video'
                       : (file.type && file.type.startsWith('image/')) ? 'image' : 'image';
            const key = ++this.moodFileSeq;
            const item = {
                key,
                type,
                name: file.name,
                preview: URL.createObjectURL(file),
                status: 'uploading',
                url: ''
            };
            this.moodFiles.push(item);

            upload_image(file).then(data => {
                const raw = data.url || data.fileName || data.filePath || '';
                const url = String(raw).split(',').map(u => u.replace(/^https?:\/\/[^/]+/i, '')).join(',').trim();
                if (!url) throw new Error('服务器未返回文件地址');
                const target = this.moodFiles.find(f => f.key === key);
                if (target) {
                    target.url = url;
                    target.status = 'done';
                }
            }).catch(err => {
                const idx = this.moodFiles.findIndex(f => f.key === key);
                if (idx > -1) {
                    URL.revokeObjectURL(this.moodFiles[idx].preview);
                    this.moodFiles.splice(idx, 1);
                }
                this.showToast(`「${file.name}」上传失败：${(err && err.message) || '请重试'}`, 'error');
            });
        });
    },

    // 移除某个已选/已上传的心情配图（缩略图右上角删除）
    removeMoodFile(idx) {
        const item = this.moodFiles[idx];
        if (item && item.preview && item.preview.startsWith('blob:')) URL.revokeObjectURL(item.preview);
        this.moodFiles.splice(idx, 1);
    },

    // 清空心情微语已选配图（发布成功后调用）
    resetMoodFiles() {
        this.moodFiles.forEach(f => {
            if (f.preview && f.preview.startsWith('blob:')) URL.revokeObjectURL(f.preview);
        });
        this.moodFiles = [];
    },

    // 点击"编辑"：把该篇心情内容回填到发表框，进入更新模式
    editMoodPost(id) {
        if (!this.requireLogin()) return;
        const post = this.moodPosts.find(p => Number(p.id) === Number(id));
        if (!post) return;
        this.editingMoodId = Number(id);
        // 回填文字
        const textarea = this.$refs.moodTextInput;
        if (textarea) textarea.value = post.text || '';
        // 回填心情分类
        const tagSelect = this.$refs.moodTagSelect;
        if (tagSelect && post.typeid !== undefined && post.typeid !== null && post.typeid !== '') {
            tagSelect.value = post.typeid;
        }
        // 回填已有配图/视频/录音（服务器地址，可预览/删除；删除后不参与更新）
        this.resetMoodFiles();
        (post.rawFiles || []).forEach(u => {
            const full = resolveMediaUrl(u);
            const path = full.split(/[?#]/)[0];
            const base = path.substring(path.lastIndexOf('/') + 1);
            let type = 'image';
            if (/^voice_/i.test(base) || /\.(mp3|m4a|wav|aac|ogg|oga|webma)$/i.test(path)) type = 'audio';
            else if (/\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(path)) type = 'video';
            this.moodFiles.push({
                key: ++this.moodFileSeq,
                type,
                name: base,
                preview: full,
                status: 'done',
                url: u  // 相对路径，提交用
            });
        });
        // 滚动到发表框
        const form = this.$refs.moodPostForm;
        if (form && form.scrollIntoView) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },

    // 取消更新：重置表单，回到新建模式
    cancelEditMood() {
        this.editingMoodId = null;
        const textarea = this.$refs.moodTextInput;
        if (textarea) textarea.value = '';
        const fileInput = this.$refs.moodFileInput;
        if (fileInput) fileInput.value = '';
        this.resetMoodFiles();
    },

    // 点击"更新微语"：调用 updateComment 更新到数据库
    updateMoodPost() {
        if (!this.editingMoodId) return;
        const textarea = this.$refs.moodTextInput;
        const text = textarea ? textarea.value.trim() : '';
        if (!text) {
            this.showToast('请填写心情内容', 'info');
            return;
        }
        if (this.moodFiles.some(f => f.status === 'uploading')) {
            this.showToast('图片/视频/录音还在上传中，请稍候再更新', 'info');
            return;
        }
        const tagSelect = this.$refs.moodTagSelect;
        const tag = tagSelect ? tagSelect.value : '';
        const mediaUrls = this.moodFiles.filter(f => f.status === 'done' && f.url).map(f => f.url);

        const item = {
            id: this.editingMoodId,
            content: text,
            typeid: tag,
            files: mediaUrls.filter(Boolean).join(',')
        };

        this.moodSaving = true;
        indexjs.updateComment(item).then(res => {
            if (res && res.code === 200) {
                this.showToast('心情微语已更新！', 'success');
                this.cancelEditMood();
                this.renderMoodPosts();
            } else {
                this.showToast((res && res.msg) || '更新失败！', 'error');
            }
        }).catch(err => {
            console.error('更新心情微语失败:', err);
            this.showToast((err && err.message) || '更新失败，请稍后重试', 'error');
        }).finally(() => {
            this.moodSaving = false;
        });
    },

    /**
     * 心情微语录音：与"新建时光成长日记"的 simulateVoiceRecord 同款逻辑
     * 点击开始录音，再次点击停止并上传，成功后挂到本条心情的附件里
     */
    toggleMoodRecord() {
        if (!this.requireLogin()) return;
        // 已在录音中：停止录音
        if (this.moodIsRecording) {
            if (this.moodMediaRecorder && this.moodMediaRecorder.state !== 'inactive') {
                this.moodMediaRecorder.stop();
            }
            this.moodIsRecording = false;
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
            this.showToast('当前浏览器不支持录音功能', 'error');
            return;
        }
        // 后端上传仅支持 mp4，浏览器必须支持录制 audio/mp4，否则不录音也不上传
        const mimeType = ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2'].find(m => MediaRecorder.isTypeSupported(m)) || '';
        if (!mimeType) {
            this.showToast('当前浏览器不支持录制 MP4 格式，无法使用录音功能', 'error');
            return;
        }
        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            const chunks = [];
            const recorder = new MediaRecorder(stream, { mimeType });
            recorder.ondataavailable = e => {
                if (e.data && e.data.size > 0) chunks.push(e.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach(t => t.stop());
                const finalMime = recorder.mimeType || mimeType;
                const ext = finalMime.includes('mp4') ? 'mp4' : 'webm';
                const blob = new Blob(chunks, { type: finalMime });

                // 计算录音时长 mm:ss
                const seconds = Math.round((Date.now() - this.moodRecordStartTs) / 1000);
                const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
                const ss = String(seconds % 60).padStart(2, '0');

                // 先把录音加入附件列表（本地可预览播放、可删除），上传中标记 uploading
                const fileName = `voice_${Date.now()}.${ext}`;
                const file = new File([blob], fileName, { type: finalMime });
                const key = ++this.moodFileSeq;
                this.moodFiles.push({
                    key,
                    type: 'audio',
                    name: fileName,
                    preview: URL.createObjectURL(blob),
                    status: 'uploading',
                    url: ''
                });

                // 上传录音到后端，成功后回填相对路径用于发布
                upload_image(file).then(data => {
                    const rawUrl = data.url || data.fileName || data.filePath || '';
                    const url = String(rawUrl).split(',').map(u => u.replace(/^https?:\/\/[^/]+/i, '')).join(',').trim();
                    if (!url) throw new Error('服务器未返回文件地址');
                    const target = this.moodFiles.find(f => f.key === key);
                    if (target) {
                        target.url = url;
                        target.status = 'done';
                    }
                    this.showToast(`录音完成（${mm}:${ss}）`, 'success');
                }).catch(err => {
                    const idx = this.moodFiles.findIndex(f => f.key === key);
                    if (idx > -1) {
                        URL.revokeObjectURL(this.moodFiles[idx].preview);
                        this.moodFiles.splice(idx, 1);
                    }
                    this.showToast((err && err.message) || '录音上传失败', 'error');
                });
            };
            recorder.start();
            this.moodMediaRecorder = recorder;
            this.moodRecordStartTs = Date.now();
            this.moodIsRecording = true;
            this.showToast('录音中，再次点击"停止"结束', 'info');
        }).catch(err => {
            this.showToast('无法访问麦克风：' + ((err && err.message) || '请检查录音权限'), 'error');
        });
    },

    saveMoodPost() {
        if (!this.requireLogin()) return;
        if (this.moodSaving) return;
        const moodTextInput = this.$refs.moodTextInput;
        const moodTagSelect = this.$refs.moodTagSelect;

        const text = moodTextInput ? moodTextInput.value.trim() : '';

        if (!text) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.moodPostForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }
        if (this.moodFiles.some(f => f.status === 'uploading')) {
            this.showToast('图片/视频/录音还在上传中，请稍候再发布', 'info');
            return;
        }
        const tag = moodTagSelect ? moodTagSelect.value : '';

        const now = new Date();
        const timeStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`;

        // 汇总附件相对地址：配图/视频/录音（均已上传，逗号拼接）
        const mediaUrls = this.moodFiles.filter(f => f.status === 'done' && f.url).map(f => f.url);

        // 构造后台 Comment 实体
        const item = {
            content: text,
            typeid: tag,
            files: mediaUrls.filter(Boolean).join(','),
            createTime: timeStr,
            // likes: 0,
        };

        this.moodSaving = true;
        indexjs.addComment(item).then(res => {
            if (res && res.code === 200) {
                this.showToast("心情微语已发布！", 'success');
                this.editingMoodId = null;
                this.resetMoodFiles();
                if (moodTextInput) moodTextInput.value = '';
                const fileInput = this.$refs.moodFileInput;
                if (fileInput) fileInput.value = '';
                this.renderMoodPosts();
            } else {
                this.showToast(res.msg || "发布失败！", 'error');
            }
        }).catch(err => {
            console.error('添加心情微语失败:', err);
            this.showToast((err && err.message) || '发布失败，请稍后重试', 'error');
        }).finally(() => {
            this.moodSaving = false;
        });
    },

    toggleLikeMood(id) {
        const post = this.moodPosts.find(p => p.id === id);
        if (!post) return;

        // 本地 +1，只更新对应按钮的显示，不刷新整个列表
        post.likes = (post.likes || 0) + 1;
        const container = this.$refs.moodFeedContainer;
        const btn = container ? container.querySelector(`[data-action="toggleLikeMood"][data-id="${id}"]`) : null;
        if (btn) {
            const icon = btn.querySelector('i');
            const span = btn.querySelector('span');
            if (icon) icon.classList.add('text-rose-500');
            if (span) span.innerText = `给爸爸加油 (${post.likes})`;
        }

        // 数据库正常更新
        indexjs.commentUpdatelikes({'id': id}).then(res => {
            if (!res || res.code !== 200) {
                this.showToast((res && res.msg) || '点赞失败！', 'error');
            }
        }).catch(err => {
            console.error('点赞失败:', err);
            this.showToast(err.message || '点赞失败！', 'error');
        });
    },

    deleteMoodPost(id) {
        if (!this.requireLogin()) return;
        if (!id) return;
        indexjs.deleteComment(id).then(res => {
            if (res && res.code === 200) {
                this.showToast("心情微语已删除！", 'success');
                this.renderMoodPosts();
            } else {
                this.showToast((res && res.msg) || '删除失败！', 'error');
            }
        }).catch(err => {
            console.error('删除心情微语失败:', err);
            this.showToast(err.message || '删除失败！', 'error');
        });

        this.moodPosts = this.moodPosts.filter(p => p.id !== id);
        this.renderMoodPosts();
    },

    /**
     * 打开心情多图查看器
     * @param {Number} postId - 心动动态 id
     * @param {Number} index - 点击的图片在该动态图片列表中的下标
     */
    openMoodMediaViewer(postId, index = 0) {
        const post = this.moodPosts.find(p => p.id === postId);
        const images = (post && post.medias) ? post.medias.filter(m => m.type === 'image') : [];
        this.openMediaViewer(images, index, {
            deletable: this.isLoggedIn,  // 登录后才可在查看器中删除当前图片
            source: 'mood',
            id: postId
        });
    },

    /**
     * 通用多图查看器（心情动态与相册多图素材共用）
     * @param {Array} images - 图片列表 [{ url, type }]
     * @param {Number} index - 初始展示的下标
     * @param {Object|null} meta - 可选素材信息 { tag, title, date, deletable, source, id }，传入时查看器底部显示信息条
     */
    openMediaViewer(images, index = 0, meta = null) {
        images = images || [];
        if (images.length === 0) return;
        this.moodViewer.images = images;
        this.moodViewer.index = Math.max(0, Math.min(index, images.length - 1));
        this.moodViewer.meta = meta;
        this.resetViewerZoom();
        this.viewerTouch = null;
        this.openModal('moodMediaModal');
    },

    // 重置查看器图片的缩放与位移
    resetViewerZoom() {
        this.moodViewer.scale = 1;
        this.moodViewer.tx = 0;
        this.moodViewer.ty = 0;
    },

    // ---- 触摸手势：单指左右滑动切换、双指捏合缩放、双击缩放（移动端） ----
    onViewerTouchStart(e) {
        if (e.touches.length === 1) {
            const t = e.touches[0];
            const now = Date.now();
            // 双击检测：与上次点击间隔 < 300ms 且位移极小 → 缩放切换
            const dt = now - (this.viewerTouch ? (this.viewerTouch.tapTime || 0) : 0);
            this.viewerTouch = {
                mode: 'pan',
                startX: t.clientX,
                startY: t.clientY,
                lastX: t.clientX,
                lastY: t.clientY,
                startTx: this.moodViewer.tx,
                startTy: this.moodViewer.ty,
                startScale: this.moodViewer.scale,
                moved: false,
                tapTime: now
            };
            if (dt < 300) {
                // 双击：放大到 2.5 倍或还原
                if (this.moodViewer.scale > 1) {
                    this.resetViewerZoom();
                } else {
                    this.moodViewer.scale = 2.5;
                }
                this.viewerTouch.mode = 'doubletap';
            }
        } else if (e.touches.length === 2) {
            const dist = this.touchDist(e.touches);
            this.viewerTouch = {
                mode: 'pinch',
                startDist: dist,
                startScale: this.moodViewer.scale
            };
        }
    },

    onViewerTouchMove(e) {
        const g = this.viewerTouch;
        if (!g) return;
        if (e.touches.length === 2 && g.mode === 'pinch') {
            const dist = this.touchDist(e.touches);
            if (g.startDist > 0) {
                let scale = g.startScale * (dist / g.startDist);
                this.moodViewer.scale = Math.min(4, Math.max(1, scale));
                if (this.moodViewer.scale <= 1) this.resetViewerZoom();
            }
        } else if (e.touches.length === 1 && g.mode === 'pan') {
            const t = e.touches[0];
            const dx = t.clientX - g.startX;
            const dy = t.clientY - g.startY;
            if (Math.abs(dx) > 8 || Math.abs(dy) > 8) g.moved = true;
            if (this.moodViewer.scale > 1) {
                // 已放大：单指拖动查看图片细节
                this.moodViewer.tx = g.startTx + (t.clientX - g.lastX);
                this.moodViewer.ty = g.startTy + (t.clientY - g.lastY);
            }
        }
    },

    onViewerTouchEnd(e) {
        const g = this.viewerTouch;
        if (!g) return;
        // 单指滑动结束且未放大、且有明显水平位移 → 切换图片
        if (g.mode === 'pan' && this.moodViewer.scale <= 1 && g.moved) {
            const dx = (e.changedTouches[0] ? e.changedTouches[0].clientX : g.lastX) - g.startX;
            const dy = (e.changedTouches[0] ? e.changedTouches[0].clientY : g.lastY) - g.startY;
            if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.2) {
                if (dx < 0) this.nextMoodMedia();
                else this.prevMoodMedia();
            }
        }
        // 手势结束后若缩回到 1 倍，复位位移
        if (this.moodViewer.scale <= 1) {
            this.moodViewer.tx = 0;
            this.moodViewer.ty = 0;
        }
        this.viewerTouch = null;
    },

    // PC 端鼠标双击：放大到 2.5 倍或还原
    onViewerImageDblClick() {
        if (this.moodViewer.scale > 1) {
            this.resetViewerZoom();
        } else {
            this.moodViewer.scale = 2.5;
        }
    },

    // 两指之间的距离
    touchDist(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    },

    /**
     * 查看器内删除（相册多图/心情动态场景）：仅删除当前这张图片，
     * 剩余文件通过 updatePhoto / updateComment 更新回原记录，而不是删除整条记录。
     */
    deleteViewerMedia() {
        const meta = this.moodViewer.meta;
        if (!meta || !meta.deletable) return;
        if (meta.source === 'album') {
            this.deleteAlbumViewerImage(meta.id);
        } else if (meta.source === 'mood') {
            this.deleteMoodViewerImage(meta.id);
        }
    },

    /**
     * 删除相册查看器中当前查看的这张图片，并更新该相册素材
     * 说明：查看器仅展示"图片"（images 数组，与 rawFiles 中图片顺序一致），
     *       故按查看器下标定位 rawFiles 中对应的图片文件并剔除，其余图片+所有视频保留。
     */
    deleteAlbumViewerImage(id) {
        if (!this.requireLogin()) return;
        const m = this.albumMediaList.find(item => Number(item.id) === Number(id));
        if (!m) return;
        const imgIndex = this.moodViewer.index;
        // rawFiles 中所有图片文件的相对路径（顺序与 m.images / 查看器一致）
        const imageRawList = (m.rawFiles || []).filter(u => {
            const full = resolveMediaUrl(u);
            const path = full.split(/[?#]/)[0];
            return !/\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(path);
        });
        const target = imageRawList[imgIndex];
        if (target === undefined) {
            this.showToast('未找到该图片', 'error');
            return;
        }
        // 从 rawFiles 中剔除目标图片这一项（按同一字符串精确匹配）
        const remaining = (m.rawFiles || []).filter(u => u !== target);

        const item = {
            id: m.id,
            title: m.title,
            typeid: m.typeid,
            photoDate: m.date,
            mediaUrl: m.mediaUrl || '',
            files: remaining.length ? remaining.join(',') : ''
        };

        indexjs.updatePhoto(item).then(res => {
            if (res && res.code === 200) {
                this.showToast('该图片已删除', 'success');
                // 从查看器移除当前图片；无剩余图片则关闭查看器
                const imgs = this.moodViewer.images.slice();
                imgs.splice(imgIndex, 1);
                if (imgs.length === 0) {
                    this.closeModal('moodMediaModal');
                } else {
                    this.moodViewer.images = imgs;
                    this.moodViewer.index = Math.min(imgIndex, imgs.length - 1);
                }
                this.loadAlbumMediaList();
            } else {
                this.showToast((res && res.msg) || '删除失败', 'error');
            }
        }).catch(err => {
            console.error('删除相册图片失败:', err);
            this.showToast((err && err.message) || '删除失败，请稍后重试', 'error');
        });
    },

    prevMoodMedia() {
        const len = this.moodViewer.images.length;
        if (!len) return;
        this.moodViewer.index = (this.moodViewer.index - 1 + len) % len;
        this.resetViewerZoom();
    },

    /**
     * 删除心情微语查看器中当前查看的这张图片，并更新该条微语
     * 说明：查看器仅展示"图片"（顺序与 rawFiles 中图片项一致），
     *       按查看器下标定位并剔除该图片，其余图片/视频/录音与文字、分类原样保留。
     */
    deleteMoodViewerImage(id) {
        if (!this.requireLogin()) return;
        const post = this.moodPosts.find(p => Number(p.id) === Number(id));
        if (!post) return;
        const imgIndex = this.moodViewer.index;
        // rawFiles 中所有图片文件的相对路径（顺序与 medias 图片 / 查看器一致）
        const imageRawList = (post.rawFiles || []).filter(u => {
            const path = resolveMediaUrl(u).split(/[?#]/)[0];
            const base = path.substring(path.lastIndexOf('/') + 1);
            if (/^voice_/i.test(base) || /\.(mp3|m4a|wav|aac|ogg|oga|webma)$/i.test(path)) return false;
            return !/\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(path);
        });
        const target = imageRawList[imgIndex];
        if (target === undefined) {
            this.showToast('未找到该图片', 'error');
            return;
        }
        // 剔除目标图片，其余图片/视频/录音保留
        const remaining = (post.rawFiles || []).filter(u => u !== target);

        const item = {
            id: post.id,
            content: post.text,
            typeid: post.typeid,
            files: remaining.length ? remaining.join(',') : ''
        };

        indexjs.updateComment(item).then(res => {
            if (res && res.code === 200) {
                this.showToast('该图片已删除', 'success');
                // 从查看器移除当前图片；无剩余图片则关闭查看器
                const imgs = this.moodViewer.images.slice();
                imgs.splice(imgIndex, 1);
                if (imgs.length === 0) {
                    this.closeModal('moodMediaModal');
                } else {
                    this.moodViewer.images = imgs;
                    this.moodViewer.index = Math.min(imgIndex, imgs.length - 1);
                }
                this.renderMoodPosts();
            } else {
                this.showToast((res && res.msg) || '删除失败', 'error');
            }
        }).catch(err => {
            console.error('删除心情图片失败:', err);
            this.showToast((err && err.message) || '删除失败，请稍后重试', 'error');
        });
    },

    nextMoodMedia() {
        const len = this.moodViewer.images.length;
        if (!len) return;
        this.moodViewer.index = (this.moodViewer.index + 1) % len;
        this.resetViewerZoom();
    },

    // 切换心情分类筛选并重新渲染
    filterMood(cat) {
        this.currentMoodCategory = cat;
        this.renderMoodPosts();
    },

    // 统计某个心情分类（typeid）下的微语数量
    moodCountByType(typeid) {
        return this.moodPosts.filter(p => Number(p.typeid) === Number(typeid)).length;
    },

    renderMoodPosts() {
        const container = this.$refs.moodFeedContainer;
        if (!container) return;

        const doRender = () => {
            // 按当前分类筛选（'ALL' 显示全部，否则按 moodList 字典 id 匹配 typeid）
            const filtered = this.currentMoodCategory === 'ALL'
                ? this.moodPosts
                : this.moodPosts.filter(p => Number(p.typeid) === Number(this.currentMoodCategory));

            if (filtered.length === 0) {
                container.innerHTML = `<div class="text-center text-xs text-slate-400 py-10">${this.moodPosts.length === 0 ? '暂无心情微语，快发第一条心情吧！' : '该分类下暂无心情微语'}</div>`;
                return;
            }

            const dadName = this.dadName || "独爱秉宣的奶爸";

            container.innerHTML = filtered.map(p => {
                // 拆分：图片（顶部网格、可点开灯箱），视频/录音（下方另起一行）
                const images = p.medias ? p.medias.filter(m => m.type === 'image') : [];
                const others = p.medias ? p.medias.filter(m => m.type !== 'image') : [];
                return `
                <div class="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-2xs space-y-3 hover:border-amber-300 transition">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                            <div class="w-10 h-10 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 text-white font-extrabold flex items-center justify-center text-sm shadow-xs overflow-hidden">
                                ${this.avatarUrl ? `<img src="${this.avatarUrl}" class="w-full h-full object-cover">` : dadName.substring(0, 1)}
                            </div>
                            <div>
                                <div class="flex items-center space-x-2">
                                    <span class="font-bold text-xs sm:text-sm text-slate-800">${dadName}</span>
                                    <span class="bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-bold">${p.tag}</span>
                                </div>
                                <span class="text-[10px] text-slate-400 font-mono">${p.time}</span>
                            </div>
                        </div>
                        ${this.isLoggedIn ? `
                        <div class="flex items-center space-x-2">
                            <button data-action="editMoodPost" data-id="${p.id}" class="flex items-center space-x-1 text-amber-500 hover:text-white hover:bg-amber-500 border border-amber-300 px-2.5 py-1.5 rounded-lg text-xs font-medium transition" title="编辑该心情">
                                <i class="fa-solid fa-pen-to-square"></i>
                                <span>编辑</span>
                            </button>
                            <button data-action="deleteMoodPost" data-id="${p.id}" class="flex items-center space-x-1 text-rose-500 hover:text-white hover:bg-rose-500 border border-rose-300 px-2.5 py-1.5 rounded-lg text-xs font-medium transition" title="删除该心情">
                                <i class="fa-solid fa-trash-can"></i>
                                <span>删除</span>
                            </button>
                        </div>
                        ` : ''}
                    </div>

                    ${p.text ? `<p class="text-xs sm:text-sm text-slate-700 whitespace-pre-line leading-relaxed font-sans">${p.text}</p>` : ''}

                    ${images.length ? `
                        <div class="${images.length > 1 ? 'grid grid-cols-3 sm:grid-cols-6 gap-1' : ''}">
                            ${images.map((m, idx) => `
                                <div data-action="openMoodMedia" data-id="${p.id}" data-index="${idx}" class="cursor-zoom-in rounded-xl overflow-hidden border border-slate-200 ${images.length === 1 ? 'bg-slate-900 max-h-72 max-w-md' : 'bg-slate-100 aspect-square'}">
                                    <img src="${m.url}" alt="Mood media" class="w-full ${images.length === 1 ? 'max-h-72 object-cover' : 'h-full object-cover'}" loading="lazy">
                                </div>
                            `).join('')}
                        </div>
                    ` : ''}

                    ${others.length ? `
                        <div class="space-y-2 pt-1">
                            ${others.map(m => m.type === 'video' ? `
                                <div class="rounded-xl overflow-hidden bg-slate-900 border border-slate-200 max-w-md">
                                    <video controls src="${m.url}" class="max-h-72 w-full object-contain"></video>
                                </div>
                            ` : `
                                <div class="flex items-center gap-2 rounded-xl border border-purple-200 bg-purple-50 px-3 py-2 max-w-md">
                                    <i class="fa-solid fa-microphone text-purple-500 text-sm shrink-0"></i>
                                    <audio controls src="${m.url}" class="flex-1 min-w-0 h-7"></audio>
                                </div>
                            `).join('')}
                        </div>
                    ` : ''}

                    <div class="flex justify-between items-center pt-2 border-t border-slate-100 text-xs">
                        <button data-action="toggleLikeMood" data-id="${p.id}" class="text-slate-500 hover:text-rose-500 font-medium transition flex items-center space-x-1.5">
                            <i class="fa-solid fa-heart ${p.likes > 0 ? 'text-rose-500' : 'text-slate-400'}"></i>
                            <span>给爸爸加油 (${p.likes || 0})</span>
                        </button>
                        <span class="text-[10px] text-slate-400">来自 ${this.siteTitle}</span>
                    </div>
                </div>
            `; }).join('');
        };

        indexjs.getCommentList({ 'pageNum': 1, 'pageSize': this.maxSize }).then(res => {
            if (res && res.code === 200) {
                const rows = res.rows || (res.data && res.data.rows) || res.data || [];
                // 将后台 Comment 数据转换为前端 moodPosts 格式
                this.moodPosts = rows.map(r => {
                    // files 兼容多文件：多张图/视频/录音地址以英文逗号分隔存储
                    const medias = r.files ? String(r.files).split(',')
                        .map(u => u.trim()).filter(Boolean)
                        .map(u => {
                            const url = resolveMediaUrl(u);
                            // 取路径文件名（去掉 query/hash）用于类型判断
                            const path = url.split(/[?#]/)[0];
                            const base = path.substring(path.lastIndexOf('/') + 1);
                            let type = 'image';
                            if (/^voice_/i.test(base) || /\.(mp3|m4a|wav|aac|ogg|oga|webma)$/i.test(path)) {
                                type = 'audio';
                            } else if (/\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(path)) {
                                type = 'video';
                            }
                            return { url, type };
                        }) : [];
                    // typeid 存的是字典 id，从 moodList 中反查 dictName 用于展示
                    const tagName = getDictValuesByField(JSON.stringify(this.moodList.filter(m => Number(m.id) === Number(r.typeid))), 'dictName', ' ');
                    const rawFiles = r.files ? String(r.files).split(',').map(u => u.trim()).filter(Boolean) : [];
                    return {
                        id: r.id,
                        typeid: r.typeid,
                        text: r.content,
                        tag: tagName || r.typeid,
                        time: r.createTime,
                        medias: medias,
                        rawFiles: rawFiles,
                        likes: r.likes || 0
                    };
                });
            }
            doRender();
        }).catch(err => {
            console.error('查询心情微语失败:', err);
            doRender();
        });
    },

    // Fetal Records
    saveFetalCheckup(e) {
        if (!this.requireLogin()) return;
        e.preventDefault();
        const checkDate = this.$refs.fetalDate.value;
        const week = this.$refs.fetalWeek.value;
        const bpm = this.$refs.fetalHeartRate.value;
        const weight = this.$refs.fetalWeight.value;
        const bpd = this.$refs.fetalBpd.value;
        const fl = this.$refs.fetalFl.value;
        const ac = this.$refs.fetalAc.value;
        const afd = this.$refs.fetalAmniotic.value;
        const tip = this.$refs.fetalDoctorSummary.value;

        let item = {
            'checkDate':checkDate, 
            'week':week,
            'bpm':bpm,
            'weight':weight,
            'bpd':bpd,
            'fl':fl,
            'ac':ac,
            'afd':afd,
            'tip':tip,
        }
        indexjs.addPregnancy(item).then(res => {
            if (res.code === 200) {
                this.renderFetalRecords();
                this.$refs.fetalDataForm.reset();
            }
        });
    },

    deleteFetalRecord(id) {
        if (!this.requireLogin()) return;

        if(id>0){
            indexjs.deletePregnancy(id).then(res => {
                if (res.code === 200) {
                    this.renderFetalRecords();
                }
            });
        }


        this.fetalRecords.filter(r => r.id !== id);
        this.renderFetalRecords();
    },

    // 加载孕期胎儿数据与产检记录：从数据库查询后渲染
    renderFetalRecords() {
        const container = this.$refs.fetalHistoryContainer;
        if (!container) return;

        indexjs.getPregnancyList({
            'pageNum': 1,
            'pageSize': this.maxSize
        }).then(res => {
            if (res && res.code === 200) {
                this.fetalRecords = res.rows || (res.data && res.data.rows) || res.data || [];
            }
            this.drawFetalRecords();
        }).catch(err => {
            console.error('查询产检记录失败:', err);
            this.drawFetalRecords();
        });
    },

    // 纯渲染：把当前 fetalRecords 绘制到产检档案容器
    drawFetalRecords() {
        const container = this.$refs.fetalHistoryContainer;
        if (!container) return;

        if (this.fetalRecords.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-6">暂无产检数据记录。</div>`;
            return;
        }

        container.innerHTML = this.fetalRecords.map(r => `
            <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-2">
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                    <div class="flex items-center space-x-2">
                        <span class="font-extrabold text-xs sm:text-sm text-slate-800">${r.week}</span>
                        <span class="text-[10px] bg-rose-100 text-rose-800 px-2 py-0.5 rounded font-mono">${r.checkDate}</span>
                    </div>
                    <button data-action="deleteFetalRecord" data-id="${r.id}" class="text-slate-400 hover:text-rose-600 text-xs transition">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-600 font-mono bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    <div>💓 胎心率: <strong class="text-slate-800">${r.bpm || '-'} bpm</strong></div>
                    <div>⚖️ 估重: <strong class="text-slate-800">${r.weight || '-'} g</strong></div>
                    <div>📏 双顶径: <strong class="text-slate-800">${r.bpd || '-'} mm</strong></div>
                    <div>🦴 股骨长: <strong class="text-slate-800">${r.fl || '-'} mm</strong></div>
                    <div> 腹围: <strong class="text-slate-800">${r.ac || '-'} mm</strong></div>
                    <div> 羊水最大深度: <strong class="text-slate-800">${r.afd || '-'} mm</strong></div>
                </div>

                <div class="text-xs text-slate-700 leading-relaxed bg-amber-50/50 p-2.5 rounded-lg border border-amber-100">
                    <span class="font-bold text-amber-900">👨‍⚕️ 医生指导与总结：</span>
                    <p class="mt-0.5 text-slate-700">${r.tip}</p>
                </div>
            </div>
        `).join('');
    },

    // Diaries Logic
    applyWolaiTemplate(type) {
        if (!this.requireLogin()) return;
        const titleInput = this.$refs.diaryTitleInput;
        const categoryInput = this.$refs.diaryCategoryInput;

        // 把纯文本模板（含换行）转换为富文本 HTML：每行一个段落，空行留白
        const fillEditor = (text) => {
            const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const html = String(text).split('\n').map(line => {
                const l = line.trim();
                return l ? `<p>${esc(l)}</p>` : '<p><br></p>';
            }).join('');
            if (diaryEditorRaw) {
                diaryEditorRaw.setHtml(html);
                diaryEditorHtml = diaryEditorRaw.getHtml(); // 同步模块级内容，保存时取此值
            } else {
                // 编辑器尚未创建完成，待就绪后再填充
                this.$nextTick(() => {
                    if (diaryEditorRaw) {
                        diaryEditorRaw.setHtml(html);
                        diaryEditorHtml = diaryEditorRaw.getHtml();
                    }
                });
            }
        };

        if (type === 'daily') {
            if (titleInput) titleInput.value = `王秉宣育儿日常复盘 - ${new Date().toLocaleDateString()}`;
            if (categoryInput) categoryInput.value = 62; // '日常';
            fillEditor(`【今日护理核心总结】
- 喂奶次数/奶量：
- 换尿布/便便性状：
- 睡眠时长与质量：

【大运动与互动情况】
- 完成了15分钟趴卧练习与婴儿抚触。

【奶爸感悟与心得】
- 应对哭闹时保持了平静，及时给予回应。`);
        } else if (type === 'checkup') {
            if (titleInput) titleInput.value = `孕期产检B超纪录 - 孕周复盘`;
            if (categoryInput) categoryInput.value = 63; // '孕期';
            fillEditor(`【产检概况】
- 检查日期：
- 当前孕周：
- 胎儿估重/胎心率：

【医生嘱咐】
- 补钙与蛋白摄入要求：

【奶爸备产心里话】
- 听到宝宝心跳那一刻，心里充满了踏实与期待。`);
        } else if (type === 'milestone') {
            if (titleInput) titleInput.value = `🏆 第一次里程碑突破：秉宣学会了...`;
            if (categoryInput) categoryInput.value = 64; // '里程碑';
            fillEditor(`【里程碑时刻】
- 时间：
- 突破动作/技能：（如第一次翻身 / 第一次发音“打打”）

【细节与陪伴过程】
- 爸爸全程陪在身边，及时给予了积极掌声与鼓舞！`);
        } else if (type === 'voice') {
            if (titleInput) titleInput.value = `🎙️ 奶爸声音留声机 - 留给秉宣的话`;
            if (categoryInput) categoryInput.value = 66; // '语音备忘';
            fillEditor(`宣宣，这是爸爸给你留下的声音档案...
愿你健康快乐成长，爸爸永远是你的坚实后盾！`);
        }
    },

    handleLocalFileUpload(e) {
        if (!this.requireLogin()) return;
        const file = e.target.files[0];
        if (!file) return;
        let type = 'image';
        if (file.type.startsWith('video/')) type = 'video';
        if (file.type.startsWith('audio/')) type = 'audio';

        const previewBadge = this.$refs.localFilePreviewBadge;
        const fileNameText = this.$refs.localFileNameText;
        const fileTypeTag = this.$refs.localFileTypeTag;

        // 先展示选中状态，并提示上传中
        if (previewBadge && fileNameText && fileTypeTag) {
            fileNameText.innerText = `上传中: ${file.name}`;
            fileTypeTag.innerText = type === 'image' ? '照片' : (type === 'video' ? '视频' : '语音');
            previewBadge.classList.remove('hidden');
        }

        // 调用网页端上传实现，把文件传到后端
        upload_image(file).then(data => {
            // 后端返回的 data 中通常含 url / name / fileName / filePath 等字段
            // 只保存相对地址（如 /profile/upload/2026/08/31/xxx.png），IP/域名展示时统一拼接 BASE_URL
            const rawUrl = data.url || data.fileName || data.filePath || '';
            const url = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
            this.uploadedLocalFile = { url, type, name: file.name };

            // 把服务器地址回填到媒体链接输入框，saveNewDiary 提交时即可带上
            // const mediaInput = this.$refs.diaryMediaInput;
            // if (mediaInput) mediaInput.value = url;

            if (fileNameText) fileNameText.innerText = `${file.name} (已上传)`;
            this.showToast('文件上传成功', 'success');
        }).catch(err => {
            // 上传失败：清空选择，恢复提示
            if (fileNameText) fileNameText.innerText = `${file.name} (上传失败)`;
            this.uploadedLocalFile = null;
            const input = this.$refs.diaryLocalFileInput;
            if (input) input.value = '';
            this.showToast((err && err.message) || '文件上传失败', 'error');
        });
    },

    simulateVoiceRecord() {
        if (!this.requireLogin()) return;
        // 已在录音中：停止录音
        if (this.isRecording) {
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.isRecording = false;
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
            this.showToast('当前浏览器不支持录音功能', 'error');
            return;
        }
        // 后端上传仅支持 mp4，浏览器必须支持录制 audio/mp4，否则不录音也不上传
        const mimeType = ['audio/mp4', 'audio/mp4;codecs=mp4a.40.2'].find(m => MediaRecorder.isTypeSupported(m)) || '';
        if (!mimeType) {
            this.showToast('当前浏览器不支持录制 MP4 格式，无法使用录音功能', 'error');
            return;
        }
        navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
            const chunks = [];
            const recorder = new MediaRecorder(stream, { mimeType });
            recorder.ondataavailable = e => {
                if (e.data && e.data.size > 0) chunks.push(e.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach(t => t.stop());
                const finalMime = recorder.mimeType || mimeType;
                const ext = finalMime.includes('mp4') ? 'mp4' : 'webm';
                const blob = new Blob(chunks, { type: finalMime });

                // 计算并回填语音时长 mm:ss
                const seconds = Math.round((Date.now() - this.recordStartTs) / 1000);
                const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
                const ss = String(seconds % 60).padStart(2, '0');
                const audioInput = this.$refs.diaryAudioInput;
                if (audioInput) audioInput.value = `${mm}:${ss}`;

                // 上传录音到后端，只保存相对路径
                const fileName = `voice_${Date.now()}.${ext}`;
                const file = new File([blob], fileName, { type: finalMime });
                const previewBadge = this.$refs.localFilePreviewBadge;
                const fileNameText = this.$refs.localFileNameText;
                const fileTypeTag = this.$refs.localFileTypeTag;
                if (previewBadge && fileNameText && fileTypeTag) {
                    fileNameText.innerText = `上传中: ${fileName}`;
                    fileTypeTag.innerText = '语音';
                    previewBadge.classList.remove('hidden');
                }
                upload_image(file).then(data => {
                    const rawUrl = data.url || data.fileName || data.filePath || '';
                    const url = rawUrl.replace(/^https?:\/\/[^/]+/i, '');
                    this.uploadedLocalFile = { url, type: 'audio', name: fileName };
                    if (fileNameText) fileNameText.innerText = `${fileName} (已上传)`;
                    this.showToast(`录音完成（${mm}:${ss}）`, 'success');
                }).catch(err => {
                    if (fileNameText) fileNameText.innerText = `${fileName} (上传失败)`;
                    this.uploadedLocalFile = null;
                    this.showToast((err && err.message) || '录音上传失败', 'error');
                });
            };
            recorder.start();
            this.mediaRecorder = recorder;
            this.recordStartTs = Date.now();
            this.isRecording = true;
            this.showToast('录音中，再次点击"停止"结束', 'info');
        }).catch(err => {
            this.showToast('无法访问麦克风：' + ((err && err.message) || '请检查录音权限'), 'error');
        });
    },

    /**
     * wangEditor 编辑器创建完成回调：实例存模块级变量（非响应式），ready 标记触发 Toolbar 联动
     */
    onDiaryEditorCreated(editor) {
        diaryEditorRaw = editor;
        this.diaryEditorReady = true;
        // 编辑器实例若已销毁后重建（如编辑模式下进入），回填待设置的富文本内容
        if (diaryEditorHtml) {
            editor.setHtml(diaryEditorHtml);
        }
    },

    /**
     * 编辑器内容变化：单向同步到模块级 diaryEditorHtml（非响应式，不回填编辑器，避免内容重置）
     */
    onDiaryEditorChanged(editor) {
        diaryEditorHtml = editor.getHtml();
    },

    /**
     * 供 Toolbar :editor 使用，返回非响应式的编辑器实例
     */
    getDiaryEditor() {
        return diaryEditorRaw;
    },

    /**
     * 去除 HTML 标签取纯文本（用于保存时校验内容是否为空、卡片摘要提取）
     */
    stripHtml(html) {
        const tmp = document.createElement('div');
        tmp.innerHTML = html || '';
        return (tmp.textContent || '').trim();
    },

    /**
     * 日记卡片摘要：去除富文本 HTML 标签取纯文本，截断后转义输出（防注入）
     */
    diaryExcerpt(html, maxLen = 60) {
        const text = this.stripHtml(html);
        const short = text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
        const tmp = document.createElement('div');
        tmp.textContent = short;
        return tmp.innerHTML;
    },

    /**
     * 表单提交入口：根据 editingDiaryId 分流到“新建”或“更新”
     */
    onDiarySubmit() {
        if (this.editingDiaryId) {
            this.updateDiary();
        } else {
            this.saveNewDiary();
        }
    },

    /**
     * 收集日记表单内容并做必填校验；返回 null 表示校验未通过。
     * existingFiles：编辑模式下原附件相对路径（未上传新附件时沿用）
     */
    collectDiaryForm(existingFiles) {
        const titleInput = this.$refs.diaryTitleInput;
        const categoryInput = this.$refs.diaryCategoryInput;
        const dateInput = this.$refs.diaryDateInput;
        const mediaInput = this.$refs.diaryMediaInput;
        const audioInput = this.$refs.diaryAudioInput;

        const title = titleInput ? titleInput.value.trim() : '';
        const typeid = categoryInput ? categoryInput.value : '';
        const noteDate = (dateInput && dateInput.value) ? dateInput.value : new Date().toISOString().split('T')[0];
        const content = (diaryEditorHtml || '').trim();
        const mediaUrl = mediaInput ? mediaInput.value.trim() : '';
        const audioTime = audioInput ? audioInput.value.trim() : '';
        // 附件：有新上传用新上传的相对路径，否则编辑模式沿用原 files
        const newFile = this.uploadedLocalFile ? this.uploadedLocalFile.url : null;
        const files = newFile || existingFiles || null;

        if (!title || !this.stripHtml(content)) {
            this.showToast(!title ? '请输入日记标题' : '请输入日记正文内容', 'error');
            return null;
        }

        const item = {
            'title': title, // 标题
            'typeid': typeid, // 分类标签
            'noteDate': noteDate, // 记录日期
            'content': content, // 富文本内容
            'mediaUrl': mediaUrl, // 网络媒体链接 (可选)
            'files': files, // 图片/短视频/录音附件 (可选)
            'audioTime': audioTime // 语音时长
        };
        return item;
    },

    saveNewDiary() {
        if (!this.requireLogin()) return;
        const item = this.collectDiaryForm(null);
        if (!item) return;

        indexjs.addNote(item).then(res => {
            if (res.code === 200) {
                this.showToast("添加成功！", 'success');
                // 仅保存成功后才关闭弹窗并重置表单；失败则保留弹窗与已填内容
                this.closeModal('quickAddModal');
                this.resetDiaryForm();
                this.renderGroupedDiaries();
            } else {
                this.showToast(res.msg || "添加失败！", 'error');
            }
        })
    },

    /**
     * 更新日记：调用 indexjs.updateNote（PUT，需带 id）
     */
    updateDiary() {
        if (!this.requireLogin()) return;
        const editingId = this.editingDiaryId;
        const original = this.diaries.find(d => d.id === editingId);
        // 编辑时未重新上传附件，则沿用原服务器相对路径
        const existingFiles = original ? (original.rawFiles || '') : '';
        const item = this.collectDiaryForm(existingFiles);
        if (!item) return;
        item.id = editingId;

        indexjs.updateNote(item).then(res => {
            if (res.code === 200) {
                this.showToast("更新成功！", 'success');
                // 更新成功后关闭弹窗并重置（退出编辑模式），失败则保留弹窗与已填内容
                this.closeModal('quickAddModal');
                this.resetDiaryForm();
                this.renderGroupedDiaries();
            } else {
                this.showToast(res.msg || "更新失败！", 'error');
            }
        })
    },

    filterDiary(cat) {
        this.currentDiaryCategory = cat;
        this.renderGroupedDiaries();
    },

    // 统计某个分类（typeid）下的日记数量
    diaryCountByType(typeid) {
        return this.diaries.filter(d => Number(d.typeid) === Number(typeid)).length;
    },

    // 查询日记列表，转换格式后渲染分组日记
    renderGroupedDiaries() {
        const container = this.$refs.groupedDiariesContainer;
        if (!container) return;

        indexjs.getNoteList({'pageNum': 1, 'pageSize': this.maxSize}).then(res => {
            if (res && res.code === 200) {
                // rows 每行转换为 diaries 展示格式
                this.diaries = (res.rows || []).map(r => {
                    // 根据 typeid 从 diaryList 字典中取分类名称
                    const dict = this.diaryList.find(d => d.id === r.typeid);
                    const date = r.noteDate || '';
                    let yearMonth = '';
                    if (date) {
                        const [y, m] = date.split('-');
                        yearMonth = `${y}年${Number(m)}月`;
                    }
                    // 附件媒体：files 可含多个逗号分隔的图片/视频/音频，逐个解析
                    const mediaList = String(r.files || r.mediaUrl || '')
                        .split(',').map(s => s.trim()).filter(Boolean)
                        .map(u => ({
                            url: resolveMediaUrl(u),
                            type: (() => {
                                const p = u.split('?')[0].split('#')[0].toLowerCase();
                                if (/^voice_/.test(p.split('/').pop() || '') || /\.(mp3|wav|m4a|aac|ogg|flac|amr)$/.test(p)) return 'audio';
                                if (/\.(mp4|mov|webm|avi|mkv|m4v)$/.test(p)) return 'video';
                                return 'image';
                            })()
                        }));
                    const firstMedia = mediaList[0] || null;
                    const hasAudio = mediaList.some(m => m.type === 'audio') || !!r.audioTime;
                    return {
                        id: r.id,
                        title: r.title,
                        category: dict ? dict.dictName : '',
                        typeid: r.typeid,
                        date: date,
                        yearMonth: yearMonth,
                        content: r.content,
                        // 原始服务器字段（编辑回填用）
                        rawFiles: r.files || '',
                        rawMediaUrl: r.mediaUrl || '',
                        rawAudioTime: r.audioTime || '',
                        mediaList: mediaList,                       // 全部附件媒体
                        media: firstMedia ? firstMedia.url : '',     // 兼容旧字段
                        mediaType: firstMedia ? firstMedia.type : (hasAudio ? 'audio' : null),
                        audioDuration: r.audioTime
                    };
                });
            }
            this.drawGroupedDiaries();
        }).catch(err => {
            console.error('获取日记列表失败:', err);
            this.drawGroupedDiaries();
        });
    },

    // 纯渲染：按分类过滤并分组绘制日记卡片
    drawGroupedDiaries() {
        const container = this.$refs.groupedDiariesContainer;
        if (!container) return;

        let filtered = this.diaries;
        if (this.currentDiaryCategory !== 'ALL') {
            filtered = this.diaries.filter(d => Number(d.typeid) === Number(this.currentDiaryCategory));
        }

        const countEl = this.$refs.diaryCountAll;
        if (countEl) countEl.innerText = this.diaries.length;

        if (filtered.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-10">暂无日记记录。</div>`;
            return;
        }

        const groups = {};
        filtered.forEach(item => {
            if (!groups[item.yearMonth]) groups[item.yearMonth] = [];
            groups[item.yearMonth].push(item);
        });

        container.innerHTML = Object.keys(groups).map(ym => `
            <div class="space-y-3">
                <div class="flex items-center space-x-2 border-b border-slate-200 pb-1.5">
                    <i class="fa-solid fa-calendar-days text-indigo-500 text-xs"></i>
                    <h4 class="font-extrabold text-xs text-slate-800 font-mono">${ym}</h4>
                    <span class="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.2 rounded-full font-bold">${groups[ym].length} 篇</span>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    ${groups[ym].map(d => `
                        <div data-action="openDiaryDetail" data-id="${d.id}" class="bg-white border border-slate-200 hover:border-indigo-400 rounded-xl p-5 shadow-2xs cursor-pointer hover:shadow-md transition flex flex-col justify-between space-y-3 min-h-[15rem] group">
                            <div class="space-y-2">
                                <div class="flex justify-between items-center text-xs">
                                    <span class="bg-sky-100 text-sky-800 px-2 py-0.5 rounded font-bold">${d.category}</span>
                                    <span class="text-slate-400 font-mono">${d.date}</span>
                                </div>
                                <h5 class="font-bold text-base text-slate-800 group-hover:text-indigo-600 transition line-clamp-2 leading-snug">${d.title}</h5>
                                <!-- 卡片摘要：剥离 HTML 标签，仅展示纯文本片段；查看全文渲染富文本 -->
                                <p class="text-[13px] text-slate-500 line-clamp-6 leading-relaxed overflow-hidden">${this.diaryExcerpt(d.content, 160)}</p>
                            </div>

                            <div class="flex items-center justify-between text-xs text-slate-400 border-t border-slate-100 pt-2.5">
                                <span class="flex items-center gap-1.5">
                                    ${d.media ? '<i class="fa-solid fa-paperclip text-indigo-500"></i>' : ''}
                                    ${d.audioDuration ? '<i class="fa-solid fa-microphone text-purple-500"></i>' : ''}
                                    <span>查看全文</span>
                                </span>
                                <i class="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition"></i>
                            </div>
                        </div>
                    `).join('')}
                </div>
            </div>
        `).join('');
    },

    openDiaryDetail(id) {
        const item = this.diaries.find(d => d.id === id);
        if (!item) return;
        detailDiaryId = id; // 记录当前详情 id，供“编辑”按钮使用

        this.$refs.detailTitle.innerText = item.title;
        this.$refs.detailCategoryTag.innerText = item.category;
        this.$refs.detailDate.innerText = item.date;
        // 富文本(HTML)用 innerHTML 渲染（含图片/视频）；历史纯文本仍走 innerText 保留换行
        const contentEl = this.$refs.detailContent;
        // 收集日记详情里所有可放大查看的图片（富文本正文图片 + 附件图片）
        const detailImages = [];
        const openDetailViewer = (idx) => {
            if (detailImages.length) this.openMediaViewer(detailImages.slice(), idx, null);
        };
        if (contentEl) {
            if (/<[a-z][\s\S]*>/i.test(item.content || '')) {
                contentEl.classList.remove('whitespace-pre-line');
                contentEl.innerHTML = item.content;
                // 约束富文本内媒体尺寸，视频补充 controls 使其可播放；图片可点击放大查看
                contentEl.querySelectorAll('img').forEach(img => {
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                    img.style.borderRadius = '8px';
                    img.style.display = 'block';
                    img.style.margin = '8px 0';
                    const url = img.getAttribute('src') || '';
                    if (url) {
                        img.style.cursor = 'zoom-in';
                        const idx = detailImages.length;
                        detailImages.push({ url, alt: img.getAttribute('alt') || '' });
                        img.addEventListener('click', () => openDetailViewer(idx));
                    }
                });
                contentEl.querySelectorAll('video').forEach(v => {
                    v.style.maxWidth = '100%';
                    v.style.height = 'auto';
                    v.style.borderRadius = '8px';
                    v.style.display = 'block';
                    v.style.margin = '8px 0';
                    v.setAttribute('controls', 'controls');
                });
            } else {
                contentEl.classList.add('whitespace-pre-line');
                contentEl.innerText = item.content;
            }
        }

        const mediaBox = this.$refs.detailMediaBox;
        const mediaGrid = this.$refs.detailMediaGrid;
        const audioBox = this.$refs.detailAudioBox;

        // 附件媒体：多图缩略图（可点击进入全屏多图查看）+ 视频 + 音频
        if (mediaGrid) {
            mediaGrid.innerHTML = '';
            const attachImages = [];
            (item.mediaList || []).forEach(m => {
                const cell = document.createElement('div');
                if (m.type === 'image') {
                    const idx = attachImages.length;
                    attachImages.push({ url: m.url, alt: item.title || '' });
                    cell.className = 'relative aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200 cursor-zoom-in group';
                    cell.innerHTML = `<img src="${m.url}" alt="${item.title || ''}" class="w-full h-full object-cover">
                        <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition flex items-center justify-center">
                            <i class="fa-solid fa-magnifying-glass-plus text-white opacity-0 group-hover:opacity-100 transition text-lg drop-shadow"></i>
                        </div>`;
                    cell.addEventListener('click', () => {
                        // 合并：正文富文本图片在前，附件图片在后，定位到当前附件图
                        const all = detailImages.concat(attachImages);
                        this.openMediaViewer(all, detailImages.length + idx, null);
                    });
                } else if (m.type === 'video') {
                    cell.className = 'col-span-3 rounded-lg overflow-hidden bg-slate-900 border border-slate-200';
                    cell.innerHTML = `<video src="${m.url}" controls class="w-full max-h-72 object-contain bg-black block"></video>`;
                } else {
                    // 音频：音频播放器卡片
                    cell.className = 'col-span-3 bg-purple-50 border border-purple-200 rounded-lg p-2.5 flex items-center gap-2';
                    cell.innerHTML = `<i class="fa-solid fa-microphone text-purple-600 shrink-0"></i>
                        <audio src="${m.url}" controls class="w-full min-w-0"></audio>`;
                }
                mediaGrid.appendChild(cell);
            });

            if ((item.mediaList || []).length > 0) {
                mediaBox.classList.remove('hidden');
            } else {
                mediaBox.classList.add('hidden');
            }
        }

        // 语音时长留声机提示（无音频文件、仅有录音时长时展示）
        const hasAudioFile = (item.mediaList || []).some(m => m.type === 'audio');
        if (!hasAudioFile && item.audioDuration) {
            audioBox.classList.remove('hidden');
            this.$refs.detailAudioDuration.innerText = item.audioDuration;
        } else {
            audioBox.classList.add('hidden');
        }

        if (this.$refs.deleteDetailBtn) {
            this.$refs.deleteDetailBtn.onclick = () => {
                this.deleteDiary(id);
                this.closeModal('diaryDetailModal');
            };
        }

        this.openModal('diaryDetailModal');
    },

    /**
     * 详情弹窗点“编辑”：把当前日记内容复制到新增日记表单，切换为“更新”模式。
     * 复用同一弹窗（quickAddModal）与编辑器，保存时调用 updateNote。
     */
    editDiaryFromDetail() {
        if (!this.requireLogin()) return;
        const id = detailDiaryId;
        const item = this.diaries.find(d => d.id === id);
        if (!item) {
            this.showToast('未找到该日记数据', 'error');
            return;
        }

        // 关闭详情弹窗
        this.closeModal('diaryDetailModal');

        // 打开新增弹窗（openModal 会把 editingDiaryId 重置为 null 并默认日期，故之后再设置编辑态）
        this.openModal('quickAddModal');
        this.editingDiaryId = item.id;

        // 回填表单各字段
        const titleInput = this.$refs.diaryTitleInput;
        const categoryInput = this.$refs.diaryCategoryInput;
        const dateInput = this.$refs.diaryDateInput;
        const mediaInput = this.$refs.diaryMediaInput;
        const audioInput = this.$refs.diaryAudioInput;
        const badge = this.$refs.localFilePreviewBadge;

        if (titleInput) titleInput.value = item.title || '';
        if (categoryInput) categoryInput.value = item.typeid;
        if (dateInput) dateInput.value = item.date || '';
        if (mediaInput) mediaInput.value = item.rawMediaUrl || '';
        if (audioInput) audioInput.value = item.rawAudioTime || '';
        if (badge) badge.classList.add('hidden'); // 原附件沿用 rawFiles，不在本地徽标展示
        this.uploadedLocalFile = null; // 标记未上传新附件；提交时沿用原 files

        // 回填富文本内容
        const fill = () => {
            const html = item.content || '<p><br></p>';
            diaryEditorHtml = html;
            if (diaryEditorRaw) {
                diaryEditorRaw.setHtml(html);
            }
        };
        if (diaryEditorRaw) {
            fill();
        } else {
            // 编辑器尚未创建完成（onCreated 会读取 diaryEditorHtml 自动回填）
            this.$nextTick(fill);
        }
    },

    deleteDiary(id) {
        if (!this.requireLogin()) return;

        indexjs.deleteNote(id).then(() => {
            this.showToast("删除成功！", 'success');
            this.diaries = this.diaries.filter(d => d.id !== id);
            this.renderGroupedDiaries();
        })
        
    },

    playDetailVoice() {
        this.showToast("正在播放爸爸的声音留声机音频...", 'info');
    },

    handleAlbumLocalFileUpload(e) {
        if (!this.requireLogin()) return;
        const picked = Array.from(e.target.files || []);
        // 清空 input 的值，便于再次选择同名文件时仍能触发 change
        if (e.target) e.target.value = '';
        if (picked.length === 0) return;

        // 图片、视频数量均不限，可多次选择追加；逐个加入缩略图列表
        picked.forEach(file => {
            const type = (file.type && file.type.startsWith('video/')) ? 'video' : 'image';
            const key = ++this.albumFileSeq;
            const item = {
                key,
                type,
                name: file.name,
                preview: URL.createObjectURL(file),
                status: 'uploading',
                url: ''
            };
            this.albumFiles.push(item);

            upload_image(file).then(data => {
                const raw = data.url || data.fileName || data.filePath || '';
                const url = String(raw).split(',').map(u => u.replace(/^https?:\/\/[^/]+/i, '')).join(',').trim();
                if (!url) throw new Error('服务器未返回文件地址');
                const target = this.albumFiles.find(f => f.key === key);
                if (target) {
                    target.url = url;
                    target.status = 'done';
                }
            }).catch(err => {
                // 单个失败：从列表移除并提示
                const idx = this.albumFiles.findIndex(f => f.key === key);
                if (idx > -1) {
                    URL.revokeObjectURL(this.albumFiles[idx].preview);
                    this.albumFiles.splice(idx, 1);
                }
                this.showToast(`「${file.name}」上传失败：${(err && err.message) || '请重试'}`, 'error');
            });
        });
    },

    // 移除某个已选/已上传素材（缩略图右上角删除）
    removeAlbumFile(idx) {
        const item = this.albumFiles[idx];
        if (item && item.preview && item.preview.startsWith('blob:')) URL.revokeObjectURL(item.preview);
        this.albumFiles.splice(idx, 1);
    },

    // 已选素材中的图片数量
    albumImageCount() {
        return this.albumFiles.filter(f => f.type === 'image').length;
    },

    // 清空相册弹窗已选素材（关闭/保存成功后调用）
    resetAlbumFiles() {
        this.albumFiles.forEach(f => {
            if (f.preview && f.preview.startsWith('blob:')) URL.revokeObjectURL(f.preview);
        });
        this.albumFiles = [];
    },

    // 重置相册素材弹窗表单（标题/链接/缩略图/文件框），编辑态退出时也调用
    resetAlbumForm() {
        this.resetAlbumFiles();
        const titleInput = this.$refs.albumMediaTitleInput;
        const mediaUrlInput = this.$refs.albumMediaUrlInput;
        const fileInput = this.$refs.albumLocalFileInput;
        if (titleInput) titleInput.value = '';
        if (mediaUrlInput) mediaUrlInput.value = '';
        if (fileInput) fileInput.value = '';
    },

    saveNewAlbumMedia() {
        if (!this.requireLogin()) return;
        const titleInput = this.$refs.albumMediaTitleInput;
        const tagInput = this.$refs.albumMediaTagInput;
        const dateInput = this.$refs.albumMediaDateInput;
        const mediaUrlInput = this.$refs.albumMediaUrlInput;

        const title = titleInput ? titleInput.value.trim() : ''; // 素材标题/简介
        const typeid = tagInput ? tagInput.value : ''; // 主题分类字典id（option 的 value 为 albumList 的 id）
        const photoDate = (dateInput && dateInput.value) ? dateInput.value : new Date().toISOString().split('T')[0]; // 拍摄日期
        const mediaUrl = mediaUrlInput ? mediaUrlInput.value.trim() : ''; // 网络链接(可选)
        // 本地上传的图片/视频：仅统计上传完成的素材，地址以英文逗号拼接
        const doneFiles = this.albumFiles.filter(f => f.status === 'done' && f.url);
        const files = doneFiles.length > 0 ? doneFiles.map(f => f.url).join(',') : null;

        if (!title) {
            // 兜底：正常情况下表单 required 已由浏览器在输入框下方弹出提示
            const form = this.$refs.albumMediaForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }
        if (this.albumFiles.some(f => f.status === 'uploading')) {
            this.showToast('文件还在上传中，请稍候再保存', 'info');
            return;
        }
        if (!files && !mediaUrl) {
            this.showToast('请先上传图片/视频或填写网络链接', 'info');
            return;
        }

        const item = {
            'title': title,
            'typeid': typeid,
            'photoDate': photoDate,
            'mediaUrl': mediaUrl,
            'files': files
        };

        this.albumSaving = true;
        indexjs.addPhoto(item).then(res => {
            if (res.code === 200) {
                this.showToast("素材已存入成长相册！", 'success');
                // 保存成功后刷新相册数据（相册与日记是两份独立数据）
                this.loadAlbumMediaList();
                this.editingAlbumId = null;
                this.closeModal('addAlbumMediaModal');
                this.resetAlbumForm();
            } else {
                this.showToast(res.msg || "保存失败！", 'error');
            }
        }).catch(err => {
            console.error('添加相册素材失败:', err);
            this.showToast((err && err.message) || '保存失败，请稍后重试', 'error');
        }).finally(() => {
            this.albumSaving = false;
        });
    },

    // 从数据库查询相册素材（photo 表），刷新成长相册
    loadAlbumMediaList() {
        const container = this.$refs.albumGalleryContainer;
        if (!container) return;

        indexjs.getPhotoList({'pageNum': 1, 'pageSize': this.maxSize}).then(res => {
            if (res && res.code === 200) {
                // 只取有媒体链接的记录，转换为相册展示格式
                this.albumMediaList = (res.rows || []).filter(r => r.files || r.mediaUrl).map(r => {
                    const dict = this.albumList.find(d => Number(d.id) === Number(r.typeid));
                    // files 兼容多文件：图片/视频地址以英文逗号分隔存储
                    const rawFiles = String(r.files || r.mediaUrl || '').split(',')
                        .map(u => u.trim()).filter(Boolean);
                    const medias = rawFiles.map(u => {
                        const url = resolveMediaUrl(u);
                        const path = url.split(/[?#]/)[0];
                        const type = /\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(path) ? 'video' : 'image';
                        return { url, type };
                    });
                    const images = medias.filter(m => m.type === 'image');
                    const videos = medias.filter(m => m.type === 'video');
                    const date = r.photoDate || '';
                    let yearMonth = '';
                    if (date) {
                        const [y, m] = date.split('-');
                        yearMonth = `${y}年${Number(m)}月`;
                    }
                    return {
                        id: r.id,
                        title: r.title,
                        tag: dict ? dict.dictName : '日常萌照',
                        typeid: r.typeid,
                        mediaUrl: r.mediaUrl || '',
                        type: images.length ? 'image' : (videos.length ? 'video' : 'image'),
                        date: date,
                        yearMonth: yearMonth,
                        url: images[0] ? images[0].url : (medias[0] ? medias[0].url : ''),
                        images: images,        // 平铺展示用：仅图片
                        videos: videos,        // 平铺展示用：仅视频（图片下方另起一行）
                        rawFiles: rawFiles,    // 编辑回填用：服务器相对路径
                        urls: medias.map(m => m.url) // 兼容旧字段
                    };
                });
            }
            this.renderAlbumGallery();
        }).catch(err => {
            console.error('查询相册素材失败:', err);
            this.renderAlbumGallery();
        });
    },

    filterAlbumType(type) {
        this.currentAlbumType = type;
        this.renderAlbumGallery();
    },

    filterAlbumTag(tag) {
        this.currentAlbumTag = tag;
        this.renderAlbumGallery();
    },

    renderAlbumGallery() {
        const container = this.$refs.albumGalleryContainer;
        if (!container) return;

        let filtered = this.albumMediaList;
        if (this.currentAlbumTag !== 'ALL') {
            filtered = filtered.filter(item => Number(item.typeid) === Number(this.currentAlbumTag));
        }
        // 类型筛选：照片画廊只保留含图片的素材且仅显示图片；视频回忆只保留含视频的素材且仅显示视频
        const typeMode = this.currentAlbumType; // 'ALL' | 'image' | 'video'
        filtered = filtered
            .map(item => {
                const showImages = typeMode === 'ALL' || typeMode === 'image' ? item.images : [];
                const showVideos = typeMode === 'ALL' || typeMode === 'video' ? item.videos : [];
                return { ...item, showImages, showVideos };
            })
            .filter(item => item.showImages.length || item.showVideos.length);

        const totalEl = this.$refs.albumTotalCount;
        const photoEl = this.$refs.albumPhotoCount;
        const videoEl = this.$refs.albumVideoCount;
        // 统计所有相册下的实际文件数量（每套素材可含多图多视频）
        const totalPhotos = this.albumMediaList.reduce((n, i) => n + (i.images ? i.images.length : 0), 0);
        const totalVideos = this.albumMediaList.reduce((n, i) => n + (i.videos ? i.videos.length : 0), 0);
        if (totalEl) totalEl.innerText = totalPhotos + totalVideos;
        if (photoEl) photoEl.innerText = totalPhotos;
        if (videoEl) videoEl.innerText = totalVideos;

        if (filtered.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-10">相册中暂无此类素材。</div>`;
            return;
        }

        const groups = {};
        filtered.forEach(item => {
            if (!groups[item.yearMonth]) groups[item.yearMonth] = [];
            groups[item.yearMonth].push(item);
        });

        container.innerHTML = Object.keys(groups).map(ym => `
            <div class="space-y-3">
                <div class="flex items-center space-x-2 border-b border-slate-200 pb-1.5">
                    <i class="fa-solid fa-camera text-teal-600 text-xs"></i>
                    <h4 class="font-extrabold text-xs text-slate-800 font-mono">${ym}</h4>
                    <span class="text-[10px] bg-teal-100 text-teal-800 px-2 py-0.2 rounded-full font-bold">${groups[ym].reduce((n, item) => n + item.showImages.length + item.showVideos.length, 0)} 个素材</span>
                </div>

                <div class="space-y-3">
                    ${groups[ym].map(m => `
                        <div class="bg-white border border-slate-200 rounded-2xl p-4 shadow-2xs hover:border-teal-300 transition space-y-3">
                            <div class="flex items-start justify-between gap-2">
                                <div class="min-w-0">
                                    <div class="flex items-center space-x-2 flex-wrap gap-y-1">
                                        <span class="text-[10px] bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full font-bold">${m.tag}</span>
                                        <p class="text-xs sm:text-sm font-bold text-slate-800 truncate">${m.title}</p>
                                    </div>
                                    <p class="text-[10px] text-slate-400 font-mono mt-0.5"><i class="fa-regular fa-calendar mr-1"></i>${m.date}</p>
                                </div>
                                ${this.isLoggedIn ? `
                                <div class="flex items-center space-x-2 shrink-0">
                                    <button data-action="importAlbumMedia" data-id="${m.id}" class="flex items-center space-x-1 text-sky-600 hover:text-white hover:bg-sky-600 border border-sky-300 px-2.5 py-1.5 rounded-lg text-xs font-medium transition" title="向该相册导入新的图片/视频素材">
                                        <i class="fa-solid fa-file-import"></i>
                                        <span>导入素材</span>
                                    </button>
                                    <button data-action="editAlbumMedia" data-id="${m.id}" class="flex items-center space-x-1 text-teal-600 hover:text-white hover:bg-teal-600 border border-teal-300 px-2.5 py-1.5 rounded-lg text-xs font-medium transition" title="编辑该相册">
                                        <i class="fa-solid fa-pen-to-square"></i>
                                        <span>编辑相册</span>
                                    </button>
                                    <button data-action="deleteAlbumMedia" data-id="${m.id}" class="flex items-center space-x-1 text-rose-500 hover:text-white hover:bg-rose-500 border border-rose-300 px-2.5 py-1.5 rounded-lg text-xs font-medium transition" title="删除该相册">
                                        <i class="fa-solid fa-trash-can"></i>
                                        <span>删除相册</span>
                                    </button>
                                </div>
                                ` : ''}
                            </div>

                            ${m.showImages.length ? `
                                <div class="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                                    ${m.showImages.map((img, idx) => `
                                        <div data-action="openAlbumMedia" data-id="${m.id}" data-index="${idx}" class="cursor-zoom-in aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                                            <img src="${img.url}" alt="${m.title}" class="w-full h-full object-cover hover:scale-105 transition duration-300" loading="lazy">
                                        </div>
                                    `).join('')}
                                </div>
                            ` : ''}

                            ${m.showVideos.length ? `
                                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    ${m.showVideos.map(v => `
                                        <div class="rounded-xl overflow-hidden bg-slate-900 border border-slate-200">
                                            <video controls src="${v.url}" class="max-h-64 w-full object-contain"></video>
                                        </div>
                                    `).join('')}
                                </div>
                            ` : ''}
                        </div>
                    `).join('')}
                </div>
            </div>
        `).join('');
    },

    /**
     * 打开相册图片多图查看器（平铺缩略图点击）
     * @param {Number} id - 相册素材 id
     * @param {Number} index - 点击的图片在该素材图片列表中的下标
     */
    openAlbumMediaViewer(id, index = 0) {
        const m = this.albumMediaList.find(item => Number(item.id) === Number(id));
        if (!m) return;
        const images = (m.images || []).map(i => ({ url: i.url, type: 'image' }));
        if (images.length === 0) return;
        this.openMediaViewer(images, index, {
            tag: m.tag,
            title: m.title,
            date: `拍摄时间: ${m.date}`,
            deletable: true,
            source: 'album',
            id: m.id
        });
    },

    /**
     * 点击相册素材"编辑"：把该套素材回填到添加素材弹窗，进入更新模式
     */
    /**
     * 导入素材：打开独立的导入弹窗，标题只读、仅上传新素材（不读取已有素材），
     * 导入时把新文件追加到该相册已有素材后面。
     */
    importAlbumMedia(id) {
        if (!this.requireLogin()) return;
        const m = this.albumMediaList.find(item => Number(item.id) === Number(id));
        if (!m) return;
        this.importingAlbumId = Number(id);
        this.resetImportAlbumFiles();
        this.openModal('importAlbumMediaModal');
        this.$nextTick(() => {
            const titleInput = this.$refs.importAlbumTitleInput;
            if (titleInput) titleInput.value = m.title || '';
        });
    },

    // 导入弹窗：选择本地图片/视频，逐个上传并显示缩略图（数量不限、可多次追加）
    handleImportAlbumFileUpload(e) {
        if (!this.requireLogin()) return;
        const picked = Array.from(e.target.files || []);
        if (e.target) e.target.value = '';
        if (picked.length === 0) return;

        picked.forEach(file => {
            const type = (file.type && file.type.startsWith('video/')) ? 'video' : 'image';
            const key = ++this.importFileSeq;
            const item = {
                key,
                type,
                name: file.name,
                preview: URL.createObjectURL(file),
                status: 'uploading',
                url: ''
            };
            this.importAlbumFiles.push(item);

            upload_image(file).then(data => {
                const raw = data.url || data.fileName || data.filePath || '';
                const url = String(raw).split(',').map(u => u.replace(/^https?:\/\/[^/]+/i, '')).join(',').trim();
                if (!url) throw new Error('服务器未返回文件地址');
                const target = this.importAlbumFiles.find(f => f.key === key);
                if (target) {
                    target.url = url;
                    target.status = 'done';
                }
            }).catch(err => {
                const idx = this.importAlbumFiles.findIndex(f => f.key === key);
                if (idx > -1) {
                    URL.revokeObjectURL(this.importAlbumFiles[idx].preview);
                    this.importAlbumFiles.splice(idx, 1);
                }
                this.showToast(`「${file.name}」上传失败：${(err && err.message) || '请重试'}`, 'error');
            });
        });
    },

    // 移除导入弹窗中某个本次新增素材
    removeImportAlbumFile(idx) {
        const item = this.importAlbumFiles[idx];
        if (item && item.preview && item.preview.startsWith('blob:')) URL.revokeObjectURL(item.preview);
        this.importAlbumFiles.splice(idx, 1);
    },

    // 导入弹窗本次新增中的图片数量
    importImageCount() {
        return this.importAlbumFiles.filter(f => f.type === 'image').length;
    },

    // 清空导入弹窗本次新增素材
    resetImportAlbumFiles() {
        this.importAlbumFiles.forEach(f => {
            if (f.preview && f.preview.startsWith('blob:')) URL.revokeObjectURL(f.preview);
        });
        this.importAlbumFiles = [];
    },

    // 关闭/取消导入弹窗：退出导入态并清空本次新增
    cancelImportAlbum() {
        this.importingAlbumId = null;
        this.resetImportAlbumFiles();
        const titleInput = this.$refs.importAlbumTitleInput;
        const fileInput = this.$refs.importAlbumFileInput;
        if (titleInput) titleInput.value = '';
        if (fileInput) fileInput.value = '';
    },

    // 确认导入：把本次新增素材追加到该相册已有素材后面，调用 updatePhoto 更新
    confirmImportAlbumMedia() {
        if (!this.importingAlbumId) return;
        if (!this.requireLogin()) return;
        const m = this.albumMediaList.find(item => Number(item.id) === Number(this.importingAlbumId));
        if (!m) return;

        if (this.importAlbumFiles.some(f => f.status === 'uploading')) {
            this.showToast('文件还在上传中，请稍候再导入', 'info');
            return;
        }
        const newFiles = this.importAlbumFiles.filter(f => f.status === 'done' && f.url).map(f => f.url);
        if (newFiles.length === 0) {
            this.showToast('请先选择要导入的图片/视频', 'info');
            return;
        }

        // 已有素材（相对路径）+ 本次新增，追加到后面
        const existing = (m.rawFiles || []).filter(Boolean);
        const merged = existing.concat(newFiles);

        const item = {
            id: m.id,
            title: m.title,
            typeid: m.typeid,
            photoDate: m.date,
            mediaUrl: m.mediaUrl || '',
            files: merged.join(',')
        };

        this.albumSaving = true;
        indexjs.updatePhoto(item).then(res => {
            if (res && res.code === 200) {
                this.showToast('素材已导入相册！', 'success');
                this.loadAlbumMediaList();
                this.closeModal('importAlbumMediaModal');
            } else {
                this.showToast((res && res.msg) || '导入失败！', 'error');
            }
        }).catch(err => {
            console.error('导入相册素材失败:', err);
            this.showToast((err && err.message) || '导入失败，请稍后重试', 'error');
        }).finally(() => {
            this.albumSaving = false;
        });
    },

    editAlbumMedia(id) {
        if (!this.requireLogin()) return;
        const m = this.albumMediaList.find(item => Number(item.id) === Number(id));
        if (!m) return;
        this.editingAlbumId = Number(id);
        this.openModal('addAlbumMediaModal');
        this.$nextTick(() => {
            const titleInput = this.$refs.albumMediaTitleInput;
            const tagInput = this.$refs.albumMediaTagInput;
            const dateInput = this.$refs.albumMediaDateInput;
            const mediaUrlInput = this.$refs.albumMediaUrlInput;
            if (titleInput) titleInput.value = m.title || '';
            if (tagInput && m.typeid !== undefined && m.typeid !== null && m.typeid !== '') tagInput.value = m.typeid;
            if (dateInput) dateInput.value = m.date || '';
            if (mediaUrlInput) mediaUrlInput.value = m.mediaUrl || '';
            // 回填已有图片/视频到缩略图区（服务器地址，可预览、可删除；删除后不参与更新）
            this.resetAlbumFiles();
            (m.rawFiles || []).forEach(u => {
                const full = resolveMediaUrl(u);
                const path = full.split(/[?#]/)[0];
                const base = path.substring(path.lastIndexOf('/') + 1);
                const type = /\.(mp4|mov|webm|avi|m4v|mkv)$/i.test(path) ? 'video' : 'image';
                this.albumFiles.push({
                    key: ++this.albumFileSeq,
                    type,
                    name: base,
                    preview: full,
                    status: 'done',
                    url: u  // 相对路径，提交用
                });
            });
        });
    },

    /**
     * 取消相册编辑：退出更新模式并重置表单
     */
    cancelEditAlbum() {
        this.editingAlbumId = null;
        this.closeModal('addAlbumMediaModal');
    },

    /**
     * 点击"更新成长相册"：调用 updatePhoto 更新该套素材
     */
    updateAlbumMedia() {
        if (!this.editingAlbumId) return;
        if (!this.requireLogin()) return;
        const titleInput = this.$refs.albumMediaTitleInput;
        const tagInput = this.$refs.albumMediaTagInput;
        const dateInput = this.$refs.albumMediaDateInput;
        const mediaUrlInput = this.$refs.albumMediaUrlInput;

        const title = titleInput ? titleInput.value.trim() : '';
        const typeid = tagInput ? tagInput.value : '';
        const photoDate = (dateInput && dateInput.value) ? dateInput.value : new Date().toISOString().split('T')[0];
        const mediaUrl = mediaUrlInput ? mediaUrlInput.value.trim() : '';
        const doneFiles = this.albumFiles.filter(f => f.status === 'done' && f.url);
        const files = doneFiles.length > 0 ? doneFiles.map(f => f.url).join(',') : null;

        if (!title) {
            const form = this.$refs.albumMediaForm;
            if (form && form.reportValidity) form.reportValidity();
            return;
        }
        if (this.albumFiles.some(f => f.status === 'uploading')) {
            this.showToast('文件还在上传中，请稍候再更新', 'info');
            return;
        }
        if (!files && !mediaUrl) {
            this.showToast('请先上传图片/视频或填写网络链接', 'info');
            return;
        }

        const item = {
            id: this.editingAlbumId,
            title: title,
            typeid: typeid,
            photoDate: photoDate,
            mediaUrl: mediaUrl,
            files: files
        };

        this.albumSaving = true;
        indexjs.updatePhoto(item).then(res => {
            if (res && res.code === 200) {
                this.showToast('成长相册已更新！', 'success');
                this.loadAlbumMediaList();
                this.closeModal('addAlbumMediaModal');
            } else {
                this.showToast((res && res.msg) || '更新失败！', 'error');
            }
        }).catch(err => {
            console.error('更新相册素材失败:', err);
            this.showToast((err && err.message) || '更新失败，请稍后重试', 'error');
        }).finally(() => {
            this.albumSaving = false;
        });
    },

    // 点击“删除相册”：弹出确认对话框，提示所有素材会被删除，用户确认后才真正删除
    deleteAlbumMedia(id) {
        if (!this.requireLogin()) return;
        if (!id) return;
        const m = this.albumMediaList.find(item => Number(item.id) === Number(id));
        if (!m) return;
        this.pendingDeleteAlbumId = Number(id);
        this.pendingDeleteAlbumTitle = m.title || '';
        this.openModal('confirmDeleteAlbumModal');
    },

    // 确认删除相册：调用 deletePhoto 删除整条相册记录
    confirmDeleteAlbum() {
        const id = this.pendingDeleteAlbumId;
        if (!id) return;
        indexjs.deletePhoto(id).then(res => {
            if (res && res.code === 200) {
                this.showToast('相册已删除', 'success');
                this.albumMediaList = this.albumMediaList.filter(item => Number(item.id) !== Number(id));
                this.renderAlbumGallery();
            } else {
                this.showToast((res && res.msg) || '删除失败', 'error');
            }
        }).catch(err => {
            console.error('删除相册失败:', err);
            this.showToast((err && err.message) || '删除失败，请稍后重试', 'error');
        });
        this.pendingDeleteAlbumId = null;
        this.pendingDeleteAlbumTitle = '';
        this.closeModal('confirmDeleteAlbumModal');
    },

    // Food Plan
    switchFoodMonth(m) {
        this.currentFoodMonth = m;
        document.querySelectorAll('.food-month-btn').forEach(btn => {
            // 移除选中态样式
            btn.classList.remove('bg-amber-600', 'text-white', 'font-bold');
            // 还原未选中态样式
            btn.classList.add('bg-white', 'text-amber-900', 'border', 'border-amber-200', 'hover:bg-amber-100', 'font-medium');
        });
        const activeTab = this.$refs[`food-tab-${m}`];
        if (activeTab) {
            // 移除未选中态样式
            activeTab.classList.remove('bg-white', 'text-amber-900', 'border', 'border-amber-200', 'hover:bg-amber-100', 'font-medium');
            // 应用选中态样式
            activeTab.classList.add('bg-amber-600', 'text-white', 'font-bold');
        }
        this.renderFoodPlan();
    },

    toggleFoodCheck(id) {
        if (!this.requireLogin()) return;
        // 在所有月份中查找对应 id 的食谱项
        const months = this.foodPlanMonths;
        for (const m in months) {
            const list = months[m];
            if (!Array.isArray(list)) continue;
            const item = list.find(r => r.id == id);
            if (!item) continue;
            // 新值：选中为 1，未选中为 0
            const newVal = item.val == 1 ? 0 : 1;
            if (id > 0) {
                indexjs.updateFood({id: Number(id), val: newVal}).then(res => {
                    if (res && res.code == 200) {
                        item.val = newVal;
                        this.updateFoodCard(item);
                    }
                }).catch(() => {
                    this.showToast('更新辅食状态失败', 'error');
                });
            } else {
                // 数据库中尚无记录，仅本地切换
                item.val = newVal;
                this.updateFoodCard(item);
            }
            return;
        }
    },

    // 局部更新单个食谱卡片的选中展示状态
    updateFoodCard(item) {
        const card = this.$refs.foodMonthContent && this.$refs.foodMonthContent.querySelector(`[data-id="${item.id}"]`);
        if (!card) return;
        const checked = item.val == 1;
        card.classList.toggle('bg-amber-50', checked);
        card.classList.toggle('border-amber-400', checked);
        card.classList.toggle('bg-white', !checked);
        card.classList.toggle('border-amber-200', !checked);
        card.classList.toggle('opacity-90', checked);
        const checkbox = card.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = checked;
        card.querySelectorAll('.line-through').forEach(el => el.classList.remove('line-through'));
        if (checked) {
            card.querySelectorAll('span.font-extrabold, h5').forEach(el => el.classList.add('line-through'));
        }
    },

    renderFoodPlan() {
        const container = this.$refs.foodMonthContent;
        if (!container) return;

        const doRender = () => {
            const recipes = this.foodPlanMonths[this.currentFoodMonth] || [];
            container.innerHTML = `
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    ${recipes.map(r => `
                        <div data-action="toggleFoodCheck" data-id="${r.id}" class="${r.val == 1 ? 'bg-amber-50 border-amber-400' : 'bg-white border-amber-200'} border rounded-xl p-3 shadow-2xs space-y-1.5 flex items-start space-x-3 transition ${r.val == 1 ? 'opacity-90' : ''} cursor-pointer hover:border-amber-300">
                            <input type="checkbox" id="foodCheck-${r.id}" ${r.val == 1 ? 'checked' : ''} class="mt-1 rounded text-amber-600 focus:ring-amber-500 cursor-pointer">
                            <div class="space-y-0.5 flex-1 min-w-0">
                                <div class="flex justify-between items-center">
                                    <span class="font-extrabold text-amber-900 text-xs ${r.val == 1 ? 'line-through' : ''}">${r.dayRange}</span>
                                    <span class="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.2 rounded font-mono">${r.texture}</span>
                                </div>
                                <h5 class="font-bold text-xs text-slate-800 truncate ${r.val == 1 ? 'line-through text-slate-500' : ''}">🥣 食谱：${r.food}</h5>
                                <p class="text-[11px] text-slate-600 leading-tight">${r.note}</p>
                            </div>
                        </div>
                    `).join('')}
                </div>
            `;
        };

        indexjs.getFoodList({pageNum: 1, pageSize: 100}).then(res => {
            if (res && res.code == 200) {
                this.foodStatus = res.rows || [];
                if (this.foodStatus.length > 0) {
                    // 按顺序把 foodStatus 中每条数据的 id、val 赋给 foodPlanMonths 四组共 40 条数据
                    let idx = 0;
                    for (const m of Object.keys(this.foodPlanMonths)) {
                        const list = this.foodPlanMonths[m];
                        if (!Array.isArray(list)) continue;
                        for (const item of list) {
                            if (idx >= this.foodStatus.length) break;
                            item.id = this.foodStatus[idx].id;
                            item.val = this.foodStatus[idx].val;
                            idx++;
                        }
                        if (idx >= this.foodStatus.length) break;
                    }
                }
            }
            doRender();
        }).catch(() => {
            doRender();
        });
    },

    // Growth Charts Logic 保存 WHO生长曲线记录
    saveGrowthMeasurement(e) {
        if (!this.requireLogin()) return;
        e.preventDefault();
        const m = parseInt(this.$refs.entryMonth.value);
        const w = parseFloat(this.$refs.entryWeight.value);
        const h = parseFloat(this.$refs.entryHeight.value);
        const hc = parseFloat(this.$refs.entryHead.value) || null;

        // this.growthHistory.push({ month: m, weight: w, height: h, head: hc });

        let item = {
            'head':hc, // 头围
            'month':m, // 月龄
            'weight':w, // 体重
            'height':h, // 身高
            'assessment':'', // 评估结果
        }
        // 添加WHO生长曲线记录
        indexjs.addWho(item).then(res => {
            if (res && res.code === 200) {
                this.$refs.growthEntryForm.reset();
                // 保存成功后重新查询，列表与图表在数据返回后统一刷新
                this.renderGrowthRecords();
            }
        }).catch(err => {
            console.error('添加WHO生长记录失败:', err);
        });
    },

    deleteGrowthRecord(id) {
        if (!this.requireLogin()) return;
        if(id >0) {
            // 删除WHO生长曲线记录，成功后统一刷新（renderGrowthRecords 内部会刷新列表、图表与摘要）
            indexjs.deleteWho(id).then(res => {
                if (res && res.code === 200) {
                    this.renderGrowthRecords();
                }
            }).catch(err => {
                console.error('删除WHO生长记录失败:', err);
            });
        }
    },

    renderGrowthRecords() {
        const container = this.$refs.growthHistoryContainer;
        if (!container) return;

        indexjs.getWhoList({'pageNum':1, 'pageSize':this.maxSize}).then(res => {
            if (res && res.code === 200) {
                // rows 为多行，只提取 month、weight、height、head 转换为展示格式（id 用于删除）
                this.growthHistory = (res.rows || []).map(g => ({
                    month: g.month,
                    'weight': g.weight,
                    'height': g.height,
                    'head': g.head,
                    'id': g.id,
                }));
            }
            this.drawGrowthRecords();
            this.initGrowthCharts();
            this.updateGrowthSummary();
        }).catch(err => {
            console.error('查询WHO生长记录失败:', err);
            this.drawGrowthRecords();
        });
    },

    // 纯渲染：把当前 growthHistory 绘制到生长档案容器
    drawGrowthRecords() {
        const container = this.$refs.growthHistoryContainer;
        if (!container) return;

        if (this.growthHistory.length === 0) {
            container.innerHTML = `<div class="text-center text-xs text-slate-400 py-6">暂无生长测量数据记录。</div>`;
            return;
        }

        const sorted = [...this.growthHistory].sort((a, b) => b.month - a.month);

        container.innerHTML = sorted.map(g => `
            <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs space-y-2">
                <div class="flex justify-between items-center border-b border-slate-100 pb-2">
                    <div class="flex items-center space-x-2">
                        <span class="font-extrabold text-xs sm:text-sm text-slate-800">${g.month} 月龄</span>
                        <span class="text-[10px] bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-mono font-bold">WHO测量档案</span>
                    </div>
                    <button data-action="deleteGrowthRecord" data-id="${g.id}" class="text-slate-400 hover:text-rose-600 text-xs transition" title="删除此条记录">
                        <i class="fa-solid fa-trash-can"></i>
                    </button>
                </div>

                <div class="grid grid-cols-3 gap-2 text-[11px] text-slate-600 font-mono bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    <div>⚖️ 体重: <strong class="text-slate-800">${g.weight} kg</strong></div>
                    <div>📏 身高: <strong class="text-slate-800">${g.height} cm</strong></div>
                    <div>🧠 头围: <strong class="text-slate-800">${g.head ? g.head + ' cm' : '未测量'}</strong></div>
                </div>

                <div class="text-xs text-slate-700 leading-relaxed bg-purple-50/40 p-2.5 rounded-lg border border-purple-100/60">
                    <span class="font-bold text-purple-900">📊 发育测评：</span>
                    <span class="text-slate-700">体重与身高处于 WHO P50 黄金中位数水平附近，发育曲线平稳，整体指标优秀。</span>
                </div>
            </div>
        `).join('');
    },

    updateGrowthSummary() {
        if (this.growthHistory.length === 0) return;
        // 按 month 升序排序，取最大月龄（最近一次测量）的记录
        const sorted = [...this.growthHistory].sort((a, b) => a.month - b.month);
        const latest = sorted[sorted.length - 1];
        const tagEl = this.$refs.latestMonthTag;
        if (tagEl) {
          tagEl.innerText = `最新月龄: ${latest.month}个月`;  
          this.latestMonth = latest.month;
        }

        const summaryEl = this.$refs.growthSummaryText;
        if (summaryEl) {
            summaryEl.innerHTML = `
                ${this.userInfo.chlidName || this.babyName}在 <strong>${latest.month} 月龄</strong> 测量：体重 <strong>${latest.weight} kg</strong>，身高 <strong>${latest.height} cm</strong>。<br>
                根据 WHO 0-3岁男孩百分位数标准：体重处于 <strong>P50 黄金中位数水平</strong>，生长发育曲线平稳上升，营养摄入充足，奶爸护理效果极佳！
            `;
        }
    },

    initGrowthCharts() {
        const wCanvas = this.$refs.weightChartCanvas;
        const hCanvas = this.$refs.heightChartCanvas;
        if (!wCanvas || !hCanvas) return;

        // 身高和体重数据
        const userWeightData = this.p50months.map(m => {
            const found = this.growthHistory.find(g => g.month === m);
            return found ? found.weight : null;
        });

        const userHeightData = this.p50months.map(m => {
            const found = this.growthHistory.find(g => g.month === m);
            return found ? found.height : null;
        });

        if (weightChart) weightChart.destroy();
        weightChart = new Chart(wCanvas, {
            type: 'line',
            data: {
                labels: this.p50months.map(m => `${m}月`),
                datasets: [
                    { label: '秉宣测量体重(kg)', data: userWeightData, borderColor: '#0284c7', backgroundColor: '#0284c7', borderWidth: 3, pointRadius: 5 },
                    { label: 'WHO P50 标准线', data: this.p50Weight, borderColor: '#cbd5e1', borderDash: [5, 5], fill: false }
                ]
            },
            options: { responsive: true, maintainAspectRatio: false }
        });

        if (heightChart) heightChart.destroy();
        heightChart = new Chart(hCanvas, {
            type: 'line',
            data: {
                labels: this.p50months.map(m => `${m}月`),
                datasets: [
                    { label: '秉宣测量身高(cm)', data: userHeightData, borderColor: '#9333ea', backgroundColor: '#9333ea', borderWidth: 3, pointRadius: 5 },
                    { label: 'WHO P50 标准线', data: this.p50Height, borderColor: '#cbd5e1', borderDash: [5, 5], fill: false }
                ]
            },
            options: { responsive: true, maintainAspectRatio: false }
        });
    },

    // 事件委托：统一处理动态 innerHTML 中带 data-action 的点击
    delegateClick(event) {
        const trigger = event.target.closest('[data-action]');
        if (!trigger) return;
        const action = trigger.dataset.action;
        const rawId = trigger.dataset.id;
        const id = rawId === undefined ? undefined : (isNaN(Number(rawId)) ? rawId : Number(rawId));
        switch (action) {
            case 'deleteMoodPost': this.deleteMoodPost(id); break;
            case 'editMoodPost': this.editMoodPost(id); break;
            case 'toggleLikeMood': this.toggleLikeMood(id); break;
            case 'openMoodMedia': this.openMoodMediaViewer(id, Number(trigger.dataset.index) || 0); break;
            case 'deleteFetalRecord': this.deleteFetalRecord(id); break;
            case 'deleteTrackerLog': this.deleteTrackerLog(id); break;
            case 'openDiaryDetail': this.openDiaryDetail(id); break;
            case 'openAlbumMedia': this.openAlbumMediaViewer(id, Number(trigger.dataset.index) || 0); break;
            case 'editAlbumMedia': this.editAlbumMedia(id); break;
            case 'importAlbumMedia': this.importAlbumMedia(id); break;
            case 'deleteAlbumMedia': this.deleteAlbumMedia(id); break;
            case 'toggleFoodCheck': this.toggleFoodCheck(id); break;
            case 'deleteGrowthRecord': this.deleteGrowthRecord(id); break;
            default: break;
        }
    },

    getUserInfo() {
        indexjs.getUserInfo().then(res => {
            if(res && res.code === 200) {
                this.userInfo = res.data || {};
                this.applyUserInfo();
            }
        })
    },

    // 将 userInfo 中的配置应用到页面展示
    applyUserInfo() {
        const u = this.userInfo || {};
        if (u.webTitle) this.siteTitle = u.webTitle;           // 网站标题
        if (u.fatherName) this.dadName = u.fatherName;         // 奶爸昵称
        if (u.chlidName) this.babyName = u.chlidName;          // 宝宝姓名
        if (u.fatherTag) this.dadStatus = u.fatherTag;         // 奶爸状态标签
        if (u.fatherImage) {
            this.avatarPath = u.fatherImage;                   // 相对路径（存库用）
            this.avatarUrl = resolveMediaUrl(u.fatherImage);   // 奶爸头像（展示用）
        }

        // 选择移植 / 月经日期
        if (u.yizhidate) {
            this.transferDate = u.yizhidate;
            const dateInput = this.$refs.calcDateInput;
            if (dateInput) dateInput.value = u.yizhidate;
        }
        // 选择推算依据（存的是 pregnancyList 字典 id）
        if (u.yuchanqituisuan) {
            this.embryoType = u.yuchanqituisuan;
            const typeSelect = this.$refs.calcTypeSelect;
            if (typeSelect) typeSelect.value = String(u.yuchanqituisuan);
            // 字典已加载时按"+ N天"推算预产期
            const dict = this.pregnancyList.find(d => Number(d.id) === Number(u.yuchanqituisuan));
            const match = dict ? dict.dictName.match(/\+\s*(\d+)\s*天/) : null;
            if (match && u.yizhidate) {
                const baseDate = new Date(u.yizhidate);
                baseDate.setDate(baseDate.getDate() + Number(match[1]));
                const yyyy = baseDate.getFullYear();
                const mm = String(baseDate.getMonth() + 1).padStart(2, '0');
                const dd = String(baseDate.getDate()).padStart(2, '0');
                this.dueDate = `${yyyy}-${mm}-${dd}`;
                this.updateCountdown();
            }
        }

        this.updateProfileUI();
    },

    initApp() {
        try {
            // 刷新后根据 localStorage 中的 token 恢复登录态（控制需登录的导航项显隐）
            this.isLoggedIn = this.isLogin();
            // 加载数据字典
            indexjs.getOrgtreeList({"dicNames":"日常护理,时光成长日记,时光成长相册,心情微语,预产期推算算法"}).then(res => {
            
                // 设置数据字典
                this.orgtreeList = res || [];
                if(this.orgtreeList.length > 0) {
                    this.dailyList = this.orgtreeList[0] || [];
                }
                if(this.orgtreeList.length > 1) {
                    this.diaryList = this.orgtreeList[1] || [];
                }
                if(this.orgtreeList.length > 2) {
                    this.albumList = this.orgtreeList[2] || [];
                }
                if(this.orgtreeList.length > 3) {
                    this.moodList = this.orgtreeList[3] || [];
                }
                if(this.orgtreeList.length > 4) {
                    this.pregnancyList = this.orgtreeList[4] || [];
                }

                // 字典可能晚于用户信息加载完成，重新应用一次以回填推算依据下拉框
                if (this.userInfo && Object.keys(this.userInfo).length > 0) {
                    this.applyUserInfo();
                }
            })

            // 拉取用户信息
            this.getUserInfo();

            // 拉取全部日常护理打卡记录并渲染 （默认首页）
            this.refreshTrackerLogs();

            // 刷新 秉宣诞生倒计时等信息
            this.updateProfileUI();
        } catch (err) {
            console.error("Initialization error:", err);
        }
    }
    },
    beforeUnmount() {
        // 销毁 wangEditor 实例，防止内存泄漏（editor-for-vue 不自动销毁）
        this.destroyDiaryEditor();
    },
    mounted() {
        // 页面初始化即同步一次浏览器标题（watch 只在值变化时触发）
        document.title = this.siteTitle;
        // 等 Vue 把模板渲染到 DOM 后再初始化
        this.$nextTick(() => {
            this.initApp();
        });
    }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
html, body 
{
    height: 100%;
    font-family: 'Noto Sans SC', sans-serif;
    background-color: #f8fafc;
    color: #1e293b;
    overflow-x: hidden;
}
.nav-item.active {
    background-color: #e0f2fe;
    color: #0369a1;
    font-weight: 700;
    border-right: 4px solid #0284c7;
}
.nav-item.active i {
    color: #0284c7 !important;
}
.custom-scrollbar::-webkit-scrollbar {
    width: 5px;
    height: 5px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 9999px;
}

.bkleft {
    float: left;
}
.txtleft, label,div,span,p {
    text-align: left;
}

/* 日记详情富文本内容：还原编辑器排版，媒体自适应宽度 */
.diary-rich-content p {
    margin: 0 0 0.5em;
}
.diary-rich-content img,
.diary-rich-content video {
    max-width: 100%;
    height: auto;
}

/* 富文本编辑器：文本区内容超出时上下滚动（外层已用 flex 限定高度） */
.w-e-text-container {
    overflow-y: auto !important;
}
</style>
